import { setServerOffset, tzFormat, appNow } from '../../utils/appClock'
// ============================================
// useChartData — WebSocket, trade ingestion, candle management
// Owns: connection lifecycle, trade batching, candle state,
//       historical fetch, sleep/wake buffering, rebucketing
// ============================================

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { getTokenName } from '../../utils/jupiter'
import { publishTankSample } from '../../utils/meteoraTank'

// ─── Wallet stats registry, keyed by MINT ───
// NOT per-hook-instance. Every chart slot runs its own useChartData, so a
// per-instance ref meant four separate Maps and a panel that could only read
// whichever one it happened to be handed — it showed an empty slot while a
// different slot held 110 wallets. Keyed by mint, any consumer asks for the
// token it cares about and two slots on the same token merge for free.
// Survives slot switches; capped so a long session can't grow forever.
// ── Wallet interning ──
// TradeBuffer is columnar Float64/Int8; a 44-char base58 address cannot live
// there. Intern to a Uint32 id instead: 4 bytes on the row, the string kept
// once here. Ids are global (not per-mint) so a wallet keeps its identity
// across token switches, and 0 is reserved for "unattributed" — which is what
// every backfill row is, since the server's backfill payload carries no trader.
const WALLET_ID = new Map()             // address -> id
const WALLET_ADDR = ['']                // id -> address (index 0 = unattributed)
export function walletIdFor(addr) {
  if (!addr) return 0
  let id = WALLET_ID.get(addr)
  if (id === undefined) { id = WALLET_ADDR.length; WALLET_ADDR.push(addr); WALLET_ID.set(addr, id) }
  return id
}
export function walletAddrFor(id) { return WALLET_ADDR[id] || '' }
// push-simple-20261021: synchronous live-trade hook. Subscribers are called
// right after a live trade lands in rawTradesRef, on the same call stack, so
// armed orders (Push) fire on the trade itself. fn(token, walletAddr, side,
// sol, priceSol, timeMs). Costs nothing while nobody subscribes.
const _rawTradeSubs = new Set()
export function onRawTrade(fn) { _rawTradeSubs.add(fn); return () => _rawTradeSubs.delete(fn) }

// ── Chart wallet selection ──
// Module state, not React state: the tracker panel and the chart canvas are
// siblings with no shared owner, and the canvas redraws from refs. A version
// counter is the redraw trigger — subscribers bump, the draw loop compares.
let _selWallet = null                   // address, or null
let _selVersion = 0
const _selSubs = new Set()
export function selectChartWallet(addr) {
  const next = addr || null
  if (next === _selWallet) return
  _selWallet = next
  _selVersion++
  for (const fn of _selSubs) { try { fn(next) } catch {} }
}
export function getChartWallet() { return _selWallet }
export function chartWalletVersion() { return _selVersion }
export function onChartWalletChange(fn) { _selSubs.add(fn); return () => _selSubs.delete(fn) }

// Build the per-cell overlay for one wallet. DERIVED, never stored: candle
// data is untouched, so none of the chart invariants (rebuilds never reduce
// volume, display bucket never mutates cells) can be violated by this. One
// pass over the trade buffer, only when the selection changes.
//
// The level key MUST be stepIdxOf(mcap, bucket) with the bucket THE DRAW IS
// USING — forEachLevel yields _display levels when a display bucket is
// active, so keying on _nativeBucket would put badges one row off the
// moment you zoom, and it would look plausible. The caller passes the
// display bucket; the draw looks up with stepIdxOf(levelMcap, sameBucket),
// so the two sides agree by construction rather than by coincidence.
// `ratio` (log scale): when set, the level key is the GLOBAL log step
// logStepIdx(mcap, ratio) instead of the linear stepIdxOf — the same
// anchor-free lattice buildDisplay and the grid memo use, so a display row
// and the raw trades inside it share one key. Anchor-free is what keeps this
// stable across a pan; keying on a row INDEX would churn the cache every
// frame. The draw looks up with the matching function on the display mcap.
export function buildWalletOverlay(buf, candles, walletId, bucket, ratio = null) {
  if (!buf || !walletId || !candles || !candles.length || !(bucket > 0)) return null
  const byStart = []
  for (const c of candles) {
    if (c && !c.isHistorical && c.startTime != null) byStart.push(c)
  }
  if (!byStart.length) return null
  byStart.sort((a, b) => a.startTime - b.startTime)
  const out = new Map()
  const n = buf.length
  for (let i = 0; i < n; i++) {
    if (buf.walletAt(i) !== walletId) continue
    const t = buf.timeAt(i)
    // Last candle whose startTime <= t. Linear scan would be O(n·m); the
    // candle list is sorted so a binary search keeps this one pass.
    let lo = 0, hi = byStart.length - 1, hit = -1
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      if (byStart[mid].startTime <= t) { hit = mid; lo = mid + 1 } else { hi = mid - 1 }
    }
    if (hit < 0) continue
    const c = byStart[hit]
    const _m = buf.mcapAt(i)
    const step = ratio ? logStepIdx(_m, ratio) : stepIdxOf(_m, bucket)
    let m = out.get(c.startTime)
    if (!m) { m = new Map(); out.set(c.startTime, m) }
    let cell = m.get(step)
    if (!cell) { cell = { buy: 0, sell: 0, nBuy: 0, nSell: 0 }; m.set(step, cell) }
    if (buf.sideSignAt(i) > 0) { cell.buy += buf.amountAt(i); cell.nBuy++ }
    else { cell.sell += buf.amountAt(i); cell.nSell++ }
  }
  return out.size ? out : null
}

const WALLET_STATS = new Map()          // mint -> Map<wallet, stats>
const WALLET_STATS_MAX_MINTS = 8

export function getWalletStats(mint) {
  return (mint && WALLET_STATS.get(mint)) || null
}
function resetWalletStats(mint) {
  // The registry has NO per-trade dedup — by design it is a running fold.
  // That is safe for live trades (each arrives once) and for gap_fill
  // (spans the fold never saw). It is NOT safe for buffer_dump, which
  // replays the most recent ~21k trades on every wake into a map that
  // deliberately outlives resetChart: every wake re-added the same trades
  // and every wallet's gross roughly doubled per wake. The fix is not
  // dedup machinery — the dump IS the session replay, so the dump is
  // AUTHORITATIVE: wipe the mint's map and let the replay rebuild it.
  // Costs nothing real: the registry's own docstring defines the window
  // as the replayed session, and that is exactly what remains.
  if (mint) WALLET_STATS.delete(mint)
}
// (chart2-bounce) ONE WRITER PER MINT. The registry is module-level and keyed
// on the mint, so with both chart slots on the same coin each slot's ingest
// folded every trade in again (every wallet's totals doubled), and either
// slot's buffer_dump wiped the map the other had built. The first slot to
// fold a mint owns it; the other reads the same map but never writes. Owner-
// ship moves only when the owner unmounts or charts a different mint.
const WALLET_STATS_OWNER = new Map()    // mint -> instance { tokRef, dead }
function ownsWalletStats(mint, inst) {
  if (!mint || !inst) return true
  const cur = WALLET_STATS_OWNER.get(mint)
  if (cur === inst) return true
  if (!cur || cur.dead || cur.tokRef.current !== mint) { WALLET_STATS_OWNER.set(mint, inst); return true }
  return false
}
function walletStatsFor(mint) {
  let M = WALLET_STATS.get(mint)
  if (!M) {
    M = new Map()
    WALLET_STATS.set(mint, M)
    while (WALLET_STATS.size > WALLET_STATS_MAX_MINTS) {
      WALLET_STATS.delete(WALLET_STATS.keys().next().value)   // oldest out
    }
  }
  return M
}
if (typeof window !== 'undefined') {
  window.__wstats = (mint) => {
    const M = mint ? WALLET_STATS.get(mint) : [...WALLET_STATS.values()][0]
    return {
      mints: [...WALLET_STATS.keys()].map(m => `${m.slice(0, 6)}:${WALLET_STATS.get(m).size}`),
      wallets: M ? M.size : 0,
    }
  }
}
import {
  WS_URL, WS_MAX_RECONNECT_ATTEMPTS, WS_HEARTBEAT_INTERVAL, HTTP_PROXY_BASE,
  MAX_CANDLES, MAX_TRADES_WINDOW_MS, DEFAULT_VIEW_WINDOW,
  MCAP_BUCKET_STEPS,
  deduplicateCandles as _dedupeRaw, fetchHyperliquidCandles,
  pickBucketForRange, pickStorageBucket, clearTextSpriteCache, snapBucket,
  logStepIdx, cmeInfo, CME_FEED_DELAY_MS,
} from '../../utils/chartUtils'
import { fetchFutProfiles, fetchSolProfiles, profilesToCellCandles, seedLiveFromTip, foldCellCandles } from '../../utils/hlFootprintHistory'
import { decodeBackfillFrame } from '../../utils/wireBackfill'
import {
  forEachLevel, candleMaxLevelVolume, candleLevelCount, candleTotalVolume,
  newLiveCandle, addTickToLive, stepIdxOf, forEachStorageLevel, rekeyStep,
} from '../../utils/candleHelpers'
import {
  resolveTokenKey,
  loadTrades, persistTrades, flushTradesNow, clearTrades,
} from '../../utils/candleStore'
import TradeBuffer from '../../utils/tradeBuffer'
import { isPhoneDevice } from './useIsPhone'
import { dataCommitsHeld } from '../../utils/dataCommitGate'
import { dbg } from '../../utils/debugFlag'
import { getVolBars, onVolBarsChange, buildVolCandles, autoVolSize, CME_VOL_DEFAULT } from '../../utils/volBars'   // vol-bars-20261101

// (perf5) Longest a chart's Y glide may hold live-data commits. A long wheel
// session still sees the live candle / stats / tape catch up this often.
const DATA_HOLD_MAX_MS = 1000

const FUTURES_TRADE_RETENTION_MS = 3 * 60 * 60 * 1000  // futures raw-buffer cap (see prune in startNewCandle)

// ── Futures footprint history (Seam B) — OWN CAPTURE ────────────────
// Stage 2 of the own-data plan: profiles now come from OUR server's
// /fut/profile (swaps.db aggregation of the 24/7 HL capture + whatever
// BX/CB recorded) instead of a vendor. Same shapes end to end, so the
// dormant OpenMarket machinery (profilesToCellCandles, kline map-merge,
// _omFootprint render gates) lit back up with a fetcher swap — exactly the
// drop-in the adapter game plan promised. Depth = capture retention (3d).
// Coins outside FUT_CAPTURE_COINS return empty → plain klines, no error.
const FUT_HISTORY_ENABLED = true
// Maps the chart timeInterval to the OM interval enum, mirroring the SAME
// slotting fetchHyperliquidCandles uses for klines (15/30/60s charts get
// 1-minute periods, etc.) so cell-candles land on the kline startTimes and
// the map-merge in loadFuturesHistory lines up 1:1.
//
// EVERY offered TF needs a row. A missing key falls back to 'MINUTE' — and
// because chartUtils' kline map has the same fallback, a hole here doesn't
// error, it silently rebuilds the whole history on the WRONG lattice. That
// was the 15m chart: 900 was missing from BOTH maps, so "15m history" was
// 500 one-minute klines + one-minute profiles — the stacked columns with
// per-minute x labels, none of them aligned to the 15m live candles.
// ─── Slot fold: profiles → chart-timeframe slots ───
// profilesToCellCandles stamps each cell candle with the PROFILE's own
// timestamp (`startTime: per.ts * 1000`), and the kline merge keys on
// startTime. That is fine whenever the profile period is >= the timeframe,
// because a coarse timestamp is always a valid slot boundary. It breaks when
// the period is FINER: 30m asks for FIFTEEN_MINUTES, so half the profiles
// land on :15/:45 where no 1800s kline exists. Those candles match nothing,
// never upgrade, and render as a SECOND set of footprints beside the real
// ones — the long-standing 30m duplication, and 1800 is the only row in the
// map whose period is finer than its key.
//
// Folding at the PROFILE level (not the candle level) is what makes this
// safe: profilesToCellCandles already aggregates its flat [price,buy,sell]
// triples into a Map keyed by step and SUMS collisions, so concatenating two
// profiles' triples is exactly equivalent to having fetched one profile for
// the whole slot. No arithmetic is duplicated here, and nothing needs to know
// about buckets or steps.
//
// Cheap no-op in the common case: when the period already divides the
// timeframe, every profile floors to its own ts and the map is 1:1.
function foldProfilesToSlot(profiles, slotSec) {
  if (!Array.isArray(profiles) || !(slotSec > 0) || profiles.length === 0) return profiles
  const bySlot = new Map()
  let folded = false
  for (const per of profiles) {
    const ts = Math.floor(per.ts / slotSec) * slotSec
    const cur = bySlot.get(ts)
    if (!cur) {
      // Copy on first touch — never mutate the caller's array, which may be
      // the cached store's own profile data.
      bySlot.set(ts, { ...per, ts, profile: Array.from(per.profile || []) })
    } else {
      const src = per.profile || []
      for (let i = 0; i < src.length; i++) cur.profile.push(src[i])
      folded = true
    }
  }
  if (!folded && bySlot.size === profiles.length) return profiles   // nothing changed
  return Array.from(bySlot.values()).sort((a, b) => a.ts - b.ts)
}

const OM_INTERVAL_MAP = {
  15: 'MINUTE', 30: 'MINUTE', 60: 'MINUTE', 300: 'FIVE_MINUTES',
  900: 'FIFTEEN_MINUTES',
  1800: 'FIFTEEN_MINUTES', 3600: 'HOUR', 14400: 'FOUR_HOURS', 86400: 'DAY',
}
const OM_PERIOD_SEC = {
  MINUTE: 60, FIVE_MINUTES: 300, FIFTEEN_MINUTES: 900,
  HOUR: 3600, FOUR_HOURS: 14400, DAY: 86400,
}
// Profile period for a futures chart TF. HL/CEX capture is minute-grained,
// so 15s/30s charts ride 1m profiles; CME is aggregated from raw ticks in
// 15s slots server-side, so its sub-minute TFs get their own true period.
const futProfilePeriodSec = (coin, tfSec) =>
  (cmeInfo(coin) && tfSec < 60) ? tfSec : OM_PERIOD_SEC[OM_INTERVAL_MAP[tfSec] || 'MINUTE']
// Commit-time profile-fetch depth cap (hours). The commit's Promise.all
// aborts any history fetch at 8s (the OM postmortem rule) — but the server
// aggregation scales with the window, so the old depth=MAX_CANDLES×period
// ask (72h on the 30m chart) routinely blew the 8s budget and silently
// downgraded every TF switch to plain klines ("changing timeframes loses
// the history"). Split: the commit fetches a SHALLOW fast window so cells
// land with the first paint, then _deepFutHistoryFetch (below) pulls the
// full depth in the background with a longer timeout and upgrades the
// remaining klines in place when it arrives.
const FUT_SHALLOW_HOURS = 12
// CME history depth. The server holds CME_HISTORY_HOURS (24h) of tick
// aggregate; the app-wide MAX_CANDLES (500) cut that to 8h on 1m and 2h on
// 15s. CME views get a deeper cap so the full window can show. It is bounded
// at 2880 (24h at 30s, 12h at 15s) to keep footprint rendering sane.
// Non-CME views are unchanged (MAX_CANDLES).
const CME_HISTORY_HOURS = 24
const CME_FETCH_MAX_HOURS = 170   // server holds 7 days (168h) once the staged change is live
const CME_MAX_CANDLES = 2880
const maxCandlesFor = (coin, tfSec) => (coin && cmeInfo(coin) && tfSec > 0)
  ? Math.max(MAX_CANDLES, Math.min(CME_MAX_CANDLES, Math.ceil(CME_HISTORY_HOURS * 3600 / tfSec)))
  : coin ? MAX_CANDLES
  : tfSec >= 60 ? Infinity : MAX_CANDLES  // Solana: 1m+ uncapped (full 72h server history); 15s/30s keep 500
const FUT_DEEP_TIMEOUT_MS = 30000
// Solana raw-buffer cap. Solana had NO cap: min() kept trades back to the
// oldest live candle, so holding ONE token (no rotation to reset it) grew the
// buffer for the whole session — every candle close re-filtered the entire
// array and the heap/GC load built into the lag a refresh cleared. This bounds
// it. It costs no reconstruction depth: Solana TF switches FOLD from the 26h
// fine 5s series (foldFineCandles), never from this buffer — the raw→display
// rebuild only runs on a sub-5s downshift, which isn't an offered interval.
// The cap engages only AFTER the backfill train completes (see backfillActiveRef)
// so the fine-series re-seed at finalChunk first captures the full server depth.
const SOLANA_TRADE_RETENTION_MS = 3 * 60 * 60 * 1000

// How many trailing candles the incremental aggregate pass re-folds each tick.
// Closed candles are immutable, so the y-range + max-volume aggregates only need
// a full rescan on a structural change; between those, a late/out-of-order trade
// can only land in the live candle or one very close to it. 8 is a wide safety
// margin — a far-late trade self-corrects at the next candle close, which is
// structural. Keeps per-tick cost O(1) in total candle count (see updateChartData).
const AGG_RECENCY_CANDLES = 8

// ── Spike gate — ONE admission rule, shared with the server ──
// Port of server_swaps.py SpikeGate (same constants — keep the two in sync).
// The server already runs this rule on every Solana path (live feed, raw
// window, gap_fill, /sol/profile); the client runs it again on the LIVE
// stream and the buffer_dump as defence against a bad client-side mcap (the
// trade_mcap_sol vs price×supply fallback).
//   ref = median of the last SPIKE_REF_N ADMITTED mcaps (count-based — trade
//         size never moves it), d = |ln(mcap / ref)|
//   d <= 8%  → admit now      d > 10x → reject
//   else HOLD until ceil(d / SPIKE_STEP) confirmers arrive (2 at 8–17%, 3 at
//   17–26% … 6 at +50%): later prints on the same side, at least half as far
//   out, each from a different wallet; beyond ±50% on a multi-pool coin they
//   must also span ≥2 pools. Window: SPIKE_CONFIRM_MS, or the next
//   SPIKE_CONFIRM_PRINTS prints on a thin coin, capped at SPIKE_CONFIRM_MAX_MS.
// Futures (HL/CEX feeds, no wallets, no phantom prints) bypass the gate.
const SPIKE_FILTER_ON = true
const SPIKE_FREE = Math.log(1.08)
const SPIKE_STEP = Math.log(1.08)
const SPIKE_WIDE = Math.log(1.5)
const SPIKE_HARD = Math.log(10)
const SPIKE_CONFIRM_MS = 30000
const SPIKE_CONFIRM_PRINTS = 12
const SPIKE_CONFIRM_MAX_MS = 600000
const SPIKE_REF_N = 16
const SPIKE_BOOT_N = 3
const SPIKE_POOL_RECENT_MS = 600000

export class SpikeGate {
  constructor() { this.reset() }
  reset() {
    this.ring = []          // admitted log-mcaps, chronological
    this.srt = []           // same values, sorted
    this.pending = []       // held far prints
    this.boot = []          // cold-start prints awaiting a seed reference
    this.pools = new Map()  // pool -> last ts seen
  }
  ref() { const s = this.srt; return s.length ? s[s.length >> 1] : null }
  _push(lp) {
    this.ring.push(lp)
    const s = this.srt
    let lo = 0, hi = s.length
    while (lo < hi) { const m = (lo + hi) >> 1; if (s[m] < lp) lo = m + 1; else hi = m }
    s.splice(lo, 0, lp)
    if (this.ring.length > SPIKE_REF_N) {
      const old = this.ring.shift()
      let a = 0, b = s.length
      while (a < b) { const m = (a + b) >> 1; if (s[m] < old) a = m + 1; else b = m }
      s.splice(a, 1)
    }
  }
  // Trusted values (already admitted server-side) — warms the reference.
  prime(values) {
    for (const v of values) if (v > 0) this._push(Math.log(v))
    this.boot = []
  }
  // Returns the items admitted NOW: earlier held prints this one confirmed
  // (in confirmation order), then this one if it is admitted at once.
  feed(ts, value, wallet, pool, item) {
    if (!(value > 0)) return []
    const lp = Math.log(value)
    if (pool) {
      this.pools.set(pool, ts)
      if (this.pools.size > 64) {
        for (const [k, t] of this.pools) if (ts - t > SPIKE_POOL_RECENT_MS) this.pools.delete(k)
      }
    }
    if (!wallet || wallet === 'unknown') wallet = null
    if (!this.srt.length) {
      this.boot.push([ts, lp, wallet, pool, item])
      if (this.boot.length < SPIKE_BOOT_N) return []
      const vals = this.boot.map(b => b[1]).sort((a, b) => a - b)
      const seed = vals[vals.length >> 1]
      const boot = this.boot
      this.boot = []
      const out = []
      for (const b of boot) out.push(...this._judge(b[0], b[1], b[2], b[3], b[4], seed))
      return out
    }
    return this._judge(ts, lp, wallet, pool, item, null)
  }
  _judge(ts, lp, wallet, pool, item, seedRef) {
    const admitted = []
    if (this.pending.length) {
      const keep = []
      for (const c of this.pending) {
        c.seen++
        const age = ts - c.ts
        if (age > SPIKE_CONFIRM_MAX_MS || (age > SPIKE_CONFIRM_MS && c.seen > SPIKE_CONFIRM_PRINTS)) continue
        const dq = lp - c.ref
        if ((dq > 0) === (c.d > 0) && Math.abs(dq) >= Math.abs(c.d) * 0.5 && Math.abs(dq) <= SPIKE_HARD
            && wallet && wallet !== c.w && !c.cw.has(wallet)) {
          c.cw.add(wallet)
          if (pool) c.cp.add(pool)
          if (c.cw.size >= c.need && (!c.div || c.cp.size >= 2)) {
            this._push(c.lp)
            admitted.push(c.item)
            continue
          }
        }
        keep.push(c)
      }
      this.pending = keep
    }
    const ref = (seedRef != null && !this.srt.length) ? seedRef : this.ref()
    const d = lp - ref
    const ad = Math.abs(d)
    if (ad <= SPIKE_FREE) {
      this._push(lp)
      admitted.push(item)
    } else if (ad <= SPIKE_HARD) {
      let np = 0
      for (const t of this.pools.values()) if (ts - t < SPIKE_POOL_RECENT_MS) np++
      this.pending.push({
        ts, lp, ref, d, w: wallet, need: Math.ceil(ad / SPIKE_STEP),
        div: ad > SPIKE_WIDE && np > 1, cw: new Set(), cp: new Set(pool ? [pool] : []),
        seen: 0, item,
      })
    }
    return admitted
  }
}

// TEMP source probe — logs the two candidate mcaps (server trade_mcap_sol vs the
// price×supply fallback) when they disagree, so we can see WHICH number is bad
// and where it comes from. Capped per token. Set false / delete once known.
const SPIKE_PROBE = true

// ── Fine-fold TF engine ──
// A canonical FINE candle series (5s) is maintained live alongside the
// display candles. Timeframe changes then FOLD fine candles into the
// display interval — exact integer-lattice level merges (fine and display
// share nativeBucketRef, so stepIdx adds directly; no re-snap, volume
// conserved), synchronous, no raw-trade rescan, no IDB round-trip for the
// live region. Legacy rebuildLiveFromTrades remains the fallback for any
// TF the 5s base doesn't divide, and behind the flag for instant revert.
const USE_FINE_FOLD = true
const FINE_INTERVAL_SEC = 5
const FINE_INTERVAL_MS  = FINE_INTERVAL_SEC * 1000
// Solana fine series now covers only the RAW window (history comes from
// /sol/profile as cell candles, never through the fine series). 6.5h keeps
// room for the legacy 6h train an old server sends (see SOL_HISTORY below).
const FINE_RETENTION_SOL_MS = 6.5 * 60 * 60 * 1000
const FINE_RETENTION_FUT_MS = FUTURES_TRADE_RETENTION_MS  // match the raw-buffer cap: fold and legacy rebuild cover the identical window
// ─── Local trade persistence switch ───────────────────────────────────────
// Solana trade persistence to IndexedDB is OFF: the server's swap archive
// backfills the recent window on subscribe, so the local copy is redundant —
// and it was the source of the stale-price spikes and the empty-gap "stitch"
// (an old local cluster + the server window with a hole between). HL futures
// has no server-side trade archive, so its local persistence stays ON.
const PERSIST_SOL_TRADES_LOCALLY = false
// ── Solana history (/sol/profile) — the futures recipe ───────────────────
// History used to arrive as a 6h RAW train over the websocket (20k-trade
// chunks, merged backward, folded through a 26h fine series). It now comes
// pre-aggregated per slot from the server (SQL GROUP BY over swaps.db) as
// cell candles — shallow window first, then older slabs — exactly like
// futures. The socket only replays a short raw window (SOL_RAW_WINDOW_MS)
// for the live candle, tape, VWAP seed and trail. Raw-trade features
// (wallet badges, buffer spike pass, trail) therefore cover that window.
// If the server predates the route (404) the client falls back to the
// legacy full train for the rest of the session (_solProfileMissing).
const SOL_HISTORY_ENABLED = true
const SOL_RAW_WINDOW_MS = 30 * 60 * 1000
const SOL_HIST_MAX_SEC = 72 * 3600         // server clamps to the same
const SOL_HIST_SHALLOW_SEC = 2 * 3600      // first (commit-blocking-ish) window
const SOL_HIST_SLAB_SEC = 6 * 3600         // older depth, one request each
const SOL_GATE_HOLD_MS = 2500              // max reveal hold for the shallow window
// solprof-20261017: futures-style chunking. When the server serves Solana
// history from its pre-built bars (sol_prof.db), every response carries
// fastFrom = the ms from which bars cover up to ~now. Windows inside that
// span load like futures: 12h first, then 24h pieces, short timeout, ONE
// try. Anything older than fastFrom, any TF that isn't whole minutes, or an
// old server (no fastFrom) keeps the old 2h + 6h-slab, 3-try schedule.
// A failed fast window falls back to the old schedule for that load.
// Set SOL_FAST_CHUNKS = false to force the old behavior everywhere.
const SOL_FAST_CHUNKS = true
const SOL_FAST_FIRST_SEC = 12 * 3600
const SOL_FAST_SLAB_SEC = 24 * 3600
const SOL_FAST_TIMEOUT_MS = 8000
let _solFastFromMs = (() => { try { return Number(localStorage.getItem('solcheddar:solFastFrom')) || 0 } catch { return 0 } })()
function _setSolFastFrom(ms) {
  const v = Number(ms) > 0 ? Number(ms) : 0
  if (v === _solFastFromMs) return
  _solFastFromMs = v
  try { localStorage.setItem('solcheddar:solFastFrom', String(v)) } catch {}
}
const persistAllowed = (key) => !!key && (PERSIST_SOL_TRADES_LOCALLY || !key.startsWith('sol:'))

// ── Diagnostic breadcrumb trail ─────────────────────────────────────────
// Every lifecycle event (ws open/close/bounce, sleep/wake, subscribe/ack,
// backfill chunks, dumps, commits, heals, resets) drops a crumb into a ring
// exposed as window.__cheddarTrail, and the important ones persist to
// localStorage so a freeze or reload keeps the story. window.__cheddarBuild
// answers "is this phone even running the current bundle?" — the stale-PWA
// check. Read after any weirdness:
//   JSON.stringify(window.__cheddarTrail)          (this session)
//   localStorage.getItem('solcheddar:trail')       (survives reload/freeze)
export const CHEDDAR_BUILD = '2026-07-28-4'
const _trail = []
function crumb(tag, info) {
  try {
    _trail.push({ t: new Date().toISOString().slice(11, 23), tag, ...(info || {}) })
    if (_trail.length > 250) _trail.splice(0, _trail.length - 250)
    if (typeof window !== 'undefined' && !window.__cheddarTrail) {
      window.__cheddarTrail = _trail
      window.__cheddarBuild = CHEDDAR_BUILD
    }
    if (/^(ws|sleep|wake|subscribed|subscribe|backfill-done|dump|heal|reset|fut|tf)/.test(tag)) {
      localStorage.setItem('solcheddar:trail', JSON.stringify(_trail.slice(-120)))
    }
  } catch {}
}

// Debug: registry of mounted useChartData instances (see __cheddarCharts).
const _dbgChartInstances = new Set()

// ─── Slot-aware history merge (the seam-gap fix) ────────────────────────
// The old rule `keep kline iff startTime < liveNow[0].startTime` assumed the
// live region is CONTIGUOUS from its first candle to now. It isn't whenever
// the trade buffer has holes — a Tier-A restore whose trades end before now
// (any return to a pair), a WS outage, a backgrounded phone tab. Every kline
// covering those holes was thrown away, leaving a permanent time-skip between
// "history loaded" and "where live data starts" — and every TF change re-ran
// the same cut, so the gap could never heal.
//
// New rule, strictly ADDITIVE vs the old one:
//   keep a kline if (a) it predates the first live candle  (the old rule —
//   including its accepted seam overlap on sub-interval TFs), OR (b) NO live
//   candle touches the kline's own lattice window (a hole → the kline is the
//   only data we have for that span; rescue it).
// In the healthy contiguous case (b) never fires and the output is identical
// to the old filter. Klines ride the SOURCE lattice (1m on 15/30/60s charts,
// 15m on the 30m chart), so coverage is tested per source window, derived
// from the data itself — no duplicated vendor interval map to drift.
export function filterHistoricalBySlots(filtered, liveNow, intervalMs) {
  if (!filtered || filtered.length === 0) return []
  if (!liveNow || liveNow.length === 0) return filtered.slice()

  // Source lattice = smallest positive gap between consecutive klines
  // (they arrive sorted). Falls back to the display interval when there's
  // only one kline to look at.
  let lat = Infinity
  for (let i = 1; i < filtered.length; i++) {
    const d = filtered[i].startTime - filtered[i - 1].startTime
    if (d > 0 && d < lat) lat = d
  }
  if (!Number.isFinite(lat) || lat < 1000) lat = intervalMs

  // Mark coverage at DISPLAY granularity, judge klines at FULL-WINDOW
  // granularity. A live candle can only honestly claim its own span
  // [start, start + intervalMs) — the old version marked the whole lattice
  // window covered when ANY data-bearing candle touched it, which on
  // sub-lattice TFs (15s/30s/60s on the 1m lattice) let ONE restored trade
  // in one 15s slot discard the kline covering the other 45 seconds. Live
  // sessions never showed it (continuous trades cover every sub-slot), but
  // a Tier-A restore is sparse by nature, so every refresh combed gaps into
  // the recent history ("thinks I have data I don't"). Now a kline is
  // dropped only when EVERY display slot inside its lattice window carries
  // real data; a partially covered window keeps its kline, and the dedupe's
  // richer-by-volume rule resolves the one shared startTime in the kline's
  // favor — strictly more data on screen, upgraded to cells by the deep
  // fetch afterward.
  // EMPTY live candles (wake-filler / timer slots that never saw a trade)
  // still claim nothing: they carry no data, so a kline is strictly better
  // for their span — letting them block rescue is how blank columns froze in.
  // When intervalMs >= lat, g === lat and each window holds exactly one
  // slot, so this reduces to the previous behavior bit-for-bit.
  const g = Math.min(intervalMs, lat)
  const covered = new Set()
  for (const c of liveNow) {
    const hasData = (c._liveLevels && c._liveLevels.size > 0) || (c._native && c._native.length > 0)
    if (!hasData) continue
    const end = c.startTime + intervalMs
    for (let s = Math.floor(c.startTime / g) * g; s < end; s += g) covered.add(s)
  }
  const fullyCovered = (h) => {
    const w0 = Math.floor(h.startTime / lat) * lat
    for (let s = w0; s < w0 + lat; s += g) {
      if (!covered.has(s)) return false
    }
    return true
  }

  const liveStart = liveNow[0].startTime
  return filtered.filter(h =>
    h.startTime < liveStart ||
    !fullyCovered(h)
  )
}

// ─── Extremes stamp (data-layer fix) ───
// The renderer derives wick tips from candle.high_mcap / low_mcap and falls
// back to scanning LEVEL KEYS when they're absent. Level keys are the bucket
// lattice, so those candles' wicks moved whenever the display bucket changed
// with scale, while bodies (raw open/close scalars) stayed put — wicks and
// bodies plotting off different geometries. Every constructor (trades,
// klines, OM upgrade) stamps extremes, but candles can still reach the
// renderer without them via folds/restores/packing. This stamps any such
// candle ONCE from its own NATIVE bucket (fixed at fetch time, immune to
// zoom), as a conservative envelope: low = lowest level's floor, high =
// highest level's floor + one native bucket, so the wick always contains the
// true range and never reads short. Idempotent and cheap — candles that
// already carry extremes are skipped by a null check. Wrapping dedupe means
// every existing commit path inherits it with no call-site edits.
function _stampExtremes(list) {
  let _stamped = 0
  for (let i = 0; i < list.length; i++) {
    const c = list[i]
    if (!c || (c.high_mcap != null && c.low_mcap != null)) continue
    // Already proved empty of levels+OHLC — don't re-walk forever (N/N log).
    if (c._extremesUnstampable) continue
    let hi = -Infinity, lo = Infinity
    const nat = c._native
    if (nat && nat.length >= 3) {
      for (let k = 0; k < nat.length; k += 3) {
        if ((nat[k + 1] || 0) + (nat[k + 2] || 0) === 0) continue
        const p = nat[k]
        if (p > hi) hi = p
        if (p < lo) lo = p
      }
    } else if (c._liveLevels && c._liveLevels.size > 0) {
      for (const [p, lv] of c._liveLevels) {
        const b = Array.isArray(lv) ? (lv[0] || 0) + (lv[1] || 0) : ((lv?.buy || 0) + (lv?.sell || 0))
        if (b === 0) continue
        if (p > hi) hi = p
        if (p < lo) lo = p
      }
    }
    // No levels yet: fall back to body OHLC so the stamp STICKS. Counting
    // these as stamped then `continue` left high/low null forever and the
    // set grew 8/8 → 9/9 → 11/11 on every compose.
    if (!Number.isFinite(hi) || !Number.isFinite(lo)) {
      const o = c.open_mcap, cl = c.close_mcap
      if (Number.isFinite(o) || Number.isFinite(cl)) {
        const a = Number.isFinite(o) ? o : cl
        const b = Number.isFinite(cl) ? cl : o
        hi = Math.max(a, b)
        lo = Math.min(a, b)
      }
    }
    if (!Number.isFinite(hi) || !Number.isFinite(lo)) {
      c._extremesUnstampable = true
      continue
    }
    // Bucket CENTER, not outer edge. The first cut used hi + B / lo + 0 — a
    // conservative envelope guaranteed to contain the true range, but padded
    // by a full NATIVE bucket, which on a fine display bucket is visibly
    // wider than what's on screen: wicks read systematically high AND low
    // versus the body at every zoom. The level key is its bucket's floor and
    // the true extreme is uniformly distributed within it, so the center is
    // the expected value — symmetric, and half the error.
    const B = c._nativeBucket || 0
    if (c.high_mcap == null) c.high_mcap = hi + B / 2
    if (c.low_mcap == null) c.low_mcap = lo + B / 2
    _stamped++
  }
  // ─── INSTRUMENTATION (temporary) ───
  // Names the load path that drops extremes: correlate this line with the
  // commit logs around it ([Backfill fold], pair-commit, deep merge, Tier-A,
  // TF change). Whichever path spikes the count is the culprit; the fix is
  // to carry true extremes through THAT path, after which this reads 0 and
  // the stamp becomes a never-firing safety net. Remove once it reads 0.
  if (_stamped > 0) dbg(`[Extremes] stamped ${_stamped}/${list.length} candles missing high/low`)
  return list
}
const deduplicateCandles = (arr) => _stampExtremes(_dedupeRaw(arr))

export default function useChartData({
  // Refs & state the main component owns but we need to read/write
  publicKeyRef,
  mcapBucketSize,
  setMcapBucketSize,
  timeInterval,
  autoScrollEnabled,
  viewWindowSizeRef,
  bodyScrollRef,
  showToast,
  scrollToLatest,
  // Callbacks that fire when we detect own trades (trading hook handles the actual position logic)
  onOwnBuyDetected,
  // (chart2-bounce) Slot identity ('main' | 'B'). Scopes the live-commit hold
  // (dataCommitGate) and the wallet-stats owner to this chart.
  chartId = 'main',
}) {
  const chartIdRef = useRef(chartId)
  chartIdRef.current = chartId
  // --- State ---
  const [chartData, setChartData] = useState({
    sortedMcaps: [],
    candles: [],
    maxVolume: 0,
    highlightMcap: 0,
    tradeCount: 0,
    structuralVersion: 0,
    tf: timeInterval,
    fitGen: 0,
  })
  const [stats, setStats] = useState({
    buyVolume: 0, sellVolume: 0, delta: 0, tradeCount: 0, marketCap: 0,
  })
  const [recentTrades, setRecentTrades] = useState([])
  const [status, setStatus] = useState('disconnected')
  // True while a timeframe-change historical re-fetch is in flight. The
  // reveal gate holds the cover until this clears: build the full new
  // state (rebinned slots + history), THEN render once — no intermediate
  // partial-chart frames. The ref mirrors the state SYNCHRONOUSLY so the
  // gate's rAF callback can re-check it before revealing — state alone
  // leaves a one-paint window where the gate passes before the pending
  // commit lands (the two-candle flash → blank → chart sequence).
  const [historyPending, setHistoryPending] = useState(false)
  const historyPendingRef = useRef(false)
  // Guard timer for the subscribe-time pending arm: if the server's
  // 'backfill' frame never arrives (old server build, dropped frame),
  // this clears pending so the reveal gate can't stay wedged.
  const backfillGuardRef = useRef(null)
  // Last token we received a 'subscribed' ack for. A reconnect re-subscribes
  // the SAME token — that resub must not hard-reset the y-bucket to 100 (the
  // established bucket is already right for this coin; resetting it forces a
  // 100 → smartBucket → ideal cascade, each step a full regroup+relayout =
  // the post-reconnect flashing).
  const lastSubscribedTokenRef = useRef(null)
  // Token whose backfill train completed on THIS connection. Lets the
  // 'subscribed' ack distinguish a late ack racing behind an already-
  // delivered replay (skip the wipe!) from every legitimate reset case.
  const backfillDoneTokenRef = useRef(null)
  // Chunked-backfill rebuild throttle: chunks merge into the buffer cheaply;
  // full rebuilds are the expensive part, so they run at most ~2/sec while a
  // chunk train streams (plus always on the first and final chunk).
  const bfLastRebuildRef = useRef(0)
  const bfDirtyRef = useRef(false)
  // True from subscribe until the backfill train's final chunk. While set, the
  // Solana raw-buffer cap in startNewCandle stands down, so deep backfill chunks
  // survive to seed the fine series (rebuildFineFromTrades reads the raw buffer
  // at finalChunk). Cleared on finalChunk, the 20s backfill guard timeout (old/
  // no-backfill server), and resetChart — so it can never wedge the cap off.
  const backfillActiveRef = useRef(false)
  // Hard failsafe for the flag above. The 20s no-frame guard is cancelled by
  // the FIRST backfill chunk, but only the FINAL chunk re-arms the raw-buffer
  // cap — so a train that starts and never finishes (disconnect mid-stream,
  // server deploy, tab sleep) left the cap wedged OFF for the whole session:
  // the buffer grew without bound and the per-close persist/rebuild work grew
  // with it — the "solid at first, lag builds over hours" pattern. This timer
  // is armed WITH the flag and cleared only by finalChunk/reset; if it fires,
  // the cap re-engages no matter what the train did.
  const backfillCapFailsafeRef = useRef(null)
  const markHistoryPending = useCallback((v) => {
    historyPendingRef.current = v
    setHistoryPending(v)
  }, [])
  const [currentToken, setCurrentToken] = useState('')
  const [tokenHistory, setTokenHistory] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('solcheddar_token_history') || '[]') } catch { return [] }
  })

  // --- Refs ---
  const wsRef = useRef(null)
  const candlesRef = useRef([])
  // Debug exposure. Use defineProperty so the bundler's dead-code
  // elimination doesn't drop the assignment — plain `window.x = y` looks
  // like a no-op side effect and gets stripped in production builds.
  if (typeof window !== 'undefined') {
    Object.defineProperty(window, '__cheddarCandles', {
      value: candlesRef,
      writable: true,
      configurable: true,
    })
  }
  // Raw trade buffer — columnar (Structure-of-Arrays) typed store. Holds the
  // full retention window in four typed columns (~25 B/trade, zero per-trade
  // GC objects) and hands strategy consumers a bounded, stable real-Array view
  // (see tradeBuffer.js). Lazily constructed once so the instance is stable
  // across renders; all mutation is in-place (push/trimBefore/prepend/
  // replaceAll/clear) — the ref's .current is never reassigned.
  const rawTradesRef = useRef(null)
  if (rawTradesRef.current === null) rawTradesRef.current = new TradeBuffer()
  const fineCandlesRef = useRef([])   // closed fine (5s) candles, chronological
  const currentFineRef = useRef(null) // fine candle currently receiving ticks
  const pairCommitRef = useRef(null)  // coin whose pair-switch commit owns the Tier-A restore
  if (typeof window !== 'undefined') {
    Object.defineProperty(window, '__cheddarTrades', {
      value: rawTradesRef,
      writable: true,
      configurable: true,
    })
    // Debug handle for gap forensics: inspect the live candle array from the
    // console (see the seam-gap probe snippet). Read-only usage only.
    Object.defineProperty(window, '__cheddarCandles', {
      value: candlesRef,
      writable: true,
      configurable: true,
    })
  }
  // Multi-instance registry — __cheddarCandles above is a single global that
  // the LAST-mounted chart slot overwrites (probing a dual layout grabbed the
  // idle slot's mount-artifact candle). __cheddarCharts() lists every live
  // chart instance with its identity so forensics target the right one.
  useEffect(() => {
    const inst = { hlCoinRef, currentTokenRef, candlesRef, timeIntervalRef, rawTradesRef, serverNowRef }
    _dbgChartInstances.add(inst)
    if (typeof window !== 'undefined') {
      window.__cheddarCharts = () => Array.from(_dbgChartInstances).map(i => ({
        coin: i.hlCoinRef.current,
        token: i.currentTokenRef.current,
        tf: i.timeIntervalRef.current,
        candles: i.candlesRef.current,
        trades: i.rawTradesRef.current,       // this instance's tape buffer
        now: () => i.serverNowRef.current(),  // this instance's app clock
      }))
    }
    return () => { _dbgChartInstances.delete(inst) }
  }, [])
  const currentCandleRef = useRef(null)
  const candleTimerRef = useRef(null)
  const candleTimeoutRef = useRef(null)
  const serverNowRef = useRef(() => Date.now())
  // ─── Server clock offset ───────────────────────────────────────────────
  // The browser can't read the server's wall clock directly; it only learns
  // it from the timestamps that ride inbound trades. We track the difference
  // serverTs - Date.now() (smoothed) so candle slots can be opened on SERVER
  // time. Without this, a new candle opens on the LOCAL clock (Date.now()
  // floored) while trades bucket on server time (their timestamps) — if the
  // two clocks differ, the fresh candle sits empty until server time crosses
  // the slot boundary (the "won't post until it matches the server" gap).
  // EMA-smoothed so one jittery packet doesn't swing the whole grid.
  const serverOffsetRef = useRef(0)        // ms to add to Date.now() → server time
  const serverOffsetSeenRef = useRef(false) // false until first real sample
  const recordServerClock = (serverMs) => {
    if (!Number.isFinite(serverMs)) return
    const sample = serverMs - Date.now()
    // Reject absurd samples (>10 min) — likely a parse artifact, not skew.
    if (Math.abs(sample) > 36 * 3600000) return   // was 10 min: that threw away exactly the DST/zone-sized skews this exists to fix
    if (!serverOffsetSeenRef.current) {
      serverOffsetRef.current = sample
      serverOffsetSeenRef.current = true
    } else {
      // EMA, alpha 0.1 — converges in ~20 trades, ignores per-packet jitter.
      serverOffsetRef.current = serverOffsetRef.current * 0.9 + sample * 0.1
    }
    setServerOffset(serverOffsetRef.current, 'trade')
  }
  // Server-time "now". Use for candle slot alignment + the live-candle timer
  // so opens and trade bucketing share ONE clock, and anywhere a Solana trade
  // timestamp is compared to "now".
  // ─── APP CLOCK: server time on the monotonic timer ─────────────────────
  // Synced on every token load (subscribe → 'subscribed') and every heartbeat
  // (ping → pong). The server stamps srv_in (receipt) and srv_out (send);
  // we stamp cl = performance.now() at send and read performance.now() on
  // arrival. Network delay = round trip − server processing; server time at
  // arrival = srv_out + delay/2. From then on serverNow() advances on
  // performance.now() — the local WALL clock is never read. A subscribe ack
  // always re-anchors (the "sync on token load"); a pong re-anchors only if
  // its round trip is no worse than the anchor's, or the anchor is >60s old.
  // Until the first sample lands (the first ~RTT after connect), the old
  // trade-timestamp estimate stands in.
  const appClockRef = useRef({ srv: 0, perf: 0, delay: Infinity, ok: false })
  const applyClockSync = (data, force) => {
    const t1 = performance.now()
    const t0 = Number(data?.cl), sIn = Number(data?.srv_in), sOut = Number(data?.srv_out)
    if (!Number.isFinite(t0) || !Number.isFinite(sIn) || !Number.isFinite(sOut)) return
    const delay = Math.max(0, (t1 - t0) - Math.max(0, sOut - sIn))
    const c = appClockRef.current
    if (!force && c.ok && delay > c.delay && t1 - c.perf < 60000) return
    c.srv = sOut + delay / 2
    c.perf = t1
    c.delay = delay
    c.ok = true
    setServerOffset(c.srv + (performance.now() - t1) - Date.now(), 'ws')
  }
  // A delayed feed (CME free tier, 15m) runs the chart clock on FEED time, so
  // the live candle is the slot the delayed prints actually land in.
  const feedDelayRef = useRef(0)   // set by subscribeHyperliquid (CME → CME_FEED_DELAY_MS)
  const serverNow = () => {
    const c = appClockRef.current
    const t = c.ok ? c.srv + (performance.now() - c.perf) : appNow()
    return t - feedDelayRef.current
  }
  serverNowRef.current = serverNow
  // Console handle: the app clock (server time). Lag probe = __cheddarNow()
  // minus a trade's timestamp (the server stamps it at emit).
  if (typeof window !== 'undefined') {
    Object.defineProperty(window, '__cheddarNow', { value: serverNow, writable: true, configurable: true })
  }
  // ONE clock for Solana + futures: server-aligned app time. HL/CEX trades
  // carry exchange timestamps for bucketing; slot opens / trail age use this.
  const tapeNow = () => serverNow()
  // RAW last price — the actual print (or last history close before any live
  // trade). Despite the name, NEVER snapped: this is the trading price
  // (entryMcap, markPrice, bot stops) and the chip text. Snapping it to the
  // display bucket made zoom level change PnL and could fire a stop with no
  // trade. Anything that needs a ROW maps it through bucketRowIndex /
  // findBucketIndex / proj.rowOfPrice, which all take an exact price.
  const lastBucketMcapRef = useRef(0)
  const tokenSupplyRef = useRef(1000000000)
  const liveStartTimeRef = useRef(0)
  // Bridge to _healFuturesHistory (defined later in the hook body) for
  // mount-time closures: ws onopen + the wake handler call through this ref.
  const healHistoryRef = useRef(null)

  // ─── Gap fill ───
  // A placeholder candle is minted at the exact moment we KNOW a span was
  // missed — that knowledge used to be thrown away (the gapCandles array was
  // local, logged, discarded), leaving an empty candle indistinguishable
  // from a genuinely quiet period. Nothing could tell them apart afterwards,
  // which is why sleeping left permanent holes even though every swap was
  // sitting in the server's SQLite the whole time (the wake dump replays an
  // in-memory buffer, and a long sleep OVERFLOWS it — the `dropped` count in
  // the dump log is exactly the data that never came back).
  //
  // So: whenever placeholders are created, immediately ask for their span.
  // No scanning, no heuristics, no ambiguity about quiet periods — the
  // request is made once, at the one moment the answer is certain. If the
  // server returns nothing, the period really was quiet and the
  // placeholders were right.
  const gapAskedRef = useRef(new Map())    // "mint|since|until" -> ts asked
  const verifiedEmptyRef = useRef(new Set())  // slot starts confirmed genuinely quiet
  // ─── Reconciler ───
  // Healing used to be wired as EVENTS: three hardcoded call sites (wake,
  // reconnect, watchdog), each added after someone noticed a new way to
  // lose data. That is why this bug kept coming back — every fresh way to
  // be interrupted (a timeframe change, a token switch mid-fetch, a buffer
  // overflow) needed someone to remember to add another trigger, and the
  // list of ways reality can interrupt you is not enumerable.
  //
  // So: no triggers. One reconciler on a timer asks the only question that
  // matters — "are there slots I don't have data for?" — and fills them. It
  // needs no knowledge of WHY data is missing, which is exactly why it
  // cannot be defeated by a cause nobody thought of.
  //
  // Slots that come back genuinely empty are marked verified so a quiet
  // period is asked about once and never again.
  const reconcile = () => {
    const candles = candlesRef.current
    if (!candles || candles.length < 3) return
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    if (isSleepingRef.current) return
    // A backfill train OWNS the history phase while it is in flight. The
    // rising-edge pass fires 1500ms after 'connected' — well inside a train
    // that can run for seconds — and the slots it would ask about are the
    // very ones the train is about to fill. gap_fill's fold calls
    // addTickToLive on whatever candle sits at the slot, creating one if
    // absent; it has no placeholder check, so an answer landing after the
    // train had filled the slot would DOUBLE the volume there.
    //
    // This is only a real race once a since-train can fill those slots
    // (before, the full train's backward merge could never reach them), but
    // the guard is correct either way. Deferring rather than skipping is the
    // point: finalChunk re-arms a pass below, so the reconciler still gets
    // its look at every connection — it just takes it after the train, when
    // the remaining holes are the genuine ones.
    if (backfillActiveRef.current) return

    const intervalMs = (timeIntervalRef.current || 300) * 1000
    const now = tapeNow()
    const holes = []
    // Skip the forming candle: it is empty because it is NEW, not missing.
    for (let i = 0; i < candles.length - 1; i++) {
      const c = candles[i]
      if (!c) continue
      if ((c.startTime + intervalMs) > now) continue          // not closed yet
      if (verifiedEmptyRef.current.has(c.startTime)) continue // asked, genuinely quiet
      const vol = (c.buyVolume || 0) + (c.sellVolume || 0) || c.volume || 0
      // _native is a Float64Array (packed levels) — .length, not Map.size.
      // Using .size made every cell candle look empty → perpetual holes →
      // heal('reconcile') → _deepFutHistoryFetch({force:true}) every pass,
      // stacking overlapping deep ladders (the red spikes).
      const hasCells = !!(c._native && c._native.length) || !!(c._liveLevels && c._liveLevels.size)
      if (vol === 0 && !hasCells) holes.push(c.startTime)
    }
    if (holes.length === 0) return

    if (hlCoinRef.current) {
      // Futures: klines + cells come back through the existing heal.
      healHistoryRef.current?.('reconcile')
    } else {
      requestGapFill(holes, intervalMs, 'reconcile')
    }
  }
  const reconcileRef = useRef(reconcile)
  reconcileRef.current = reconcile

  // ─── When to reconcile ───
  // Not on a timer, and not "on wake" either — both were wrong for the same
  // reason. A timer polls forever for an event that only happens at discrete
  // moments; a wake trigger fires while the socket is still reconnecting, so
  // the request goes nowhere and the gap becomes permanent. That second one
  // is the original bug: the old code DID heal on wake, it just never
  // checked whether the attempt landed.
  //
  // So: trigger on the RISING EDGE of readiness. The moment the transport is
  // genuinely connected — whatever got us there, wake, reconnect, first
  // load, a server restart — run one pass. Once per transition, not once
  // per session, because each new connection is a fresh chance that the
  // previous attempt never had.
  const wasReadyRef = useRef(false)
  useEffect(() => {
    const ready = status === 'connected'
    const rose = ready && !wasReadyRef.current
    wasReadyRef.current = ready
    if (!rose) return
    // One tick of settle: subscribe/backfill land right after 'connected',
    // and reconciling before them would ask about slots that are about to
    // fill themselves.
    const id = setTimeout(() => { try { reconcileRef.current?.() } catch {} }, 1500)
    return () => clearTimeout(id)
  }, [status])

  // Token changes only. A TF switch never re-asks the server for RAW trades:
  // the raw window, live feed and fine series are refolded locally and the
  // only fetch is the new TF's profile history (the TF effect below).
  useEffect(() => {
    const id = setTimeout(() => { try { reconcileRef.current?.() } catch {} }, 800)
    return () => clearTimeout(id)
  }, [currentToken])

  const requestGapFill = (starts, intervalMs, why) => {
    if (!starts || starts.length === 0) return
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const mint = currentTokenRef.current
    if (!mint) return
    const since = Math.min(...starts)
    const until = Math.max(...starts) + intervalMs
    const key = `${mint}|${since}|${until}`
    const askedAt = gapAskedRef.current.get(key)
    if (askedAt && Date.now() - askedAt < 60000) return   // one ask per span per minute
    gapAskedRef.current.set(key, Date.now())
    if (gapAskedRef.current.size > 200) gapAskedRef.current.clear()
    crumb('gap-ask', { why, n: starts.length, mins: Math.round((until - since) / 60000) })
    dbg(`[GapFill] ${why}: ${starts.length} slot(s) missing, ${Math.round((until - since) / 60000)}min — requesting`)
    try { ws.send(JSON.stringify({ action: 'gap_fill', mint, since, until })) } catch {}
  }
  // Retained pre-seam history source (per coin|tf). Every seam site
  // (pair commit / heal / deep fetch) stores its MERGED, pre-filter array
  // here so seam coverage can be RE-derived later instead of being frozen
  // at fetch time. The old lifecycle dropped a history candle the instant a
  // live slot covered it and threw the source away — then the 3h retention
  // rolled, the slot pruned empty, and the span belonged to nobody. That is
  // the 30m "gap" (15m history vs 30m live slots) AND why sleep spans that
  // a wake heal correctly filled could re-open later. Store events, derive
  // views — applied to the seam.
  // ─── Futures history STORE: one owner, one composer, dumb writers ───
  // Map startTime → historical candle (kline or cell) per `coin|tf`. Every
  // server load — klines, shallow profiles, deep slabs, heals — writes here
  // through _futStorePut and repaints through _composeFut. Nothing else
  // assembles the historical region. This replaces the old futHistSourceRef
  // `filtered` array, whose preservation rules were re-implemented (and
  // repeatedly desynced) in the heal, the reseam AND the deep landing —
  // four incidents in one day were three writers disagreeing about one
  // truth. Now there is no rule to copy: writers put, the composer merges.
  const futStoreRef = useRef({ key: null, map: new Map() })
  // ─── Live-region FLOOR: this session's stream is the only live there is ──
  // Set once per futures subscribe. Any non-ticking live candle whose slot
  // ended before this moment was manufactured from the IDB restore — a
  // strict SUBSET of what the server's capture holds (capture saw every
  // market fill while the tab was closed; the browser didn't). Every
  // arbitration bug this subsystem has produced — slivers, comb coverage,
  // the scattered first paint — was partial restore candles contesting
  // slots against complete history. The floor ends the contest: pre-floor
  // slots belong to the STORE, and restored trades keep only their
  // legitimate job (the tape). Reconnect backfills are unaffected — they
  // land after the floor and carry the server's complete tape anyway.
  const futLiveFloorRef = useRef(0)

  // ─── The SEAM ───
  // Between the newest CLOSED slot and the moment this client started
  // streaming lies a span nobody owned: the server quantized its window down
  // to a slot boundary, and the client subscribed somewhere inside the slot
  // that followed. Measured at 27s of the newest candle, healing on the next
  // slot roll — which is why it read as a flicker rather than a bug.
  //
  // The server now serves that partial slot (the "tip") and returns the exact
  // instant its data ends. That turns the history/live split from a
  // candle-granularity inference into a POINT on the timeline:
  //   server owns  ts <  cutoff
  //   client owns  ts >= cutoff
  // Union is everything, intersection is empty.
  //
  // The tip cannot live in the store: _composeFut only admits candles whose
  // slot has CLOSED, and the tip's slot is open by definition (that filter is
  // what dropped the tip on arrival). So it lives INSIDE the live candle,
  // underneath the trades the client saw. filterHistoricalBySlots never learns
  // the seam exists.
  //
  // { key, cutoffMs, slotStart, tips[] } — keyed coin|tf like the store, so a
  // pair or TF switch invalidates it rather than seeding the wrong lattice.
  const futSeamRef = useRef(null)
  // ─── Solana history store (/sol/profile) ───
  // Same shape and rules as the futures store, kept separate so neither
  // venue's lifecycle can touch the other's. key = `${mint}|${tf}`.
  const solHistRef = useRef({ key: null, map: new Map() })
  // Kept 1m Solana store ({key,map,cov}) so a TF switch folds from it (up) or
  // restores it (back to 1m) instead of refetching.
  const solFineRef = useRef(null)
  // Solana seam: { key, cutoffMs, slotStart, tips[] } — the server's tip of
  // the still-open slot [slotStart, cutoff) when this client's raw window
  // starts inside it (big TFs). _buildSeamCandle mints it for both venues.
  const solSeamRef = useRef(null)
  // Where this session's contiguous RAW coverage starts (the `since` of the
  // last non-continuation subscribe). Live slots at/after it are complete;
  // earlier live slots are partial and yield to a store cell.
  const solRawFromRef = useRef(0)
  const solHistSeqRef = useRef(0)            // newest full load wins
  const solTopUpBusyRef = useRef(false)
  const solProfileMissingRef = useRef(false) // server lacks /sol/profile → legacy train
  const solFitPendingRef = useRef(null)      // mint awaiting its first bucket fit + snap
  const solGateHoldRef = useRef(null)        // mint whose reveal waits on the shallow window
  const solGateTimerRef = useRef(null)
  const solShallowDoneRef = useRef(null)     // mint whose shallow window has resolved

  // Capture the seam from a commit's profile response. `om.profiles` carries
  // cutoffMs on the array (hlFootprintHistory attaches it there so the return
  // type never changed, and only for hoursOffset === 0 — a slabbed deep fetch
  // ends in the past and has no bearing on the live edge).
  //
  // LATTICE GUARD. OM_INTERVAL_MAP does not always give period === TF: the 15s
  // and 30s charts both ride 1m profiles, so one profile entry spans several
  // chart slots and there is no honest way to attribute it to one. Finer or
  // equal divides exactly (a 30m slot IS its two 15m halves summed — the same
  // rule _foldPrevStoreInto lives by), coarser never does. Refuse rather than
  // approximate; a seam we skip is the old 27s gap, a seam we fake is volume
  // in the wrong candle.
  const _captureFutSeam = (coin, tfSec, om) => {
    futSeamRef.current = null
    if (!om || !om.profiles) return
    const cutoffMs = om.profiles.cutoffMs
    if (!Number.isFinite(cutoffMs) || cutoffMs <= 0) return
    const periodSec = futProfilePeriodSec(coin, tfSec) || 0
    if (!(periodSec > 0) || tfSec % periodSec !== 0) return
    const intervalMs = tfSec * 1000
    const slotStart = Math.floor(cutoffMs / intervalMs) * intervalMs
    // Everything the server sent for the still-open slot. Usually one entry;
    // two on the 30m chart (15m profiles), which sum exactly.
    const inSlot = om.profiles.filter(p => {
      const t = p.ts * 1000
      return t >= slotStart && t < slotStart + intervalMs
    })
    if (inSlot.length === 0) return
    const tips = profilesToCellCandles(inSlot, { nativeBucket: om.nativeBucket })
    if (!tips.length) return
    futSeamRef.current = { key: `${coin}|${tfSec}`, cutoffMs, slotStart, tips }
    dbg(`[FutSeam] armed ${coin}|${tfSec}s cutoff=${new Date(cutoffMs).toISOString().slice(11, 19)} tips=${tips.length}`)
  }

  // Mint the seam slot from scratch: tip snapshot, then the raw trades at or
  // after the cutoff. Both sides exact — the tip is the server's own
  // aggregation of [slotStart, cutoff), and raw trades carry true timestamps,
  // so the split is the point itself and not a lattice rounding. Deliberately
  // reads the RAW buffer rather than the fine series even on the fold path: a
  // fine candle straddling the cutoff can be neither kept (double count) nor
  // dropped (hole), and there is no reason to accept either when the exact
  // timestamps are right there.
  //
  // Returns a FRESH candle every call and never mutates one already on the
  // chart. That is the whole idempotence argument — seedLiveFromTip is
  // additive by design (multi-tip must sum), so the guarantee has to come from
  // rebuild-not-add at the call site. seam.test.mjs pins it at ten rebuilds.
  // Null when there is no armed seam, a key mismatch, or a bucket the tip
  // cannot be folded onto — every one of which degrades to prior behavior.
  const _buildSeamCandle = () => {
    // One builder, two venues: futures keys on the HL coin, Solana on the
    // mint (subscribeHyperliquid nulls currentTokenRef, handleSubscribe
    // nulls hlCoinRef, so exactly one of these is ever set).
    const isFut = !!hlCoinRef.current
    const seamRef = isFut ? futSeamRef : solSeamRef
    const storeRef = isFut ? futStoreRef : solHistRef
    const seam = seamRef.current
    if (!seam) return null
    const coin = isFut ? hlCoinRef.current : currentTokenRef.current
    const tfSec = timeIntervalRef.current
    if (!coin || seam.key !== `${coin}|${tfSec}`) return null
    const slotEnd = seam.slotStart + tfSec * 1000
    // EXPIRY. Once the slot has closed AND the store holds a real cell for it,
    // the server's own aggregation of the whole slot is available and
    // _composeFut will serve it as historical (the closed-slot filter now
    // admits it). The tip was only ever a stand-in for the window before the
    // capture caught up; re-minting it past that point is pure waste on every
    // rebuild. A kline in that slot does NOT count — it is strictly poorer
    // than the footprint we are holding.
    if (serverNowRef.current() >= slotEnd && storeRef.current.key === seam.key
        && storeRef.current.map.get(seam.slotStart)?._omFootprint) {
      seamRef.current = null
      dbg(`[${isFut ? 'FutSeam' : 'SolSeam'}] released ${seam.key} — store cell covers the slot`)
      return null
    }
    // Minted at the TIPS' bucket (the server's grain), so the fold is always
    // exact; trades after the cutoff key onto it via addTickToLive. Normally
    // equal to the live grain; when the server's pinned grain differs this
    // one candle is coarser instead of refusing (the old 27s seam gap).
    const bucket = seam.tips[0]?._nativeBucket || nativeBucketRef.current || mcapBucketSizeRef.current
    if (!(bucket > 0)) return null
    const c = newLiveCandle(seam.slotStart, bucket)
    let seeded = false
    for (const tip of seam.tips) if (seedLiveFromTip(c, tip)) seeded = true
    if (!seeded) return null            // bucket mismatch — refuse, don't smear
    rawTradesRef.current.forEach((time, tradeMcap, side, amount) => {
      if (time < seam.cutoffMs || time < seam.slotStart || time >= slotEnd) return
      addTickToLive(c, tradeMcap, side, amount)
      if (c.open_mcap == null) c.open_mcap = tradeMcap
      c.close_mcap = tradeMcap
      if (c.high_mcap == null || tradeMcap > c.high_mcap) c.high_mcap = tradeMcap
      if (c.low_mcap  == null || tradeMcap < c.low_mcap)  c.low_mcap  = tradeMcap
    })
    return c
  }

  // Ensure the store is keyed to this coin|tf; a mismatch means a pair or
  // TF switch happened — the old lattice's candles are garbage here (the
  // "stacked overnight columns" incident), so the map resets. Off-lattice
  // strays are structurally impossible now: a store only ever holds candles
  // built for its own key.
  const _futStoreFor = (coin, tfSec) => {
    const key = `${coin}|${tfSec}`
    if (futStoreRef.current.key !== key) futStoreRef.current = { key, map: new Map() }
    return futStoreRef.current.map
  }

  // Writer. Two candle classes, one precedence rule:
  //   kline → fills an empty slot, refreshes OHLC on another KLINE, and
  //           NEVER overwrites a cell (a candlestick is strictly poorer
  //           than a footprint for the same slot);
  //   cell  → replaces a kline always, replaces a locally FOLDED cell always
  //           (the TF refold is a stand-in until the server's own cell for
  //           the slot lands), replaces another cell iff richer by volume
  //           (server capture is a superset of anything partial).
  // doji-20261103: copy a real open/close onto a guessed cell, widening the wick.
  const _adoptOhlc = (cell, src) => {
    if (!cell || !src || !Number.isFinite(src.open_mcap) || !Number.isFinite(src.close_mcap)) return
    cell.open_mcap = src.open_mcap
    cell.close_mcap = src.close_mcap
    if (Number.isFinite(src.high_mcap)) cell.high_mcap = Number.isFinite(cell.high_mcap) ? Math.max(cell.high_mcap, src.high_mcap) : src.high_mcap
    if (Number.isFinite(src.low_mcap)) cell.low_mcap = Number.isFinite(cell.low_mcap) ? Math.min(cell.low_mcap, src.low_mcap) : src.low_mcap
    cell._ohlcGuess = false
  }
  const _futStorePut = (coin, tfSec, candles, { cells = false } = {}) => {
    const map = _futStoreFor(coin, tfSec)
    for (const c of candles) {
      const prev = map.get(c.startTime)
      if (!prev) { map.set(c.startTime, c); continue }
      const prevIsCell = !!prev._omFootprint
      if (cells) {
        if (!prevIsCell || prev._folded || candleTotalVolume(c) > candleTotalVolume(prev)) {
          // doji-20261103: keep the real open/close the slot already had.
          if (c._ohlcGuess && !prev._ohlcGuess) _adoptOhlc(c, prev)
          map.set(c.startTime, c)
        }
      } else if (!prevIsCell) {
        map.set(c.startTime, c)
      } else if (prev._ohlcGuess) {
        // doji-20261103: a kline landing after the cell used to be dropped
        // whole, leaving the cell's guessed open = close. Take its OHLC.
        _adoptOhlc(prev, c)
      }
    }
  }

  // ─── Fold-forward: an up-TF switch reuses what the browser KNOWS ───
  // Fine→coarse across TIME is exact — a 30m slot IS its six 5m slots
  // summed — so when the new TF is a clean multiple of the old, the old
  // store folds straight into the new one at commit and the chart shows
  // full cells instantly for everything already in RAM; the server's cells
  // replace them as they land (_folded). Rules, each one a lie
  // avoided: a CELL folds only when ALL constituents are cells (mixing a
  // kline in would mint a partial cell — the sliver bug by construction);
  // all-known-but-some-kline folds to an honest KLINE; ANY missing
  // constituent skips the slot — unknown is not zero. Coarse→fine never
  // folds: a 30m cell holds no information about its 5m interior
  // (the twice-failed rebin the invariant exists for).
  const _foldPrevStoreInto = (prev, coin, tfSec) => {
    try {
      if (!prev || !prev.key || !prev.map || prev.map.size === 0) return 0
      const [pCoin, pTfStr] = prev.key.split('|')
      const pTf = +pTfStr
      if (pCoin !== coin || !(pTf > 0) || tfSec <= pTf || tfSec % pTf !== 0) return 0
      const need = tfSec / pTf
      const tfMs = tfSec * 1000
      const groups = new Map()
      for (const c of prev.map.values()) {
        const t = Math.floor(c.startTime / tfMs) * tfMs
        let g = groups.get(t)
        if (!g) { g = []; groups.set(t, g) }
        g.push(c)
      }
      const cellsOut = []
      const klines = []
      for (const [t, g] of groups) {
        if (g.length !== need) continue
        if (g.every(s => s._omFootprint)) {
          const c = foldCellCandles(g, t)
          if (c) cellsOut.push(c)
        } else {
          g.sort((a, b) => a.startTime - b.startTime)
          let hi = -Infinity, lo = Infinity
          for (const s of g) {
            if (s.high_mcap > hi) hi = s.high_mcap
            if (s.low_mcap < lo) lo = s.low_mcap
          }
          klines.push({ startTime: t, open_mcap: g[0].open_mcap, high_mcap: hi, low_mcap: lo,
            close_mcap: g[g.length - 1].close_mcap, isHistorical: true, _folded: true })
        }
      }
      if (klines.length) _futStorePut(coin, tfSec, klines)
      if (cellsOut.length) _futStorePut(coin, tfSec, cellsOut, { cells: true })
      return cellsOut.length + klines.length
    } catch (e) {
      console.warn('[FutHist] fold-forward failed (server fills instead):', e?.message || e)
      return 0
    }
  }

  // The ONE assembler of candlesRef for futures. Store + live, three steps:
  //   1. SLIVER DEMOTION — a store cell richer than a same-slot live candle
  //      replaces it (the candle was rebuilt from a partial Tier-A restore;
  //      capture saw every fill, so the cell is a superset). The ticking
  //      candle is sacred: its data is still arriving.
  //   2. SLOT FILTER — historical yields only where live has FULL coverage
  //      (filterHistoricalBySlots, sub-slot honest since the comb fix).
  //   3. Dedupe + ticking-candle guarantee, one paint.
  const _composeFut = (why) => {
    const coin = hlCoinRef.current
    if (!coin) return
    const tfSec = timeIntervalRef.current
    const key = `${coin}|${tfSec}`
    if (futStoreRef.current.key !== key) return          // store not for this view
    const map = futStoreRef.current.map
    const intervalMs = tfSec * 1000
    const currentCandleStart = Math.floor(serverNowRef.current() / intervalMs) * intervalMs

    const liveAll = candlesRef.current.filter(c => !c.isHistorical)
    const liveKept = []
    const liveFloor = futLiveFloorRef.current
    for (const c of liveAll) {
      const isTicking = c === currentCandleRef.current
      // Pre-floor: the slot ENDED before this session started streaming —
      // whatever this candle holds came from the restore, and the store's
      // version is a superset. The slot straddling the floor stays (it is
      // the subscribe slot: partial like any venue's first candle, and it
      // completes live).
      if (!isTicking && liveFloor > 0 && c.startTime + intervalMs <= liveFloor) continue
      const hasData = (c._liveLevels && c._liveLevels.size > 0) || (c._native && c._native.length > 0)
      if (!isTicking && !hasData) continue               // empties release their slot
      if (!isTicking) {
        const cell = map.get(c.startTime)
        if (cell && candleTotalVolume(cell) > candleTotalVolume(c)) {
          // doji-20261103: the closed live candle saw the real first/last
          // trade; the server cell only has a profile mean. Keep its OHLC.
          if (cell._ohlcGuess) _adoptOhlc(cell, c)
          continue   // demoted: cell stands in historical
        }
      }
      liveKept.push(c)
    }

    const historical = []
    for (const c of map.values()) if (c.startTime < currentCandleStart) historical.push(c)
    historical.sort((a, b) => a.startTime - b.startTime)

    const validHistorical = filterHistoricalBySlots(historical, liveKept, intervalMs)
    let arr = deduplicateCandles([...validHistorical, ...liveKept]).slice(-maxCandlesFor(hlCoinRef.current, timeIntervalRef.current))
    let last = arr[arr.length - 1]
    if (!last || last.isHistorical || last.startTime < currentCandleStart) {
      const liveCandle = currentCandleRef.current && currentCandleRef.current.startTime === currentCandleStart
        ? currentCandleRef.current
        : newLiveCandle(currentCandleStart, nativeBucketRef.current ?? mcapBucketSizeRef.current)
      arr = deduplicateCandles([...arr, liveCandle]).slice(-maxCandlesFor(hlCoinRef.current, timeIntervalRef.current))
      last = liveCandle
    }
    candlesRef.current = arr
    if (last && !last.isHistorical) currentCandleRef.current = last
    scheduleChartUpdate()
    crumb('fut-compose', { why, hist: validHistorical.length, live: liveKept.length })

    // ─── TEMP: off-grid / adjacent-pair probe ───
    // deduplicateCandles keys purely on startTime, so anything rendering
    // side-by-side necessarily has DIFFERENT startTimes. Rather than keep
    // theorising about which producer emits them, name them: report any
    // candle whose startTime is not a multiple of the interval, and any
    // adjacent pair closer together than one interval. Their gap says which
    // producer it is — 900000 means 15m profiles reaching the store
    // unfolded, 300000 means the fold-forward, 0 would mean dedupe itself.
    try {
      if (window.__futGridProbe) {
        const offGrid = []
        const tooClose = []
        for (let i = 0; i < arr.length; i++) {
          if (arr[i].startTime % intervalMs !== 0) {
            offGrid.push({
              t: new Date(arr[i].startTime).toISOString().slice(11, 19),
              rem: arr[i].startTime % intervalMs,
              cells: !!(arr[i]._native && arr[i]._native.length),
              hist: !!arr[i].isHistorical,
            })
          }
          if (i > 0) {
            const gap = arr[i].startTime - arr[i - 1].startTime
            if (gap > 0 && gap < intervalMs) {
              tooClose.push({
                gap,
                a: new Date(arr[i - 1].startTime).toISOString().slice(11, 19),
                b: new Date(arr[i].startTime).toISOString().slice(11, 19),
                aCells: !!(arr[i - 1]._native && arr[i - 1]._native.length),
                bCells: !!(arr[i]._native && arr[i]._native.length),
              })
            }
          }
        }
        if (offGrid.length || tooClose.length) {
          dbg(`[GridProbe] tf=${tfSec}s  offGrid=${offGrid.length}  tooClose=${tooClose.length}`)
          if (offGrid.length) dbg('  offGrid:', offGrid.slice(0, 6))
          if (tooClose.length) dbg('  tooClose:', tooClose.slice(0, 6))
        } else {
          dbg(`[GridProbe] tf=${tfSec}s  clean (${arr.length} candles, all on grid)`)
        }
      }
    } catch {}
  }
  const reseamFutHistoryRef = useRef(null)  // bridge: startNewCandle predates the fn
  const recentTradesRef = useRef([])
  const currentTokenRef = useRef(null)
  // (chart2-bounce) This instance's handle in the wallet-stats owner map.
  const walletStatsInstRef = useRef(null)
  if (walletStatsInstRef.current === null) walletStatsInstRef.current = { tokRef: currentTokenRef, dead: false }
  useEffect(() => {
    const inst = walletStatsInstRef.current
    inst.dead = false
    return () => { inst.dead = true }
  }, [])
  const initialMcapRef = useRef(null)
  // Live spike gate (SpikeGate — the shared distance-scaled confirmation
  // rule; see applyTradeToState and the buffer_dump handler). Lazy-built.
  const spikeGateRef = useRef(null)
  const spikeGate = () => spikeGateRef.current || (spikeGateRef.current = new SpikeGate())
  const spikeProbeRef = useRef({ src: 0 })   // TEMP: per-token log cap for SPIKE_PROBE
  // Per-coin fine native bucket — set on first trade from pickStorageBucket(mcap).
  // All live candles for this coin store at this granularity, regardless of
  // displayBucket the user picks. Stays fixed for the coin's lifecycle so
  // every display bucket flip is a lossless coarsen.
  const nativeBucketRef = useRef(null)
  // vol-bars-20261101: incremental volume-bar builder state (see utils/volBars).
  const volStRef = useRef({})
  // vol-hist-20261101: volume bars only had the ~30min-3h raw buffer. Older
  // raw trades are pulled from the server's capture DB via the existing
  // gap_fill action (6h slabs, back to 72h), kept here apart from the time-
  // candle pipeline, and folded in by buildVolCandles.
  const volHistRef = useRef({ tok: null, t: [], s: [], a: [], m: [], from: null, end: null, pending: null, done: false })
  function pumpVolHist() {
    const h = volHistRef.current
    const mint = currentTokenRef.current
    if (hlCoinRef.current || !mint || typeof mint !== 'string') return
    if (h.tok !== mint) volHistRef.current = { tok: mint, t: [], s: [], a: [], m: [], from: null, end: null, pending: null, done: false }
    const H = volHistRef.current
    if (H.done || (H.pending && Date.now() - H.pending.at < 30000)) return
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const until = H.from ?? Math.floor(rawTradesRef.current?.oldestTime?.() ?? Infinity)
    if (!Number.isFinite(until)) return
    const floor = serverNowRef.current() - 72 * 3600 * 1000
    if (until <= floor || H.t.length > 600000) { H.done = true; return }
    const since = Math.max(Math.floor(floor), until - 6 * 3600 * 1000)
    if (H.end == null) H.end = until
    H.pending = { since, until, at: Date.now() }
    try { ws.send(JSON.stringify({ action: 'gap_fill', mint, since, until })) } catch {}
  }
  // ── SINGLE OWNER. Six call sites used to write this ref directly. ──────
  // Every one was guarded on `== null`, so FIRST WRITER WON and the other
  // five silently no-op'd: live trade, backfill-since, backfill-older,
  // restore, Tier-A restore, Tier-A stored. Which one won depended on
  // whether history, backfill or a live tick landed first — and on what
  // PRICE that particular trade happened to be at.
  //
  // That is why footprint gaps appeared on some candles and not others.
  // The contract above says the native bucket must stay fine enough that
  // every display bucket is a lossless coarsen. pickStorageBucket derives it
  // from ONE price, so whichever race won set the granularity for the coin's
  // whole lifecycle. Candles whose prices sit far from that price store on a
  // grid that does not divide evenly into the display lattice, and snapping
  // them onto display rows leaves regular gaps — consistent within a column,
  // only on some columns.
  //
  // Collapsing the writes here does not yet fix the CHOICE (still one price,
  // still first-wins). It makes the choice observable and gives it one place
  // to change once the log says which path is winning and with what price.
  const setNativeBucket = (priceHint, path) => {
    if (nativeBucketRef.current != null) return nativeBucketRef.current
    if (!(priceHint > 0)) return null
    // CME: storage = the exchange tick (0.25) — every print sits on it exactly.
    const b = cmeInfo(hlCoinRef.current)?.tick ?? pickStorageBucket(priceHint, mcapBucketSizeRef.current)
    nativeBucketRef.current = b
    dbg(`[NativeBucket] set by ${path} @ px=${priceHint} bucket=${b} display=${mcapBucketSizeRef.current}`)
    return b
  }

  // ═══ Solana history layer (/sol/profile) ═══════════════════════════════
  // Plain closures over refs (like the fine engine) so mount-time handlers —
  // ws.onmessage, the 60s top-up interval — can call them safely.
  // cov: the time spans the server has ANSWERED for this store, [lo, hi) ms,
  // merged. Inside cov a missing slot means "no trades" (the server only
  // emits slots that traded); outside it means "unknown" — the TF refold
  // must tell the two apart, or a slab that failed would fold as zero.
  const _solStoreFor = (mint, tfSec) => {
    const key = `${mint}|${tfSec}`
    if (solHistRef.current.key !== key) {
      const cur = solHistRef.current
      if (cur.key && cur.key.endsWith('|60')) solFineRef.current = cur
      const kept = solFineRef.current
      solHistRef.current = (kept && kept.key === key) ? kept : { key, map: new Map(), cov: [] }
    }
    return solHistRef.current.map
  }
  const _covAdd = (cov, lo, hi) => {
    if (!(hi > lo)) return cov
    const all = [...cov, [lo, hi]].sort((a, b) => a[0] - b[0])
    const out = []
    for (const iv of all) {
      const last = out[out.length - 1]
      if (last && iv[0] <= last[1]) last[1] = Math.max(last[1], iv[1])
      else out.push([iv[0], iv[1]])
    }
    return out
  }
  const _covHas = (cov, lo, hi) => cov.some(([a, b]) => a <= lo && hi <= b)

  // TF refold for the Solana store — the futures fold-forward's twin. Only
  // an up-switch where the old TF divides the new one, and only new slots
  // wholly inside answered coverage (so every constituent is either present
  // or truly empty). Coverage carries over shrunk to new-TF boundaries, so
  // a second switch (5m → 15m → 1h) can refold again before the server
  // answers. Down / non-divisible: nothing — a coarse cell can't be split.
  const _foldSolStoreInto = (prev, mint, tfSec) => {
    try {
      if (!prev || !prev.key || !prev.map || prev.map.size === 0) return 0
      const [pMint, pTfStr] = prev.key.split('|')
      const pTf = +pTfStr
      if (pMint !== mint || !(pTf > 0) || tfSec <= pTf || tfSec % pTf !== 0) return 0
      const tfMs = tfSec * 1000
      const store = solHistRef.current
      if (store.key !== `${mint}|${tfSec}`) return 0
      store.cov = (prev.cov || [])
        .map(([a, b]) => [Math.ceil(a / tfMs) * tfMs, Math.floor(b / tfMs) * tfMs])
        .filter(([a, b]) => b > a)
      const groups = new Map()
      for (const c of prev.map.values()) {
        const t = Math.floor(c.startTime / tfMs) * tfMs
        let g = groups.get(t)
        if (!g) { g = []; groups.set(t, g) }
        g.push(c)
      }
      let n = 0
      for (const [t, g] of groups) {
        if (!_covHas(store.cov, t, t + tfMs)) continue
        const c = foldCellCandles(g, t)
        if (!c) continue
        c._solHist = true
        store.map.set(t, c)
        n++
      }
      return n
    } catch (e) {
      console.warn('[SolHist] refold failed (server fills instead):', e?.message || e)
      return 0
    }
  }

  // The ONE assembler of candlesRef for Solana once history is in play.
  // Explicit Map merge (no filterHistoricalBySlots): store cells for every
  // closed slot, then live candles on top where they are complete —
  //   • the forming slot is always live (seam-substituted when armed);
  //   • a closed live slot at/after the raw-coverage start is complete, so
  //     it wins (it carries the client's own filters, same as the tape);
  //   • a closed live slot BEFORE it is partial (subscribe/wake slot) and
  //     yields to a store cell holding at least its volume;
  //   • empty timer candles release their slot; off-lattice (old-TF) live
  //     candles and historical candles not from this store are dropped.
  const _composeSol = (why) => {
    if (hlCoinRef.current) return
    const mint = currentTokenRef.current
    if (!mint) return
    const tfSec = timeIntervalRef.current
    if (solHistRef.current.key !== `${mint}|${tfSec}`) return
    if (candlesTfRef.current !== tfSec) return       // a TF commit is mid-flight
    const intervalMs = tfSec * 1000
    const nowSlot = Math.floor(serverNowRef.current() / intervalMs) * intervalMs
    const rawFrom = solRawFromRef.current > 0 ? solRawFromRef.current : Infinity
    const byStart = new Map()
    let nHist = 0
    for (const c of solHistRef.current.map.values()) {
      if (c.startTime < nowSlot) { byStart.set(c.startTime, c); nHist++ }
    }
    let nLive = 0
    for (const c of candlesRef.current) {
      if (!c || c.isHistorical) continue
      if (c.startTime % intervalMs !== 0 || c.startTime > nowSlot) continue
      if (c.startTime !== nowSlot) {
        const hasData = (c._liveLevels && c._liveLevels.size > 0) || (c._native && c._native.length > 0)
        if (!hasData) continue
        const h = byStart.get(c.startTime)
        if (h && c.startTime < rawFrom && candleTotalVolume(h) >= candleTotalVolume(c)) continue
      }
      byStart.set(c.startTime, c)
      nLive++
    }
    const seamC = _buildSeamCandle()
    if (seamC && !byStart.get(seamC.startTime)?.isHistorical) byStart.set(seamC.startTime, seamC)
    const arr = Array.from(byStart.values()).sort((a, b) => a.startTime - b.startTime)
    candlesRef.current = deduplicateCandles(arr).slice(-maxCandlesFor(hlCoinRef.current, timeIntervalRef.current))
    ensureLiveColumn()
    scheduleChartUpdate()
    crumb('sol-compose', { why, hist: nHist, live: nLive, seam: !!seamC })
  }

  // First-load fit: the smart bucket + snap-to-latest that used to run on the
  // full train's first chunk. Runs once per subscribed mint, as soon as the
  // recent view has a real range (history or raw — whichever lands first).
  const _solFit = (mint) => {
    if (solFitPendingRef.current !== mint || currentTokenRef.current !== mint || hlCoinRef.current) return
    const arr = candlesRef.current
    const viewCount = Math.min(arr.length, viewWindowSizeRef.current || DEFAULT_VIEW_WINDOW)
    let hiMcap = -Infinity, loMcap = Infinity
    for (const c of arr.slice(-viewCount)) {
      if (c.high_mcap != null && c.high_mcap > hiMcap) hiMcap = c.high_mcap
      if (c.low_mcap != null && c.low_mcap < loMcap) loMcap = c.low_mcap
    }
    if (!(Number.isFinite(hiMcap) && Number.isFinite(loMcap) && hiMcap > loMcap)) return
    solFitPendingRef.current = null
    const cH = bodyScrollRef.current ? bodyScrollRef.current.clientHeight : 600
    const smartBucket = pickBucketForRange(hiMcap - loMcap, cH)
    if (smartBucket !== mcapBucketSizeRef.current) {
      mcapBucketSizeRef.current = smartBucket
      setMcapBucketSize(smartBucket)
    }
    setTimeout(() => { scrollToLatest?.() }, 120)
  }

  const _solReleaseGate = (mint) => {
    if (solGateTimerRef.current) { clearTimeout(solGateTimerRef.current); solGateTimerRef.current = null }
    if (solGateHoldRef.current !== mint) return
    solGateHoldRef.current = null
    // Only release if the raw window already landed; otherwise the backfill
    // frame releases as it always has (the hold is just gone now).
    if (backfillDoneTokenRef.current === mint) markHistoryPending(false)
  }

  // Old server (no /sol/profile): the new client never asks for the full
  // train, so ask for it once now and let the legacy merge branch take it.
  // Session-sticky — later subscribes omit `since` and get the train too.
  const _solProfileMissing = (mint) => {
    if (solProfileMissingRef.current) return
    solProfileMissingRef.current = true
    console.warn('[SolHist] /sol/profile missing (server predates it) — falling back to the legacy full backfill train')
    crumb('sol-hist-legacy', {})
    if (solGateTimerRef.current) { clearTimeout(solGateTimerRef.current); solGateTimerRef.current = null }
    solGateHoldRef.current = null
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || currentTokenRef.current !== mint) {
      markHistoryPending(false)
      return
    }
    backfillActiveRef.current = true
    if (backfillCapFailsafeRef.current) clearTimeout(backfillCapFailsafeRef.current)
    backfillCapFailsafeRef.current = setTimeout(() => {
      backfillCapFailsafeRef.current = null
      backfillActiveRef.current = false
    }, 180000)
    markHistoryPending(false)
    try {
      ws.send(JSON.stringify({ action: 'subscribe', token: mint, wallet: publicKeyRef.current || null, bin: 1, cl: performance.now() }))
    } catch {}
  }

  // Loader: newest window (with the seam tip) → compose → older slabs back
  // to min(72h, MAX_CANDLES × tf), each landing through the store + compose.
  // topUp: just a short newest window (60s timer) — upgrades slots the
  // capture flushed late and keeps the seam current. Store precedence
  // (richer-or-equal replaces) makes every pass idempotent.
  const _loadSolHistory = async (why, { topUp = false, sinceMs = 0 } = {}) => {
    if (!SOL_HISTORY_ENABLED || hlCoinRef.current) return
    if (solProfileMissingRef.current) { if (!topUp) console.warn('[SolHist] skipped: /sol/profile marked missing this session (reload to retry)'); return }
    const mint = currentTokenRef.current
    const tfSec = timeIntervalRef.current
    if (!mint || !(tfSec > 0)) return
    const key = `${mint}|${tfSec}`
    if (topUp) {
      if ((solTopUpBusyRef.current && !sinceMs) || solHistRef.current.key !== key) return
      solTopUpBusyRef.current = true
    }
    const seq = topUp ? solHistSeqRef.current : ++solHistSeqRef.current
    _solStoreFor(mint, tfSec)
    const stale = () => !!hlCoinRef.current || currentTokenRef.current !== mint
      || timeIntervalRef.current !== tfSec || solHistSeqRef.current !== seq
      || solHistRef.current.key !== key
    const ceilTo = (sec) => Math.ceil(sec / tfSec) * tfSec
    const endSec = Math.floor(serverNowRef.current() / 1000 / tfSec) * tfSec
    const depthSec = Math.min(ceilTo(SOL_HIST_MAX_SEC), maxCandlesFor(null, tfSec) * tfSec)
    // Top-up window: the newest closed slots + the open slot's tip — ≥10min,
    // ~8 slots on small TFs, one-two slots on big ones (cheap every 60s).
    const topUpSec = ceilTo(Math.max(600, Math.min(tfSec * 8, Math.max(tfSec, 2 * 3600))))
    // sinceMs (TF switch off the kept 1m store): top up from where it ends.
    const shallowSec = Math.min(depthSec, topUp ? Math.max(topUpSec, sinceMs ? ceilTo(endSec - Math.floor(sinceMs / 1000)) : 0) : ceilTo(SOL_HIST_SHALLOW_SEC))
    const slabSec = ceilTo(SOL_HIST_SLAB_SEC)
    // Raw coverage start = the exact seam point. Trades at/after it are in
    // the buffer (the raw window or live); the server serves the open slot
    // before it as the tip.
    const cutMs = () => {
      const o = rawTradesRef.current.oldestTime()
      return Math.max(solRawFromRef.current || 0, Number.isFinite(o) ? o : 0)
    }
    const fetchWin = async (fromSec, toSec, newest, tmo = 0) => {
      const args = {
        mint, proxyBase: HTTP_PROXY_BASE, intervalSec: tfSec, fromSec, toSec,
        bucket: nativeBucketRef.current || 0, cutMs: newest ? cutMs() : 0,
        timeoutMs: tmo || (topUp ? (sinceMs ? 20000 : 6000) : (newest ? 20000 : 15000)),
      }
      const r = await fetchSolProfiles(args)
      if (r.status === 404) { _solProfileMissing(mint); return null }
      if (r.status !== 200 && !stale() && !topUp) console.warn(`[SolHist] window ${((endSec - toSec) / 3600).toFixed(1)}-${((endSec - fromSec) / 3600).toFixed(1)}h: ${r.status === -1 ? 'timeout/network' : 'HTTP ' + r.status}`)
      if (r.status === 200) _setSolFastFrom(r.fastFrom)   // 0 = server has no fast path (yet)
      if (r.status !== 200 || stale()) return null
      if (nativeBucketRef.current == null && r.ref > 0) setNativeBucket(r.ref, 'sol-profile')
      // Cells are keyed at the bucket the server ACTUALLY used (it echoes
      // it). Normally that is our native (we sent it); if not, the cells
      // still carry their true step and buildDisplay groups them correctly.
      const nb = r.bucket
      if (!(nb > 0)) return null
      return { r, nb, fromSec, toSec }
    }
    // Server cells replace a locally refolded (_folded) cell outright — the
    // refold was the instant stand-in, this is the reconcile.
    const land = ({ r, nb, fromSec, toSec }, newest) => {
      const store = solHistRef.current
      const map = store.map
      store.cov = _covAdd(store.cov || [], fromSec * 1000, toSec * 1000)
      const cells = profilesToCellCandles(r, { nativeBucket: nb, ohlcByTs: r.ohlcByTs })
      for (const c of cells) {
        c._solHist = true
        const prev = map.get(c.startTime)
        if (!prev || prev._folded || candleTotalVolume(c) >= candleTotalVolume(prev)) map.set(c.startTime, c)
      }
      if (newest) {
        const tips = r.tips.length ? profilesToCellCandles(r.tips, { nativeBucket: nb, ohlcByTs: r.tipOhlc }) : []
        solSeamRef.current = (tips.length && Number.isFinite(r.cutoffMs))
          ? { key, cutoffMs: r.cutoffMs, slotStart: tips[0].startTime, tips }
          : null
      }
      return cells.length
    }
    let landed = 0
    // Fast (bars-backed) windows need TFs in whole 15s (prof15) steps.
    const fastOK = () => SOL_FAST_CHUNKS && tfSec % 15 === 0 && _solFastFromMs > 0
    try {
      let first = null
      let firstSec = shallowSec
      // Fast newest window: 12h in one short, single try when the bars
      // already cover it. On failure, the old schedule below runs as before.
      if (!topUp && fastOK()) {
        const fs = Math.min(depthSec, ceilTo(SOL_FAST_FIRST_SEC))
        if (_solFastFromMs <= (endSec - fs) * 1000) {
          first = await fetchWin(endSec - fs, endSec, true, SOL_FAST_TIMEOUT_MS)
          if (first) firstSec = fs
          else if (!stale() && !solProfileMissingRef.current) console.warn('[SolHist] fast newest window failed - old schedule')
        }
      }
      // Newest window: same 3-try retry as the slabs (topUp stays one-shot).
      for (let attempt = 0; attempt < (topUp ? 1 : 3) && !first; attempt++) {
        if (attempt) await new Promise(res => setTimeout(res, 1500 * attempt))
        if (stale() || solProfileMissingRef.current) break
        first = await fetchWin(endSec - shallowSec, endSec, true, topUp ? 0 : 20000 + attempt * 15000)
        if (!first && !topUp && !stale() && !solProfileMissingRef.current) console.warn(`[SolHist] newest window failed (try ${attempt + 1}/3)`)
      }
      if (first && !stale()) { landed += land(first, true); _composeSol(`sol-${why}`) }
      if (!topUp && currentTokenRef.current === mint) {
        solShallowDoneRef.current = mint
        _solFit(mint)
        _solReleaseGate(mint)
      }
      // A failed newest window no longer blocks the older slabs.
      if (topUp || stale() || solProfileMissingRef.current) return
      let fastSlabs = true
      for (let off = firstSec; off < depthSec;) {
        const toS = endSec - off
        // Fast piece: up to 24h, never reaching below fastFrom (first fully
        // covered slot). Otherwise the old 6h slab.
        const ffSec = Math.ceil(_solFastFromMs / 1000 / tfSec) * tfSec
        let fast = fastSlabs && fastOK() && toS > ffSec
        let fromS = fast
          ? Math.max(endSec - depthSec, toS - ceilTo(SOL_FAST_SLAB_SEC), ffSec)
          : Math.max(endSec - depthSec, toS - slabSec)
        if (fromS >= toS) { fast = false; fromS = Math.max(endSec - depthSec, toS - slabSec) }
        // Retry a failed/timed-out slab (was silently dropped -> stuck at 6h).
        let w = null
        const tries = fast ? 1 : 3
        for (let attempt = 0; attempt < tries && !w; attempt++) {
          if (attempt) await new Promise(res => setTimeout(res, 1500 * attempt))
          if (stale()) return
          w = await fetchWin(fromS, toS, false, fast ? SOL_FAST_TIMEOUT_MS : 25000 + attempt * 20000) // 25/45/65s; server dedupes retries
          if (!w && !stale()) {
            console.warn(`[SolHist] ${fast ? 'fast ' : ''}slab ${((endSec - toS) / 3600).toFixed(1)}-${((endSec - fromS) / 3600).toFixed(1)}h failed (try ${attempt + 1}/${tries})`)
            crumb('sol-slab-fail', { try: attempt + 1, fromS, toS, fast })
          }
        }
        if (stale()) return
        // A failed fast piece: redo the same span the old way (6h, 3 tries).
        if (!w && fast) { fastSlabs = false; continue }
        if (w && land(w, false)) { landed++; _composeSol('sol-slab') }
        off = endSec - fromS
      }
      dbg(`[SolHist] ${why}: ${solHistRef.current.map.size} slots @ ${tfSec}s (depth ${(depthSec / 3600).toFixed(1)}h)`)
    } catch (e) {
      console.warn('[SolHist] load failed:', e?.message || e)
      if (!topUp) { solShallowDoneRef.current = mint; _solReleaseGate(mint) }
    } finally {
      if (topUp) solTopUpBusyRef.current = false
    }
  }

  // Batched trade processing
  const pendingTradesRef = useRef([])
  const frameScheduledRef = useRef(false)
  const pendingChartUpdateRef = useRef(false)
  // (perf5) React commits held while a chart's Y glide walks (dataCommitGate).
  // stats deltas accumulate here; tape / chart are dirty flags (their data
  // already lives in recentTradesRef / candlesRef). heldSince = first deferral.
  const heldCommitRef = useRef({ stats: false, tape: false, chart: false, buy: 0, sell: 0, count: 0, lastMcap: null, heldSince: 0, poll: 0 })
  const commitLiveDataRef = useRef(null)

  // WebSocket reconnect / heartbeat
  const reconnectAttemptsRef = useRef(0)
  const heartbeatTimerRef = useRef(null)
  const reconnectTimerRef = useRef(null)  // pending backoff timer — 'online' preempts it
  const manualCloseRef = useRef(false)

  // Sleep/wake detection
  const sleepMonitorRef = useRef(null)
  const lastTickRef = useRef(0)
  const isSleepingRef = useRef(false)
  // Transport liveness: stamped on EVERY inbound ws message. The client
  // pings every 30s and the server pongs, so a live pipe delivers SOMETHING
  // at least twice a minute even when the market is stone quiet — which
  // makes message age the correct deadness signal (see the watchdog).
  const lastWsMsgAtRef = useRef(Date.now())
  const hiddenAtRef = useRef(0)          // when the tab went hidden (wake decides trust by duration)
  // Bridge: connectWS lives inside the WS effect's closure; the sleep/wake
  // effect below is a SEPARATE effect and cannot see it. The WS effect
  // publishes a bounce() here.
  const wsControlRef = useRef({})

  // Keep bucket/interval refs for imperative access inside callbacks
  const mcapBucketSizeRef = useRef(mcapBucketSize)
  const timeIntervalRef = useRef(timeInterval)
  const tradeFilterMinRef = useRef(0)
  const hlCoinRef = useRef(null)  // set when in futures mode

  useEffect(() => { mcapBucketSizeRef.current = mcapBucketSize }, [mcapBucketSize])
  useEffect(() => { timeIntervalRef.current = timeInterval }, [timeInterval])
  useEffect(() => { currentTokenRef.current = currentToken }, [currentToken])

  // Own-buy callback ref — set externally by FootprintChartCanvas once trading hook exists
  const onOwnBuyDetectedRef = useRef(onOwnBuyDetected)
  useEffect(() => { onOwnBuyDetectedRef.current = onOwnBuyDetected }, [onOwnBuyDetected])

  // SOL/USD price — fetch once and refresh every 60s
  const solPriceRef = useRef(150)
  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const res = await fetch('https://api.kraken.com/0/public/Ticker?pair=SOLUSD')
        const data = await res.json()
        const price = parseFloat(data.result?.SOLUSD?.c?.[0])
        if (price > 0) solPriceRef.current = price
      } catch {}
    }
    fetchSolPrice()
    const interval = setInterval(fetchSolPrice, 60000)
    return () => clearInterval(interval)
  }, [])

  // Incremental aggregate cache (min/max mcap + max cell volume) + a structural
  // signature (array identity, length, first/last startTime). updateChartData
  // rescans in full only when the signature changes; otherwise it folds just the
  // trailing candles into these cached values.
  const aggCacheRef = useRef({ arr: null, len: -1, first: null, last: null, min: Infinity, max: -Infinity, maxVol: 0 })
  // Monotonic counter bumped ONLY on a structural candle-set change (close /
  // trim / rebuild / switch — same signature as the incremental-aggregate pass
  // below). Published so COLD memos that derive from CLOSED-candle history
  // (swings, and anything else immutable between closes) can key on it instead
  // of the per-tick-changing candles reference — recomputing once per close
  // rather than on every publish, without touching the hot draw path.
  const structuralVersionRef = useRef(0)
  // Interval candlesRef is built at. Set where a TF change commits its new
  // candles (and on reset). Published as chartData.tf so the chart can tell
  // the new-interval series from old-interval ticks during an async fold.
  const candlesTfRef = useRef(timeInterval)
  // Bumped when a new live candle is created. Published as chartData.fitGen
  // so the chart refits Y on that column, not on every tick.
  const fitGenRef = useRef(0)

  // --- Update chart data (React state from mutable refs) ---
  const updateChartData = useCallback(() => {
    // ── Structural vs incremental ──
    // Closed candles are immutable; a normal tick only mutates the live candle
    // (or, rarely, a very recent one a late trade lands in) IN PLACE, leaving the
    // array identity, length, and first/last startTime untouched. So we pay
    // O(all candles) only on a STRUCTURAL change — a candle close, a trim, a
    // rebuild/backfill, or a token switch. This is what stops per-tick cost
    // scaling with candle count (i.e. with timeframe): 5m now costs what 30m does.
    // vol-bars-20261101: chart type 'vol' swaps the time candles for bars
    // that each close after N SOL, built from the raw trade buffer.
    let _volArr = null
    {
      const _vb = getVolBars(chartIdRef.current)
      const _b = nativeBucketRef.current ?? mcapBucketSizeRef.current
      if (_vb.type === 'vol' && _b && rawTradesRef.current) {
        const st = volStRef.current
        const tok = (hlCoinRef.current || '') + '|' + (currentTokenRef.current?.mint || currentTokenRef.current || '')
        if (st.tok !== tok) { volStRef.current = { tok }; }
        const st2 = volStRef.current
        // cmevol-20261102: CME counts contracts, with its own per-root size.
        const _cmeV = cmeInfo(hlCoinRef.current) ? String(hlCoinRef.current).toUpperCase() : null
        let sz = _cmeV ? ((_vb.cmeSize && _vb.cmeSize[_cmeV]) || 0) : _vb.size
        if (!(sz > 0)) {
          if (_cmeV) sz = CME_VOL_DEFAULT[_cmeV] || 500
          else {
          if (!st2.auto && rawTradesRef.current.length > 20) st2.auto = autoVolSize(rawTradesRef.current, tokenSupplyRef.current || 0)
          sz = st2.auto || 5
          }
        }
        const prevLen = st2.arr ? st2.arr.length : 0
        const _vh = volHistRef.current
        const _ch = cmeVolHistRef.current
        _volArr = buildVolCandles(st2, rawTradesRef.current, sz, _b, _cmeV ? -1 : (hlCoinRef.current ? 0 : (tokenSupplyRef.current || 0)),
          _cmeV ? (_ch.tok === _cmeV ? _ch : null) : ((!hlCoinRef.current && _vh.tok === currentTokenRef.current) ? _vh : null),
          (_cmeV && _ch.tok === _cmeV) ? (_ch.seam || 0) : 0)
        if (_cmeV) pumpCmeVolHist(_cmeV); else pumpVolHist()
        if (_volArr.length !== prevLen) fitGenRef.current++
        if (typeof window !== 'undefined') window.__cheddarVolBar = { size: sz, auto: !(_vb.size > 0), bars: _volArr.length }
      }
    }
    const arr0 = _volArr || candlesRef.current
    const lastC0 = arr0[arr0.length - 1]
    const _cache = aggCacheRef.current
    const structuralAgg =
      arr0 !== _cache.arr ||
      arr0.length !== _cache.len ||
      (arr0[0]?.startTime ?? null) !== _cache.first ||
      (lastC0?.startTime ?? null) !== _cache.last

    if (structuralAgg) {
      // A structural change is exactly when closed-candle-derived indicators
      // need to recompute — bump the published version so their memos fire.
      structuralVersionRef.current++
      if (!_volArr) {
      // Duplicate candles only appear when a candle is (re)created — i.e.
      // structurally — so the whole-array dedupe + pointer repair only run here.
      candlesRef.current = deduplicateCandles(candlesRef.current)
      // Pointer repair: the dedupe can merge two same-startTime candles and keep
      // the OTHER object, leaving currentCandleRef holding a candle no longer in
      // the array. Re-point at the surviving object.
      const cur = currentCandleRef.current
      if (cur) {
        const arr = candlesRef.current
        const last = arr[arr.length - 1]
        if (last !== cur) {
          const survivor = arr.find(c => c.startTime === cur.startTime && !c.isHistorical)
          currentCandleRef.current = survivor
            || (last && !last.isHistorical ? last : currentCandleRef.current)
        }
      }
      }
    }
    const candles = _volArr || candlesRef.current
    const bucketSize = mcapBucketSizeRef.current

    if (candles.length === 0) {
      aggCacheRef.current = { arr: candles, len: 0, first: null, last: null, min: Infinity, max: -Infinity, maxVol: 0 }
      setChartData({ sortedMcaps: [], candles: [], maxVolume: 0, highlightMcap: 0, tradeCount: 0, structuralVersion: structuralVersionRef.current, tf: candlesTfRef.current, fitGen: fitGenRef.current })
      return
    }

    // min/max mcap (y-grid) + max cell volume, in ONE pass.
    //   STRUCTURAL  → full rescan (infrequent: close/trim/rebuild/switch).
    //   INCREMENTAL → start from the cached aggregate (closed candles are
    //     unchanged) and fold only the last AGG_RECENCY_CANDLES. Extremes are
    //     monotonic within a fixed candle set (levels are only added, never
    //     removed), so re-folding the recent tail can only keep or widen them —
    //     bit-identical to a full rescan, at O(1) in total candle count.
    let minMcap, maxMcap, maxVolume
    if (structuralAgg) {
      minMcap = Infinity; maxMcap = -Infinity; maxVolume = 0
      for (const candle of candles) {
        if (!candle.isHistorical) {
          forEachLevel(candle, (mcap) => {
            if (mcap < minMcap) minMcap = mcap
            if (mcap > maxMcap) maxMcap = mcap
          })
        } else if (candle._solHist) {
          // Solana history cells: the range the old all-live 26h history
          // gave the axis. OHLC extremes already envelope every level.
          if (candle.low_mcap < minMcap) minMcap = candle.low_mcap
          if (candle.high_mcap > maxMcap) maxMcap = candle.high_mcap
        }
        const mv = candleMaxLevelVolume(candle)
        if (mv > maxVolume) maxVolume = mv
      }
    } else {
      minMcap = _cache.min; maxMcap = _cache.max; maxVolume = _cache.maxVol
      for (let i = Math.max(0, candles.length - AGG_RECENCY_CANDLES); i < candles.length; i++) {
        const candle = candles[i]
        if (!candle.isHistorical) {
          forEachLevel(candle, (mcap) => {
            if (mcap < minMcap) minMcap = mcap
            if (mcap > maxMcap) maxMcap = mcap
          })
        } else if (candle._solHist) {
          if (candle.low_mcap < minMcap) minMcap = candle.low_mcap
          if (candle.high_mcap > maxMcap) maxMcap = candle.high_mcap
        }
        const mv = candleMaxLevelVolume(candle)
        if (mv > maxVolume) maxVolume = mv
      }
    }
    // Publish the fresh aggregate to the cache for the next tick (structural
    // signature + running extremes). Stored PRE-fallback, so it reflects the
    // true computed values even when the display falls back to lastBucketMcap.
    aggCacheRef.current = {
      arr: candles,
      len: candles.length,
      first: candles[0]?.startTime ?? null,
      last: candles[candles.length - 1]?.startTime ?? null,
      min: minMcap, max: maxMcap, maxVol: maxVolume,
    }

    // ── R1: HISTORICAL FALLBACK FOR THE Y-RANGE ──
    // The scans above read levels from NON-historical candles only, so a chart
    // whose live candles carry no levels yet leaves both extremes infinite.
    // The lastBucketMcap fallback below rescues that only when a previous
    // bucket exists; otherwise the publish became
    //   setChartData({ sortedMcaps: [], candles: [], ... })
    // — an EMPTY chart, while candlesRef.current was fully populated. Every
    // FPCC draw entry guards on `mcaps?.length >= 2`, so nothing painted at
    // all. That is the "data loads but doesn't display" report: the trades
    // were in the buffer, the candles were in the ref, and the publish threw
    // both away.
    //
    // Two ways in, both real. A fresh backfill that lands as OHLC klines
    // before any live trade. And _omFootprint futures history, which is
    // isHistorical: true AND carries levels — so it is skipped by the loops
    // above even though it has everything needed to scale an axis.
    //
    // Historical candles always carry high_mcap/low_mcap (see the kline
    // builder), so use those. Runs ONLY in the degenerate case, and only until
    // the first live level arrives, so the steady-state cost is one finite
    // check. Cheap per candle too — OHLC fields, no forEachLevel walk.
    if (!Number.isFinite(minMcap) || !Number.isFinite(maxMcap)) {
      for (const candle of candles) {
        const hi = candle.high_mcap ?? candle.close_mcap ?? candle.open_mcap
        const lo = candle.low_mcap  ?? candle.close_mcap ?? candle.open_mcap
        if (Number.isFinite(lo) && lo < minMcap) minMcap = lo
        if (Number.isFinite(hi) && hi > maxMcap) maxMcap = hi
      }
      // Do NOT write this into aggCacheRef: the cache is the LEVEL-derived
      // aggregate and the incremental branch widens it from there. Seeding it
      // with an OHLC range would make a later live level unable to narrow it,
      // pinning the axis to whatever history happened to span.
    }

    if (!Number.isFinite(minMcap) && lastBucketMcapRef.current > 0) {
      minMcap = lastBucketMcapRef.current
      maxMcap = lastBucketMcapRef.current
    }

    if (!Number.isFinite(minMcap) || !Number.isFinite(maxMcap)) {
      setChartData({ sortedMcaps: [], candles: [], maxVolume: 0, highlightMcap: 0, tradeCount: 0, structuralVersion: structuralVersionRef.current, tf: candlesTfRef.current, fitGen: fitGenRef.current })
      return
    }

    const buffer = 30 * bucketSize
    const preloadMax = snapBucket(maxMcap + buffer, bucketSize)
    const preloadMin = Math.max(0, snapBucket(minMcap - buffer, bucketSize))

    // ── Rebucket freeze guard (does NOT change the process; just bails earlier) ──
    // sortedMcaps is built purely from range/bucketSize and capped at 5000. On a
    // rebucket to a small bucketSize over a wide min→max range, the true bucket
    // count can be enormous; the cap stops the array growing, but only AFTER the
    // loop has spun the full 5000 iterations INSIDE a React commit — a synchronous
    // main-thread stall, which on phone reads as a lockup.
    //
    // Two changes, both guardrails:
    //   1. Lower the ceiling on phone. 5000 rows was never renderable there (the
    //      visible RH-driven grid caps itself to the container anyway); it only
    //      ever meant "grind 5000 iterations, then draw a broken grid". A tighter
    //      cap can't lose anything the phone could actually show.
    //   2. Compute the count UP FRONT and bail before the loop when it would blow
    //      the cap, instead of discovering it 5000 iterations in. Same output for
    //      the in-range case; just no wasted spin for the pathological one.
    const ROW_CAP = isPhoneDevice() ? 1500 : 5000
    const projectedRows = Math.floor((preloadMax - preloadMin) / bucketSize) + 1

    const sortedMcaps = []
    if (bucketSize >= 1) {
      // Integer buckets — original loop
      for (let i = 0; sortedMcaps.length <= ROW_CAP; i++) {
        const m = snapBucket(preloadMax - i * bucketSize, bucketSize)
        if (m < preloadMin) break
        sortedMcaps.push(m)
      }
    } else {
      // Fractional buckets — integer step counting to avoid FP drift
      const precision = Math.max(0, Math.ceil(-Math.log10(bucketSize)))
      const factor = Math.pow(10, precision)
      const scaledBucket = Math.round(bucketSize * factor)
      const topStep = Math.round(preloadMax * factor / scaledBucket)
      // When the projected range blows the cap, keep the TOP rows — the same
      // side the integer branch keeps, and the same side the pre-guard code kept
      // (it filled downward from preloadMax and truncated). The first guard
      // version clamped the START to botStep+ROW_CAP, which kept the BOTTOM —
      // silently a different lattice than every other path builds. Either side
      // loses something in the pathological case; the point is to lose the SAME
      // thing the rest of the pipeline already expects. Sane ranges: no-op.
      const botStep = Math.round(preloadMin * factor / scaledBucket)
      const endStep = projectedRows > ROW_CAP ? Math.max(botStep, topStep - ROW_CAP) : botStep
      for (let step = topStep; step >= endStep && sortedMcaps.length <= ROW_CAP; step--) {
        sortedMcaps.push((step * scaledBucket) / factor)
      }
    }

    setChartData({
      sortedMcaps,
      candles: [...candles],
      maxVolume,
      highlightMcap: lastBucketMcapRef.current,
      tradeCount: rawTradesRef.current.length,
      structuralVersion: structuralVersionRef.current,
      tf: candlesTfRef.current,
      fitGen: fitGenRef.current,
    })
  }, [])

  // (perf5) THE live commit. One batch — stats + tape + chartData — so a
  // trade frame is ONE App / FootprintChart render. (It was two: the drain
  // committed stats + tape, then scheduleChartUpdate's own rAF committed
  // chartData on the NEXT frame — two full renders per frame on a busy futures
  // tape.) While a chart's Y glide holds commits (dataCommitGate) this only
  // marks dirty and polls once per frame; the batch flushes the frame the
  // glide lands, or after DATA_HOLD_MAX_MS of continuous holding.
  const commitLiveData = useCallback(() => {
    const h = heldCommitRef.current
    if (!(h.stats || h.tape || h.chart)) return
    if (dataCommitsHeld(chartIdRef.current)) {
      const now = performance.now()
      if (!h.heldSince) h.heldSince = now
      if (now - h.heldSince < DATA_HOLD_MAX_MS) {
        if (!h.poll) {
          h.poll = requestAnimationFrame(() => {
            h.poll = 0
            if (heldCommitRef.current === h) commitLiveDataRef.current?.()
          })
        }
        return
      }
    }
    const { stats, tape, chart, buy, sell, count, lastMcap } = h
    h.stats = false; h.tape = false; h.chart = false
    h.buy = 0; h.sell = 0; h.count = 0; h.lastMcap = null
    h.heldSince = 0
    if (stats) {
      setStats(prev => {
        const buyVolume = prev.buyVolume + buy
        const sellVolume = prev.sellVolume + sell
        return {
          buyVolume, sellVolume,
          delta: buyVolume - sellVolume,
          tradeCount: prev.tradeCount + count,
          marketCap: lastMcap ?? prev.marketCap,
        }
      })
    }
    if (tape) setRecentTrades([...recentTradesRef.current])
    if (chart) {
      // A scheduleChartUpdate rAF still pending sees the flag down and skips.
      pendingChartUpdateRef.current = false
      updateChartData()
    }
  }, [updateChartData])
  commitLiveDataRef.current = commitLiveData

  const scheduleChartUpdate = useCallback(() => {
    if (pendingChartUpdateRef.current) return
    pendingChartUpdateRef.current = true
    requestAnimationFrame(() => {
      // (perf5) Already committed this frame by commitLiveData.
      if (!pendingChartUpdateRef.current) return
      pendingChartUpdateRef.current = false
      if (dataCommitsHeld(chartIdRef.current)) {
        heldCommitRef.current.chart = true
        commitLiveData()
        return
      }
      updateChartData()
    })
  }, [updateChartData, commitLiveData])

  // cmevol-20261102: CME volume-bar history. The server has no raw CME trade
  // route, so older flow comes from /fut/profile at 15s slots ([price, buy,
  // sell] contracts per level), unrolled into synthetic prints spread across
  // each slot. 2h slabs, newest first, back to CME_VOLHIST_HOURS (server keeps
  // ~10-12h of CME). Kept apart from the time-candle pipeline.
  const CME_VOLHIST_HOURS = 12
  const cmeVolHistRef = useRef({ tok: null, t: [], s: [], a: [], m: [], from: null, end: null, seam: 0, gen: 0, pending: false, done: false, failAt: 0 })
  // cmevol2-20261102: unroll one /fut/profile response into prints in [from, to).
  function cmeProfToPrints(j, from, to) {
    const pts = (j?.series?.[0]?.points || []).map(p => p.Point || p.point || p)
      .map(P => ({ ts: P?.timestamp?.s ?? P?.timestamp, prof: P?.profile }))
      .filter(x => Number.isFinite(x.ts) && x.ts >= from && x.ts < to && Array.isArray(x.prof))
      .sort((x, y) => x.ts - y.ts)
    const t = [], s = [], a = [], m = []
    for (const { ts, prof } of pts) {
      const lv = []
      for (let k = 0; k + 2 < prof.length; k += 3) {
        const px = +prof[k], bu = +prof[k + 1], se = +prof[k + 2]
        if (!(px > 0)) continue
        if (bu > 0) lv.push(['buy', bu, px])
        if (se > 0) lv.push(['sell', se, px])
      }
      const step = 15000 / (lv.length + 1)
      lv.forEach((x, i) => { t.push(ts * 1000 + Math.floor((i + 1) * step)); s.push(x[0]); a.push(x[1]); m.push(x[2]) })
    }
    return { t, s, a, m, slots: pts.length }
  }
  // cmevol2-20261102: the server's capture flushes its newest minutes with a
  // lag, so the first slab's tail (right at the seam) can come back short and
  // was never asked again - the forming bar had a hole there. Re-ask the last
  // 15 min before the seam a few times and splice it in place.
  function refreshCmeSeam(H, root) {
    if (cmeVolHistRef.current !== H || !H.seam) return
    const to = H.seam / 1000, from = to - 900
    fetch(`${HTTP_PROXY_BASE}/fut/profile?coin=${encodeURIComponent(root)}&interval=15&from=${from}&period=900`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
      .then(j => {
        if (cmeVolHistRef.current !== H) return
        const p = cmeProfToPrints(j, from, to)
        const lo = from * 1000, hi = H.seam
        let i0 = 0; while (i0 < H.t.length && H.t[i0] < lo) i0++
        let i1 = i0; while (i1 < H.t.length && H.t[i1] < hi) i1++
        let oldV = 0; for (let i = i0; i < i1; i++) oldV += H.a[i]
        let newV = 0; for (const v of p.a) newV += v
        if (newV <= oldV) return   // nothing new flushed
        H.t.splice(i0, i1 - i0, ...p.t); H.s.splice(i0, i1 - i0, ...p.s); H.a.splice(i0, i1 - i0, ...p.a); H.m.splice(i0, i1 - i0, ...p.m)
        H.gen++
        dbg(`[CmeVolHist] ${root} seam refresh +${newV - oldV} contracts`)
        scheduleChartUpdate()
      })
      .catch(() => {})
  }
  function pumpCmeVolHist(root) {
    if (cmeVolHistRef.current.tok !== root) cmeVolHistRef.current = { tok: root, t: [], s: [], a: [], m: [], from: null, end: null, seam: 0, gen: 0, pending: false, done: false, failAt: 0 }
    const H = cmeVolHistRef.current
    if (H.done || H.pending || (H.failAt && Date.now() - H.failAt < 30000)) return
    if (H.from == null) {
      const buf = rawTradesRef.current
      const o = buf && buf.length ? buf.timeAt(0) : 0
      if (!(o > 0)) return
      // cmevol2-20261102: exact seam = end of the 15s slot holding the buffer's
      // oldest print. History owns everything before it (that whole slot
      // included), the buffer everything from it on (buildVolCandles bufFrom).
      H.seam = Math.floor(o / 15000) * 15000 + 15000
      H.from = H.seam / 1000
      H.end = H.seam
      for (const d of [20000, 75000, 180000]) setTimeout(() => refreshCmeSeam(H, root), d)
    }
    const floor = Math.floor((serverNowRef.current() - CME_VOLHIST_HOURS * 3600 * 1000) / 1000)
    if (H.from <= floor || H.t.length > 600000) { H.done = true; return }
    const to = H.from, from = Math.max(floor, to - 7200)
    H.pending = true
    fetch(`${HTTP_PROXY_BASE}/fut/profile?coin=${encodeURIComponent(root)}&interval=15&from=${from}&period=${to - from}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
      .then(j => {
        if (cmeVolHistRef.current !== H) return
        const { t, s, a, m, slots } = cmeProfToPrints(j, from, to)
        const pts = { length: slots }
        H.t = t.concat(H.t); H.s = s.concat(H.s); H.a = a.concat(H.a); H.m = m.concat(H.m)
        H.from = from; H.pending = false
        dbg(`[CmeVolHist] ${root} +${t.length} prints (${pts.length} slots), total ${H.t.length}`)
        scheduleChartUpdate()
      })
      .catch(e => { H.pending = false; H.failAt = Date.now(); console.warn('[CmeVolHist] slab failed:', e?.message || e) })
  }
  // vol-bars-20261101: chart type / bar size changed for this chart -> rebuild.
  useEffect(() => onVolBarsChange((id) => {
    if (id !== chartIdRef.current) return
    volStRef.current = {}
    aggCacheRef.current = { arr: null, len: -1, first: null, last: null, min: Infinity, max: -Infinity, maxVol: 0 }
    fitGenRef.current++
    scheduleChartUpdate()
    setTimeout(() => { scrollToLatest?.() }, 150)
  }), [scheduleChartUpdate, scrollToLatest])

  // --- Start new candle ---
  const startNewCandle = useCallback(() => {
    // Capture the candle that's about to transition from "live" to
    // "closed" so we can persist it. The PREVIOUS currentCandleRef is
    // still in candlesRef.current[length-1] — replacing the ref below
    // doesn't unset that array slot.
    const intervalMs = timeIntervalRef.current * 1000
    const alignedStart = Math.floor(serverNowRef.current() / intervalMs) * intervalMs

    fitGenRef.current++   // new footprint column → chart refits Y

    currentCandleRef.current = newLiveCandle(alignedStart, nativeBucketRef.current ?? mcapBucketSizeRef.current)
    candlesRef.current.push(currentCandleRef.current)

    // Trim live candles only
    const liveCandles = candlesRef.current.filter(c => !c.isHistorical)
    if (liveCandles.length > MAX_CANDLES) {
      const historical = candlesRef.current.filter(c => c.isHistorical)
      candlesRef.current = [...historical, ...liveCandles.slice(-maxCandlesFor(hlCoinRef.current, timeIntervalRef.current))]
    }

    // Trim raw trade buffer. Cutoff = the EARLIER of (oldest non-historical
    // candle's start) and (now - 60min) — i.e. keep whichever retains more.
    // The candle-anchored cutoff alone death-spirals across TF flips: a
    // flip shrinks the live region to whatever the buffer could rebuild,
    // the next close prunes the buffer down to THAT, and each flip eats
    // more history until one candle is left. The 60-min floor guarantees
    // rebuild material survives regardless of the live region's state.
    const liveCandlesForPrune = candlesRef.current.filter(c => !c.isHistorical)
    const floorCutoff = serverNowRef.current() - MAX_TRADES_WINDOW_MS
    const oldestLiveStart = liveCandlesForPrune.length > 0
      ? liveCandlesForPrune[0].startTime
      : floorCutoff
    let cutoff = Math.min(oldestLiveStart, floorCutoff)
    // Futures retention cap. min() alone keeps trades back to the oldest
    // live candle — 500 candles at 1m ≈ 8+ hours of THREE merged streams
    // (HL + Bitunix + Coinbase) on a busy pair. The buffer grew into the
    // hundreds of thousands: every candle close re-filtered and re-queued
    // the whole array for persistence, and the heap/GC load built into the
    // session lag that a refresh cleared. 3h keeps deep TF-rebuild material
    // (180 footprint candles at 1m; klines cover older) with a bounded
    // buffer. Solana gets the SAME 3h cap (below) — it too grows unbounded
    // when one token is held without rotation — but gated on backfillActiveRef
    // so the fine-series seed lands first.
    if (hlCoinRef.current) {
      cutoff = Math.max(cutoff, serverNowRef.current() - FUTURES_TRADE_RETENTION_MS)
    } else {
      // Solana cap — held off while a backfill is in flight so the fine-series
      // re-seed at finalChunk gets the full captured depth; once backfill
      // completes it bounds the buffer and the session-long growth stops. TF
      // switches fold from the 26h fine series, not this buffer (see the
      // SOLANA_TRADE_RETENTION_MS note), so the cap costs no display depth.
      // Defense-in-depth: even mid-backfill an ABSOLUTE ceiling applies —
      // the fine-retention window, which no backfill can exceed (server
      // sends ≤24h) — so a wedged-open flag can bound the buffer at 26h
      // worst case, never "grows for the whole session".
      const ceilMs = backfillActiveRef.current
        ? FINE_RETENTION_SOL_MS : SOLANA_TRADE_RETENTION_MS
      cutoff = Math.max(cutoff, serverNowRef.current() - ceilMs)
    }
    {
      // vol-hist-20261101: trades leaving the buffer move into the volume-bar
      // history so its bars stay contiguous.
      const _vh = volHistRef.current, buf = rawTradesRef.current
      if (_vh.end != null && _vh.tok === currentTokenRef.current && !hlCoinRef.current) {
        for (let i = 0; i < buf.length && buf.timeAt(i) < cutoff; i++) {
          const t = buf.timeAt(i)
          if (t < _vh.end) continue
          _vh.t.push(t); _vh.s.push(buf.sideAt(i)); _vh.a.push(buf.amountAt(i)); _vh.m.push(buf.mcapAt(i))
        }
        if (cutoff > _vh.end) _vh.end = cutoff
      }
      // cmevol-20261102: same carry for CME volume-bar history.
      const _ch = cmeVolHistRef.current
      if (_ch.end != null && hlCoinRef.current && _ch.tok === String(hlCoinRef.current).toUpperCase()) {
        for (let i = 0; i < buf.length && buf.timeAt(i) < cutoff; i++) {
          const t = buf.timeAt(i)
          if (t < _ch.end) continue
          _ch.t.push(t); _ch.s.push(buf.sideAt(i)); _ch.a.push(buf.amountAt(i)); _ch.m.push(buf.mcapAt(i))
        }
        if (cutoff > _ch.end) _ch.end = cutoff
      }
    }
    rawTradesRef.current.trimBefore(cutoff)   // in-place, order-independent (== old .filter)

    // Queue trade buffer for persistence. Same lifecycle as the per-
    // candle persist above — fires on each candle close. tradeStore
    // batches with its own 10s flush timer, so this is cheap.
    const tokenKey = resolveTokenKey(hlCoinRef.current, currentTokenRef.current)
    if (persistAllowed(tokenKey) && rawTradesRef.current.length > 0) {
      persistTrades(tokenKey, rawTradesRef.current)
    }

    // Futures: recompute the history<->live seam now that the slot rolled
    // and the prune ran — rescues any span whose live coverage just aged
    // out (ref bridge: this callback predates the fn's definition).
    if (hlCoinRef.current) reseamFutHistoryRef.current?.('roll')

    scheduleChartUpdate()
  }, [scheduleChartUpdate])

  // --- Schedule candle timer (wall-clock aligned) ---
  const scheduleCandleTimer = useCallback(() => {
    if (candleTimerRef.current) { clearInterval(candleTimerRef.current); candleTimerRef.current = null }
    if (candleTimeoutRef.current) { clearTimeout(candleTimeoutRef.current); candleTimeoutRef.current = null }

    const intervalMs = timeIntervalRef.current * 1000
    // Align the next-boundary calc to SERVER time so the timer fires when the
    // server's clock crosses the slot edge — matching where trades will start
    // bucketing. msUntilNext is still a real local duration (the offset cancels
    // in the subtraction), so setTimeout stays accurate.
    const sNow = serverNowRef.current()
    const nextBoundary = Math.ceil(sNow / intervalMs) * intervalMs
    const msUntilNext = nextBoundary - sNow

    candleTimeoutRef.current = setTimeout(() => {
      candleTimeoutRef.current = null
      startNewCandle()
      candleTimerRef.current = setInterval(startNewCandle, intervalMs)
    }, msUntilNext)
  }, [startNewCandle])

  // --- Reset chart ---
  const resetChart = useCallback(() => {
    // Before wiping state, snapshot the departing coin's trade buffer and
    // force the queue to disk. Without this, switching tokens within the
    // first bucket-interval drops everything (no candle has closed yet
    // to trigger the in-loop persist) and quick token-flips lose any
    // candles still sitting in the 5s flush queue.
    const departingTokenKey = resolveTokenKey(hlCoinRef.current, currentTokenRef.current)
    // Snapshot trade buffer for the departing coin before we wipe it,
    // then force both stores to disk. Captures up to RETAIN_MS of recent
    // swap data per coin so a return visit refines bucket-finer flips
    // and rebuilds clean timeframe switches from raw fidelity.
    if (persistAllowed(departingTokenKey) && rawTradesRef.current.length > 0) {
      // Float64 SNAPSHOT (not the live buffer) — resetChart wipes the buffer a
      // few lines down and the flush below is async; a snapshot is immune to
      // the clear. toFloat64() is already the on-disk interleaved format.
      persistTrades(departingTokenKey, rawTradesRef.current.toFloat64())
    }
    flushTradesNow().catch(() => {})

    crumb('reset', {})
    backfillDoneTokenRef.current = null
    candlesRef.current = []
    candlesTfRef.current = timeIntervalRef.current
    currentCandleRef.current = null
    rawTradesRef.current.clear()
    backfillActiveRef.current = false   // re-armed by the next subscribe
    if (backfillCapFailsafeRef.current) {
      clearTimeout(backfillCapFailsafeRef.current)
      backfillCapFailsafeRef.current = null
    }
    fineCandlesRef.current = []
    currentFineRef.current = null
    lastBucketMcapRef.current = 0
    initialMcapRef.current = null
    nativeBucketRef.current = null
    // Solana history is keyed to the native bucket just cleared — drop it.
    solHistRef.current = { key: null, map: new Map() }
    solFineRef.current = null
    solSeamRef.current = null
    solHistSeqRef.current++
    solShallowDoneRef.current = null
    if (solGateTimerRef.current) { clearTimeout(solGateTimerRef.current); solGateTimerRef.current = null }
    solGateHoldRef.current = null
    spikeGateRef.current = new SpikeGate()
    spikeProbeRef.current = { src: 0 }
    recentTradesRef.current = []
    pendingTradesRef.current = []
    frameScheduledRef.current = false
    {
      // (perf5) Held commits belong to the old token — drop them.
      const h = heldCommitRef.current
      if (h.poll) cancelAnimationFrame(h.poll)
      heldCommitRef.current = { stats: false, tape: false, chart: false, buy: 0, sell: 0, count: 0, lastMcap: null, heldSince: 0, poll: 0 }
    }
    clearTextSpriteCache()
    if (candleTimerRef.current) { clearInterval(candleTimerRef.current); candleTimerRef.current = null }
    if (candleTimeoutRef.current) { clearTimeout(candleTimeoutRef.current); candleTimeoutRef.current = null }
    setStats({ buyVolume: 0, sellVolume: 0, delta: 0, tradeCount: 0, marketCap: 0 })
    setRecentTrades([])
    structuralVersionRef.current++   // a reset is a structural change — invalidate deferred indicators
    setChartData({ sortedMcaps: [], candles: [], maxVolume: 0, highlightMcap: 0, tradeCount: 0, structuralVersion: structuralVersionRef.current, tf: candlesTfRef.current, fitGen: fitGenRef.current })
  }, [])

  // --- Apply single trade to candle state ---
  // Parse a server trade timestamp. The server emits naive-UTC ISO
  // strings (no Z / +00:00 suffix); new Date() parses those as LOCAL
  // time, putting every trade 5h (CDT) in the future. Two time bases in
  // one candle array = clock-built candles sorting hours behind
  // trade-built ones (the dashed-line ghost). Treat suffix-less strings
  // as UTC, and clamp anything still far-future to now as a last resort.
  const parseTradeTime = (ts) => {
    if (ts == null) return serverNow()
    let t
    if (typeof ts === 'string') {
      const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(ts)
      t = new Date(hasTz ? ts : ts + 'Z').getTime()
      // This branch runs for SERVER-emitted ISO timestamps (Solana, Coinbase).
      // HL/Bitunix arrive as numeric exchange ms and take the else branch —
      // do NOT sample those into the Solana server-offset (venue clock ≠ app).
      // Sample Solana server skew here, pre-clamp, while we still hold truth.
      if (Number.isFinite(t)) recordServerClock(t)
    } else {
      t = Number(ts)
    }
    if (!Number.isFinite(t)) return serverNow()
    // Far-future clamp on the APP clock. (Was local Date.now(): a local clock
    // running >2min slow clamped every real trade to the wrong time.) Not
    // circular — the app clock is synced from ack/pong, not from trades.
    const nowS = serverNow()
    if (t > nowS + 120000) return nowS
    return t
  }

  // ═══ Fine-series engine (see USE_FINE_FOLD constants) ═══
  // Plain closures over refs only — safe to capture in mount-time handlers
  // (ws onmessage) because every dependency is a stable ref object.

  const _finePrune = () => {
    const arr = fineCandlesRef.current
    if (arr.length === 0) return
    const cutoff = tapeNow() - (hlCoinRef.current ? FINE_RETENTION_FUT_MS : FINE_RETENTION_SOL_MS)
    if (arr[0].startTime >= cutoff) return
    let i = 0
    while (i < arr.length && arr[i].startTime < cutoff) i++
    if (i > 0) fineCandlesRef.current = arr.slice(i)
  }

  const _fineIngest = (tradeTime, tradeMcap, side, amount) => {
    if (!USE_FINE_FOLD) return
    if (!(tradeMcap > 0) || !(amount > 0) || !Number.isFinite(tradeTime)) return
    // Fine candles are born at the coin's STORAGE grain, never the display
    // bucket: the first trade to get here commits the grain if nothing has.
    // (The `?? mcapBucketSizeRef.current` fallback birthed a fresh token's
    // whole raw window at the ack-time display bucket, 100 — the fold then
    // dumped each 100-wide cell onto one fine row: history every 2-3 rows.)
    const nb = nativeBucketRef.current ?? setNativeBucket(tradeMcap, 'fine-ingest')
    if (!(nb > 0)) return
    const s = Math.floor(tradeTime / FINE_INTERVAL_MS) * FINE_INTERVAL_MS
    let c = currentFineRef.current
    if (!c || c.startTime !== s) {
      if (!c || s > c.startTime) {
        // Roll forward: close the current fine candle, open the new slot.
        if (c) fineCandlesRef.current.push(c)
        c = newLiveCandle(s, nb)
        currentFineRef.current = c
        if ((fineCandlesRef.current.length & 63) === 0) _finePrune()
      } else {
        // Late / out-of-order trade: merge into its existing fine candle.
        const arr = fineCandlesRef.current
        c = null
        for (let i = arr.length - 1; i >= 0; i--) {
          if (arr[i].startTime === s) { c = arr[i]; break }
          if (arr[i].startTime < s) break
        }
        if (!c) return   // older than fine coverage — bulk paths rebuild instead
      }
    }
    addTickToLive(c, tradeMcap, side, amount)
    if (c.open_mcap == null) c.open_mcap = tradeMcap
    c.close_mcap = tradeMcap
    if (c.high_mcap == null || tradeMcap > c.high_mcap) c.high_mcap = tradeMcap
    if (c.low_mcap  == null || tradeMcap < c.low_mcap)  c.low_mcap  = tradeMcap
  }

  // Bulk re-seed: wipe and replay the raw buffer through the fine ingester.
  // Called after any bulk buffer mutation (server backfill final chunk,
  // Tier-A restore, futures IDB restore). O(buffer) once per event,
  // incremental afterward.
  const rebuildFineFromTrades = () => {
    if (!USE_FINE_FOLD) return
    fineCandlesRef.current = []
    currentFineRef.current = null
    // Columnar scan — no per-trade object materialization. The buffer holds
    // only gated prints (server SpikeGate + the live client gate), so no
    // rebuild-time spike pass.
    rawTradesRef.current.forEach((time, mcap, side, amount) => {
      _fineIngest(time, mcap, side, amount)
    })
  }

  // Fold the fine series into display candles at intervalSec. Exact when
  // fine and display share the native bucket (the normal case — every fine
  // candle is born on nativeBucketRef): integer stepIdx addition. On a
  // mismatch the slot is built at the COARSEST bucket among its sources and
  // finer cells re-key by centre — coarse but honest (buildDisplay spreads
  // it over the rows it covers). The old fallback re-keyed a COARSE source
  // into the fine candle at each cell's lower edge: the whole cell's volume
  // on one fine row, i.e. cells every Nth display row.
  const foldFineCandles = (intervalSec) => {
    const ms = intervalSec * 1000
    const closed = fineCandlesRef.current
    const src = currentFineRef.current ? [...closed, currentFineRef.current] : closed
    if (src.length === 0) return []
    const nb0 = nativeBucketRef.current ?? mcapBucketSizeRef.current
    const slotNb = new Map()
    for (const f of src) {
      const s = Math.floor(f.startTime / ms) * ms
      const b = Math.max(nb0, f._nativeBucket || 0)
      if (!(slotNb.get(s) >= b)) slotNb.set(s, b)
    }
    const out = []
    let cur = null, curStart = NaN
    for (let i = 0; i < src.length; i++) {
      const f = src[i]
      const s = Math.floor(f.startTime / ms) * ms
      if (s !== curStart) {
        cur = newLiveCandle(s, slotNb.get(s))
        out.push(cur)
        curStart = s
      }
      const dst = cur._liveLevels
      const tb = cur._nativeBucket
      forEachStorageLevel(f, (step, buy, sell, b) => {
        const k = rekeyStep(step, b, tb)
        let d = dst.get(k)
        if (!d) { d = { buy: 0, sell: 0 }; dst.set(k, d) }
        d.buy  += buy
        d.sell += sell
      })
      cur._dataVersion = (cur._dataVersion | 0) + 1
      if (f.open_mcap != null && cur.open_mcap == null) cur.open_mcap = f.open_mcap
      if (f.close_mcap != null) cur.close_mcap = f.close_mcap
      if (f.high_mcap != null && (cur.high_mcap == null || f.high_mcap > cur.high_mcap)) cur.high_mcap = f.high_mcap
      if (f.low_mcap  != null && (cur.low_mcap  == null || f.low_mcap  < cur.low_mcap))  cur.low_mcap  = f.low_mcap
    }
    // SEAM SUBSTITUTION. The fine series only knows what this client saw, so
    // its version of the seam slot starts at the subscribe instant and the
    // pre-subscribe remainder of the slot is simply absent. Replace the whole
    // slot with the exact build (tip + post-cutoff raw trades) rather than
    // trying to patch the folded one — substitution keeps this a rebuild, and
    // rebuild-not-add is what makes repeated folds idempotent.
    const _seamC = _buildSeamCandle()
    if (_seamC) {
      const _i = out.findIndex(c => c.startTime === _seamC.startTime)
      if (_i >= 0) out[_i] = _seamC
      else { out.push(_seamC); out.sort((a, b) => a.startTime - b.startTime) }
    }
    return out
  }

  // ─── Wallet stats fold ───
  // Per-wallet accumulator over EVERY trade that flows through ingest —
  // live, backfill dump, and gap-fill all call foldWalletTrade, so the
  // stats window equals the session's replayed history, not the 350-trade
  // tape. Folded AT INGEST because TradeBuffer packs to Float64 columns:
  // the wallet string does not survive packing, so this is the only moment
  // it exists client-side. Module registry, no state — the panel polls at
  // 1Hz, which costs the rest of the app zero re-renders.
  const foldWalletTrade = (wallet, side, sol, tok, ts) => {
    if (!wallet || wallet === 'unknown' || !(sol > 0)) return
    const mint = currentTokenRef.current
    if (!mint) return
    if (!ownsWalletStats(mint, walletStatsInstRef.current)) return
    const M = walletStatsFor(mint)
    let w = M.get(wallet)
    if (!w) { w = { buys: 0, sells: 0, buySol: 0, sellSol: 0, buyTok: 0, sellTok: 0, first: ts, last: ts }; M.set(wallet, w) }
    if (side === 'buy') { w.buys++; w.buySol += sol; w.buyTok += tok }
    else { w.sells++; w.sellSol += sol; w.sellTok += tok }
    if (ts < w.first) w.first = ts
    if (ts > w.last) w.last = ts
  }

  const applyTradeToState = useCallback((trade, accum) => {
    const price = parseFloat(trade.price_sol)
    const amount = trade.amount || 0
    const side = trade.side
    const tradeMcap = trade.trade_mcap_sol || (price * tokenSupplyRef.current)

    if (price <= 0 || amount <= 0) return
    const tradeTime = parseTradeTime(trade.timestamp)

    if (!trade._spikeOk) {
      // ── Spike SOURCE probe (temporary — SPIKE_PROBE) ──
      // The mcap can come from two places: the server's trade_mcap_sol, or the
      // price×supply fallback. On a good print they agree; when they disagree, one
      // is the bad value and which one (vs the local median) reveals the source.
      if (SPIKE_PROBE) {
        const p = spikeProbeRef.current
        const serverMcap = Number(trade.trade_mcap_sol)
        const derivedMcap = price * tokenSupplyRef.current
        if (p.src < 60 && serverMcap > 0 && derivedMcap > 0) {
          const hi = Math.max(serverMcap, derivedMcap), lo = Math.min(serverMcap, derivedMcap)
          if (hi / lo > 1.25) {
            p.src++
            console.warn('[SpikeSrc] fields disagree', {
              by: +(hi / lo).toFixed(2) + '×',
              trade_mcap_sol: serverMcap,
              price_x_supply: Math.round(derivedMcap),
              price_sol: trade.price_sol,
              supply: tokenSupplyRef.current,
              used: Math.round(tradeMcap),
            })
          }
        }
      }

      // ── Spike gate (live) — the shared SpikeGate rule ──
      // A print far from the recent median is HELD until enough independent
      // prints confirm the level; size never helps. A held print released by
      // this one is applied first (it is older), flagged so it skips the gate
      // and probe on the way back in. Dropped/held → never touches the candle,
      // OHLC, fine series, buffer, wallet stats or tape.
      if (SPIKE_FILTER_ON && !hlCoinRef.current) {
        const rel = spikeGate().feed(tradeTime, tradeMcap, trade.wallet, trade.pool, trade)
        let self = false
        for (const t of rel) {
          if (t === trade) { self = true; continue }
          t._spikeOk = true
          applyTradeToState(t, accum)
        }
        if (!self) return
      }
    }
    foldWalletTrade(trade.wallet, side, price * amount, amount, tradeTime)

    const intervalMs = timeIntervalRef.current * 1000

    // Find target candle by timestamp. A candle's window is its OWN
    // interval — never "until the next candle's start". Rebuilt arrays
    // are sparse (only non-empty slots), so next-start windows spanned
    // entire inactivity gaps and swallowed any trade timestamped inside
    // one, building cells into a stale slot hours behind the live edge.
    const candles = candlesRef.current
    let targetCandle = null
    for (let i = candles.length - 1; i >= 0; i--) {
      const candleEnd = candles[i].startTime + intervalMs
      if (tradeTime >= candles[i].startTime && tradeTime < candleEnd) {
        targetCandle = candles[i]
        break
      }
    }
    if (!targetCandle) targetCandle = currentCandleRef.current
    if (!targetCandle) return

    // A late/out-of-order trade landing in an already-closed candle mutates
    // that candle's levels in place — a structural change for closed-candle-
    // derived indicators (swings) even though the array length/identity is
    // untouched. Bump so they recompute; normal trades (into the live candle)
    // leave the version alone, which is the whole point.
    if (targetCandle !== currentCandleRef.current) structuralVersionRef.current++

    // trade.wallet is present on live trades, gap_fill and buffer_dump — the
    // same three paths foldWalletTrade covers. Backfill has no trader, so
    // history interns to 0 and simply carries no badge.
    rawTradesRef.current.push(tradeTime, side, amount, tradeMcap, walletIdFor(trade.wallet))
    if (_rawTradeSubs.size) {
      const _tk = currentTokenRef.current
      for (const fn of _rawTradeSubs) { try { fn(_tk, trade.wallet || null, side, price * amount, price, tradeTime, tradeMcap) } catch {} }
    }
    lastBucketMcapRef.current = tradeMcap

    // First trade for this coin — lock in a fine native bucket based on
    // the actual mcap. All future candles will store at this granularity.
    // If the live candle was created before any trades (with a fallback
    // native), retroactively align its _nativeBucket since it's empty.
    if (nativeBucketRef.current == null) {
      setNativeBucket(tradeMcap, 'live-trade')
      const liveCandle = currentCandleRef.current
      if (liveCandle && !liveCandle.isHistorical && liveCandle._nativeBucket !== nativeBucketRef.current) {
        // Live candle is empty here (this IS the first trade). Safe to swap.
        const hadData = liveCandle._liveLevels && liveCandle._liveLevels.size > 0
        if (!hadData) {
          liveCandle._nativeBucket = nativeBucketRef.current
          liveCandle._liveLevels   = new Map()
          liveCandle._native       = null
        }
      }
    }

    if (side === 'buy')  accum.buyDelta  += amount
    else                 accum.sellDelta += amount
    addTickToLive(targetCandle, tradeMcap, side, amount)

    // Track OHLC on live candles. high/low were previously omitted —
    // when null, the canvas wick fallback walks forEachLevel and uses
    // bucket-snapped step keys, while the body uses raw open/close.
    // That mismatch caused the wick to visually drift from the body
    // by up to half a bucket row.
    if (!targetCandle.isHistorical) {
      if (targetCandle.open_mcap == null) targetCandle.open_mcap = tradeMcap
      targetCandle.close_mcap = tradeMcap
      if (targetCandle.high_mcap == null || tradeMcap > targetCandle.high_mcap) {
        targetCandle.high_mcap = tradeMcap
      }
      if (targetCandle.low_mcap == null || tradeMcap < targetCandle.low_mcap) {
        targetCandle.low_mcap = tradeMcap
      }
    }

    _fineIngest(tradeTime, tradeMcap, side, amount)

    // EXACT match only — startsWith was a bug source (silent false positives on prefix collisions
    // or false negatives on whitespace/case differences). gRPC delivers full base58 pubkeys.
    const isOwnTrade = !!(publicKeyRef.current && trade.wallet && trade.wallet !== 'unknown' &&
      trade.wallet === publicKeyRef.current)
    accum.newTrades.push({
      id: `${tradeTime}-${Math.random().toString(36).slice(2)}`,
      side,
      amount,
      mcap: tradeMcap,
      wallet: trade.wallet || 'unknown',
      isOwn: isOwnTrade,
      tradeType: trade.trade_type || 'swap',
      // Real ms timestamp — the formatted `time` string below is second-
      // resolution and can't drive the clock-based flow trail. `ts` is the
      // raw event/exchange time (candle bucketing). `arrivalTs` is ingest on
      // the CLIENT wall clock (Date.now) — same epoch as OrderflowTrail's
      // frameNow(). Server-app clock jumps on sync and was shifting trail x.
      ts: tradeTime,
      arrivalTs: Date.now(),
      time: tzFormat({ hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(new Date(tradeTime)),
    })

    if (side === 'buy' && isOwnTrade) {
      // Use the EXACT trade mcap (where the fill actually happened), not the snapped bucket center
      // Also pass the raw sol_amount from the trade payload so we can compute tokenAmount correctly downstream
      accum.ownBuy = {
        mcap: tradeMcap,
        amount,                                     // sol amount of THIS fill
        price: parseFloat(trade.price_sol) || 0,    // price per token in SOL — exact tokenAmount calc
      }
    }

    accum.lastMcap = tradeMcap
  }, [publicKeyRef])

  // --- Drain pending trades queue ---
  const drainTradeQueue = useCallback(() => {
    frameScheduledRef.current = false

    const trades = pendingTradesRef.current
    if (!trades.length) return
    pendingTradesRef.current = []

    const accum = { buyDelta: 0, sellDelta: 0, newTrades: [], lastMcap: null, ownBuy: null }
    for (const trade of trades) {
      applyTradeToState(trade, accum)
    }

    // (perf5) Fold into the held batch; commitLiveData (end of this drain)
    // publishes it now, or once a Y glide lands. Same arithmetic as before.
    const held = heldCommitRef.current
    if (accum.buyDelta > 0 || accum.sellDelta > 0) {
      held.buy += accum.buyDelta
      held.sell += accum.sellDelta
      held.count += trades.length
      if (accum.lastMcap != null) held.lastMcap = accum.lastMcap
      held.stats = true
    }

    if (accum.newTrades.length) {
      const minFilter = tradeFilterMinRef.current
      const filtered = minFilter > 0 ? accum.newTrades.filter(t => t.amount >= minFilter) : accum.newTrades
      if (filtered.length) {
        recentTradesRef.current = [...filtered, ...recentTradesRef.current].slice(0, 350)
        held.tape = true
      }
    }

    if (accum.ownBuy && currentTokenRef.current) {
      const supply = tokenSupplyRef.current
      const solAmount = accum.ownBuy.amount
      const mcap = accum.ownBuy.mcap
      const price = accum.ownBuy.price  // price per token in SOL
      // EXACT tokenAmount = SOL spent / SOL per token. No supply factor needed.
      // The old (solAmount * supply) / mcap formula was wrong by a factor of solPrice
      // because mcap on this chart is SOL-denominated, not USD.
      const tokenAmount = price > 0 ? solAmount / price : 0
      onOwnBuyDetectedRef.current?.(currentTokenRef.current, mcap, tokenAmount, supply)
    }

    held.chart = true
    commitLiveData()
  }, [applyTradeToState, commitLiveData])

  const drainTradeQueueRef = useRef(null)
  drainTradeQueueRef.current = drainTradeQueue

  // --- Heartbeat helper ---
  const clearHeartbeat = () => {
    if (heartbeatTimerRef.current) { clearInterval(heartbeatTimerRef.current); heartbeatTimerRef.current = null }
  }

  // --- WebSocket connection ---
  useEffect(() => {
    manualCloseRef.current = false

    const setupHeartbeat = (ws) => {
      clearHeartbeat()
      heartbeatTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping', cl: performance.now() }))
      }, WS_HEARTBEAT_INTERVAL)
    }

    const connectWS = () => {
      if (manualCloseRef.current) return
      // Don't stack sockets. On a SLOW connection the handshake sits in
      // CONNECTING (readyState 0) for a long time — and the old guard only
      // bailed on OPEN, so a second connectWS() during that window would fire,
      // overwrite wsRef, and ORPHAN the first socket. The orphan keeps its
      // onclose, and when it finally dies ("closed before connection was
      // established" in the console) it increments attempts and schedules ANOTHER
      // reconnect. On a flaky link they pile up — duplicate subscriptions, trades
      // processed twice, creeping lag until a refresh nukes them all. Bail if a
      // socket is already OPEN *or* CONNECTING.
      const rs = wsRef.current?.readyState
      if (rs === WebSocket.OPEN || rs === WebSocket.CONNECTING) return

      if (reconnectAttemptsRef.current >= WS_MAX_RECONNECT_ATTEMPTS) {
        setStatus('max_retries_reached')
        showToast('Connection failed after max retries. Please refresh.', 'sell')
        return
      }

      // Belt: if a non-open socket somehow lingers, neuter its handlers before
      // replacing it so its onclose can't schedule a competing reconnect.
      const stale = wsRef.current
      if (stale) {
        stale.onopen = stale.onclose = stale.onerror = stale.onmessage = null
        try { stale.close() } catch {}
      }

      const ws = new WebSocket(WS_URL)
      wsRef.current = ws

      ws.onopen = () => {
        setStatus('connected')
        reconnectAttemptsRef.current = 0
        // A fresh socket is not sleeping. Without this, the flag set on the
        // previous socket's hide-cycle survived reconnects, so the sleep
        // monitor's `!isSleepingRef.current` gate never let it tell the
        // server 'sleeping' again — buffering silently stopped working after
        // the first background/reconnect of a session.
        isSleepingRef.current = false
        setupHeartbeat(ws)
        // Restore server-side session after reconnect: wallet first
        // (so alerts_list comes back, wallet_activity broadcasts resume),
        // then re-subscribe to whichever token we were on so trade flow
        // resumes without the user having to re-click the chart.
        if (publicKeyRef.current) {
          try {
            ws.send(JSON.stringify({ action: 'set_wallet', wallet: publicKeyRef.current }))
          } catch {}
        }
        // positions-20261017: a (re)connect may have missed wallet trades -
        // ask the shared position balance poll for a fresh read.
        try { window.dispatchEvent(new CustomEvent('solcheddar-ws-open')) } catch {}
        if (currentTokenRef.current) {
          try {
            // ─── Wake fast-path: ask only for the span we missed ───
            // A wake bounce re-subscribes to a token whose history we still
            // hold. Today that pulls the whole 24h train — up to 288k rows
            // and ~55s of server build — and the extend-backward merge then
            // drops nearly all of it as newer than the buffer's oldest.
            //
            // The precondition is NOT a duration heuristic. It is the same
            // stamp the 'subscribed' ack uses to decide whether to
            // resetChart: backfillDoneTokenRef is set only at a completed
            // train and cleared by every resetChart. If it matches, the ack
            // will SKIP the wipe and this buffer survives into the merge —
            // which is exactly the condition under which a forward-extend
            // train is meaningful. Binding both decisions to one stamp is
            // what makes them impossible to disagree; a duration guess could
            // say "trust it" on a connection whose ack was about to wipe.
            //
            // The age check is the second half: the Solana raw buffer is
            // pruned to SOLANA_TRADE_RETENTION_MS on the first candle close
            // after wake, so a hide longer than that leaves nothing for a
            // since-train to attach to and the full build is genuinely the
            // right answer. Read here, BEFORE the prune runs.
            const _newest = rawTradesRef.current.newestTime()
            // Warm reconnect: stamp may be missing (lost backfill frame / 20s
            // gate escape) while candles + buffer still hold the chart. Treat
            // that like train-done for the since fast-path so we do not pull
            // a full train that the ack would then force-rebuild over.
            const _warm =
              rawTradesRef.current.length > 0 || candlesRef.current.length > 0
            const _sinceOk =
              (backfillDoneTokenRef.current === currentTokenRef.current || _warm) &&
              Number.isFinite(_newest) &&
              (serverNow() - _newest) < SOLANA_TRADE_RETENTION_MS
            const _sub = {
              action: 'subscribe',
              cl: performance.now(),   // app-clock sync stamp (echoed on the ack)
              token: currentTokenRef.current,
              wallet: publicKeyRef.current || null,
              // Ask for the binary backfill. An older server ignores the key
              // and sends JSON, which the handler above still accepts — the
              // handshake carries no deploy-ordering constraint either way.
              bin: 1,
            }
            // +1: `since` is inclusive server-side (ts >= since), and we
            // already hold the trade AT _newest. Asking from _newest would
            // re-send it; the merge's `> newest` filter would drop it, so
            // this is belt not brace — but a re-sent row is a row we paid
            // to build and ship for nothing.
            if (_sinceOk) {
              _sub.since = _newest + 1        // continuation: coverage stays contiguous
            } else if (!solProfileMissingRef.current) {
              // Fresh raw window. History is /sol/profile's job; the socket
              // replays only the last SOL_RAW_WINDOW_MS (the server's default
              // too — sent explicitly so the seam point is known here).
              _sub.since = Math.round(serverNow() - SOL_RAW_WINDOW_MS)
              solRawFromRef.current = _sub.since
              // Cold chart → the ack resets; fit + snap once history lands.
              if (!_warm) solFitPendingRef.current = currentTokenRef.current
            }
            crumb('ws-open', {
              resub: 'sol',
              since: _sinceOk ? Math.round((serverNow() - _newest) / 1000) : null,
            })
            ws.send(JSON.stringify(_sub))
          } catch {}
        } else if (hlCoinRef.current) {
          // Futures session: subscribeHyperliquid deliberately nulls
          // currentTokenRef (late-sol-backfill contamination fix), so the
          // sol re-subscribe above can never fire here — and without THIS
          // branch a reconnect restored nothing. The socket dropped out of
          // the server's coins[<coin>] relay set and the chart froze:
          // candles intact (local buffer), live edge dead. Any server
          // deploy killed every idle futures chart this way.
          try {
            crumb('ws-open', { resub: 'hl', coin: hlCoinRef.current })
            ws.send(JSON.stringify({ action: 'hl_subscribe', coin: hlCoinRef.current }))
          } catch {}
          // Reconnect = the outage minted empty timer candles that live
          // trades will never fill. Re-merge fresh klines into the holes.
          // (heal on reconnect removed — the reconciler owns this now)
        }
      }

      ws.onclose = () => {
        // Only the CURRENT socket's death should drive reconnection. If this is
        // an orphaned attempt (superseded by a newer connectWS), its close is
        // meaningless — ignore it, or it double-counts attempts and schedules a
        // competing reconnect. This is the other half of the stacking fix above.
        if (wsRef.current !== ws) return
        crumb('ws-close', { attempt: reconnectAttemptsRef.current })
        setStatus('disconnected')
        clearHeartbeat()
        if (manualCloseRef.current) return

        reconnectAttemptsRef.current += 1
        const attempt = reconnectAttemptsRef.current

        if (attempt >= WS_MAX_RECONNECT_ATTEMPTS) {
          setStatus('max_retries_reached')
          showToast('Connection failed after max retries. Please refresh.', 'sell')
          return
        }

        const baseDelay = 1000
        const maxDelay = 30000
        const expDelay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay)
        const jitter = Math.random() * 0.5 * expDelay
        setStatus(`reconnecting (${attempt}/${WS_MAX_RECONNECT_ATTEMPTS})`)
        reconnectTimerRef.current = setTimeout(connectWS, expDelay + jitter)
      }

      ws.onerror = () => {}


      ws.binaryType = 'arraybuffer'
      ws.onmessage = (event) => {
        lastWsMsgAtRef.current = Date.now()
        // BINARY BACKFILL ('CHDT' v1). Routed before JSON.parse — a binary
        // frame arrives as an ArrayBuffer and would throw here otherwise,
        // taking the whole socket handler down with it. Decoding yields the
        // exact same {type, token, seq, more, trades[]} shape the JSON path
        // produced, so every branch below runs unchanged; only the transport
        // moved. A frame we cannot decode is DROPPED rather than guessed at —
        // the 20s backfill guard already covers a missing history phase.
        let data
        if (typeof event.data === 'string') {
          data = JSON.parse(event.data)
        } else {
          data = decodeBackfillFrame(event.data)
          if (!data) return
        }

        if (data.type === 'subscribed') {
          applyClockSync(data, true)   // token load = forced app-clock re-sync
          crumb('subscribed', { tok: (data.token || '').slice(0, 8) })
          setCurrentToken(data.token)
          tokenSupplyRef.current = data.supply || 1000000000
          const sameTokenResub = lastSubscribedTokenRef.current === data.token
          lastSubscribedTokenRef.current = data.token
          // THE ACK-AFTER-REPLAY RACE: the server can stream the entire
          // backfill train BEFORE this ack arrives. The old unconditional
          // reset here wiped the freshly rebuilt buffer + fine series +
          // candles (3,584 replayed trades, gone), leaving the chart to
          // "start with live data". Skip the wipe iff this is the same
          // token on the same connection AND its train already completed —
          // handleSubscribe already reset before sending, so state is
          // clean. Token changes, fresh connections, and reconnects all
          // still reset exactly as before (the stamp is cleared by every
          // resetChart and so cannot survive into any other path).
          // Stamp-only condition. The first cut also required sameTokenResub
          // — but that ref holds the PREVIOUS ack's token, so on a
          // first visit to a token it is false and the skip never engaged
          // (observed live: armed -> 8TLx, train completes, ack wipes
          // 19,638 trades anyway). The stamp alone is sufficient AND
          // precise: it is set only at train completion and cleared by
          // every resetChart, so it can only match data.token when the
          // exact race occurred on this connection.
          //
          // ONE STAMP, THREE DECISIONS. The comment above argues the stamp is
          // sufficient and precise, then applied it to the reset alone. The
          // other two — arming the reveal gate and holding the raw-buffer cap
          // — stayed unconditional, and that is the 20s stuck: when the train
          // beats the ack, the 'backfill' frame RELEASES the gate (2579) and
          // the ack then RE-ARMS it, with no later frame in existence to
          // release it again. It sits closed until the 20s catastrophic guard
          // fires (60s via the canvas gate's EMPTY_CAP_MS when the publish was
          // empty too). All three decisions ask the same question — "is there
          // still a history phase to wait for?" — so all three read one answer.
          //
          // This is also why a page refresh beats clicking a token. A click
          // pre-arms the gate in handleSubscribe BEFORE the subscribe is sent,
          // so a fast train can land in the window between that arm and the
          // ack. A refresh's first subscribe goes out from ws.onopen with no
          // pre-arm, so the gate is only ever armed by the ack and the train
          // necessarily lands after it. Same server, same data — the click
          // path just had one more arm than it had releases.
          const trainDone = backfillDoneTokenRef.current === data.token
          // Same-token reconnect with chart still loaded: keep it. The stamp
          // alone misses real warm sessions (backfill frame lost → gate
          // escaped → stamp never set → live trades filled the chart). An
          // unconditional wipe here was "I lose the loaded data when I
          // reconnect." First visit: sameTokenResub is false, so wipe stays.
          const warmResub = sameTokenResub && (
            candlesRef.current.length > 0 || rawTradesRef.current.length > 0
          )
          if (!trainDone && !warmResub) {
            resetChart()
          } else {
            crumb('reset-skip', {
              tok: (data.token || '').slice(0, 8),
              why: trainDone ? 'train' : 'warm',
            })
          }
          // Seed bucket only on a genuine token CHANGE. On a same-token
          // reconnect resub the committed bucket is already correct — keep it
          // so the rebuild reveals at the right scale in one paint instead of
          // cascading 100 → smartBucket → ideal (three relayouts = flashes).
          if (!sameTokenResub) setMcapBucketSize(100)
          startNewCandle()
          scheduleCandleTimer()

          const liveStartTime = serverNow()
          liveStartTimeRef.current = liveStartTime

          // Restore via the shared once-per-token path (the no-ack
          // fallback in handleSubscribe may have already run it).
          {
            const ackToken = data.token
            restoreTradesOnce(`sol:${ackToken}`, () => currentTokenRef.current === ackToken)
          }

          // Solana historical footprint arrives from the server as a
          // 'backfill' WS frame (real captured swaps -> footprint). ARM the
          // pending flag here so the reveal gate holds the cover until that
          // frame lands and rebuilds — build-then-render. Without this the
          // gate revealed on the first live trades: one candle, degenerate
          // y-axis, all history slamming in as a visible column seconds
          // later. The server now always sends the frame (empty trades []
          // for fresh/uncaptured mints) as the explicit "history phase
          // done" signal; the guard timer below covers an old server build
          // or a lost frame so the gate can never wedge shut.
          //
          // SKIPPED ENTIRELY when the train already finished (trainDone). Not
          // "armed and released quickly" — never armed. There is no history
          // phase left to cover and no train left to hold the cap for, so
          // arming would close a gate whose only release has already been
          // consumed. Clear any timers still standing from the arm that this
          // ack would otherwise have replaced.
          if (!trainDone && !warmResub) {
          markHistoryPending(true)
          // (gatefast) No extra shallow-window hold: the gate opens the
          // instant the first backfill chunk lands; history fills in behind.
          // Was: if (!solProfileMissingRef.current) solGateHoldRef.current = data.token
          // Hold the Solana raw-buffer cap off until the backfill train's
          // finalChunk re-seeds the fine series at full depth.
          backfillActiveRef.current = true
          // Failsafe (see backfillCapFailsafeRef): finalChunk lost → cap
          // re-engages anyway. 3min covers the heaviest observed trains
          // (500k rows) with wide margin.
          if (backfillCapFailsafeRef.current) clearTimeout(backfillCapFailsafeRef.current)
          backfillCapFailsafeRef.current = setTimeout(() => {
            backfillCapFailsafeRef.current = null
            if (backfillActiveRef.current) {
              backfillActiveRef.current = false
              crumb('backfill-failsafe', {})
              console.warn('[Backfill] finalChunk never arrived — raw-buffer cap re-engaged by failsafe')
            }
          }, 180000)
          if (backfillGuardRef.current) clearTimeout(backfillGuardRef.current)
          // Catastrophic escape ONLY — not a deadline. The first cut set this
          // to 4000ms and heavy backfills (500k rows, ~40MB) simply take
          // longer: the guard declared the frame lost, un-armed the gate,
          // and the frame slammed in seconds later as the visible column —
          // on precisely the coins that needed the cover most. The server
          // now always sends the frame (empty for fresh mints), so on a
          // healthy socket it WILL arrive; 20s means genuinely broken.
          backfillGuardRef.current = setTimeout(() => {
            backfillGuardRef.current = null
            // GATE ONLY. This used to clear backfillActiveRef too, which is a
            // different concern on a different clock: the gate is a UX
            // deadline ("stop hiding the chart"), the cap is data integrity
            // ("don't trim a buffer the train is still filling").
            //
            // Sharing one 20s timer meant any train slower than 20s had the
            // Solana raw-buffer ceiling drop 26h -> 3h MID-TRAIN, so the next
            // candle close ran trimBefore against the buffer the remaining
            // chunks were landing in, and finalChunk's rebuildFineFromTrades
            // then re-seeded the fine series from what survived. The chart got
            // SHORTER after a successful backfill. 169k rows measured at
            // 20,141ms sits right on that boundary, so this was firing on
            // exactly the heavy tokens it was least affordable on.
            //
            // The cap already has its own owner: backfillCapFailsafeRef above,
            // at 180s, which is sized for the heaviest observed trains and
            // checks backfillActiveRef before acting. Two concerns, two
            // timers, neither guessing for the other.
            markHistoryPending(false)
          }, 20000)
          } else {
            // Train already landed. Leave the gate as the 'backfill' frame
            // left it (open) and drop any timer from the pre-send arm, which
            // would otherwise fire later against a chart that is already
            // finished. backfillActiveRef was cleared by that frame's
            // finalChunk, so the cap is correctly engaged already.
            if (backfillGuardRef.current) {
              clearTimeout(backfillGuardRef.current)
              backfillGuardRef.current = null
            }
            if (backfillCapFailsafeRef.current) {
              clearTimeout(backfillCapFailsafeRef.current)
              backfillCapFailsafeRef.current = null
            }
            crumb('ack-no-rearm', { tok: (data.token || '').slice(0, 8) })
          }

          // Solana history (/sol/profile). Every ack: a fresh subscribe needs
          // it, and a resubscribe after an outage may have holes the store
          // fills (server-side cache makes the repeat cheap).
          if (!hlCoinRef.current && data.token === currentTokenRef.current) {
            _loadSolHistory(trainDone || warmResub ? 'resub' : 'ack')
          }

          getTokenName(data.token).then(name => {
            setStatus(`token:${name} (${data.token.slice(0, 6)}...)`)
            setTokenHistory(prev => {
              const filtered = prev.filter(t => t.address !== data.token)
              const updated = [{ address: data.token, name }, ...filtered].slice(0, 7)
              try { sessionStorage.setItem('solcheddar_token_history', JSON.stringify(updated)) } catch {}
              return updated
            })
          })
        }

        // Server-pushed footprint backfill: recent captured swaps shaped as
        // raw trades {time, side, amount, tradeMcap}, already VWAP-filtered
        // and decimal-reconciled server-side to match the live stream.
        // CHUNKED, NEWEST-FIRST: the server slices history into {seq, more}
        // frames, newest slice first. Chunk 1 carries the recent window —
        // apply it, size the bucket, release the reveal gate, and the chart
        // is visible almost immediately. Every later chunk is strictly older
        // than the buffer's oldest (the server slices a chronological array
        // from the end), so the extend-backward merge below applies to each
        // chunk unchanged, and history slides into view leftward as frames
        // land. Rebuilds are throttled between chunks (a full-buffer rebuild
        // per 20k-trade chunk would stack main-thread jank); the final frame
        // (more=false) always flushes so nothing is left un-rendered.
        if (data.type === 'backfill') {
          const incoming = Array.isArray(data.trades) ? data.trades : []
          // STRICT identity: sol-mode only (a late sol backfill must never
          // rebuild a futures chart), and the frame must be tagged with the
          // exact currently-charted mint. The old check (`!data.token ||`)
          // accepted untagged frames and, before currentTokenRef was nulled
          // on futures entry, matched stale mints — the "solana data on the
          // futures chart" bug.
          const tokenOk = !hlCoinRef.current
            && !!data.token
            && data.token === currentTokenRef.current
          const firstChunk = data.seq == null || data.seq === 0
          const finalChunk = data.more !== true
          // Merge POLICY comes from what the server says it did, never from
          // what we asked for. An old server ignores `since` and sends the
          // full train untagged; reading our own request as the policy would
          // apply a forward-extend merge to a backward-extend train and
          // silently drop all of it. Absent tag == full == today's behaviour.
          const sinceMode = data.mode === 'since'
          const older = []
          crumb('backfill', { seq: data.seq ?? 0, more: data.more === true, n: incoming.length, ok: tokenOk, since: sinceMode })
          if (tokenOk && incoming.length) {
            if (sinceMode) {
              // ── Wake fast-path: extend FORWARD ──
              // The span between our last trade and now. Every row is newer
              // than the buffer's oldest, so the backward filter below would
              // discard 100% of it — that is why this needs its own policy
              // rather than a tweak to the shared one.
              //
              // The floor is captured ONCE, before the loop. TradeBuffer.push
              // updates _maxTime as it goes, so re-reading newestTime() per
              // row would gate the batch against itself and admit only the
              // first trade. Same reasoning, same idiom as _dumpFloor in the
              // buffer_dump handler below.
              //
              // Strictly-greater, not >=: the trade at the floor is one we
              // already hold. This is the only dedup this path needs — the
              // server window starts above our last trade and live trades are
              // held server-side behind cs.backfilling until the train ends,
              // so there is no third writer to race.
              const floor = rawTradesRef.current.newestTime()
              // Commit the storage grain BEFORE anything is ingested. On a
              // fresh subscribe this train IS the first data (the raw window),
              // and every fine candle it births is keyed at whatever grain
              // exists at that moment — the seed used to run after the loop.
              if (nativeBucketRef.current == null) {
                const seed = incoming[incoming.length - 1]
                if (seed && seed.tradeMcap > 0) setNativeBucket(seed.tradeMcap, 'backfill-since')
              }
              let added = 0
              for (let i = 0; i < incoming.length; i++) {
                const t = incoming[i]
                if (!t || !(t.time > floor)) continue
                if (!(t.amount > 0) || !(t.tradeMcap > 0)) continue
                const _side = t.side === 'sell' ? 'sell' : 'buy'
                rawTradesRef.current.push(t.time, _side, t.amount, t.tradeMcap)
                // Keep the 26h fine series intact across reconnect. A later
                // rebuildFineFromTrades would replay only the ~3h raw cap and
                // truncate display history on every since-train.
                _fineIngest(t.time, t.tradeMcap, _side, t.amount)
                added++
              }
              if (added) bfDirtyRef.current = true
              // The server ran SpikeGate over this window; warm the live
              // gate's reference from its newest prints so the first live
              // trades aren't judged cold.
              if (finalChunk) spikeGate().prime(incoming.slice(-16).map(t => t && t.tradeMcap))
              crumb('backfill-since', { n: incoming.length, added })
            } else {
            // LEGACY FULL TRAIN — only reached against a server without
            // /sol/profile (_solProfileMissing re-subscribes without `since`;
            // the new server never sends an untagged train).
            // Extend history BACKWARD only: keep just the trades older than
            // the buffer's current oldest. Whichever source (this backfill or
            // the IDB restore) lands first claims its range; the other adds
            // only deeper history. No overlap, no double-count, no reliance
            // on key-matching across two independent pipelines.
            const oldest = rawTradesRef.current.oldestTime()
            for (let i = 0; i < incoming.length; i++) {
              const t = incoming[i]
              if (!t || t.time >= oldest) continue
              if (!(t.amount > 0) || !(t.tradeMcap > 0)) continue
              older.push(t)
            }
            if (older.length) {
              rawTradesRef.current.prepend(older)   // insert older history before the head
              bfDirtyRef.current = true
              if (nativeBucketRef.current == null) {
                setNativeBucket(older[older.length - 1].tradeMcap, 'backfill-older')
              }
            }
            }
            // Rebuild policy: always on the first chunk (it releases the
            // gate — the reveal must land on built candles) and the final
            // chunk (flush), throttled to ~2/sec in between so a long chunk
            // train doesn't saturate the main thread with full rebuilds.
            // REBUILD AT REVEAL AND FINAL ONLY. The 500ms cadence between
            // them was the load lag: each tick ran rebuildLiveFromTrades
            // over the WHOLE growing buffer — a 20s train ≈ 40 full
            // rebuilds, each bigger than the last (O(n²); profiled at 5.3s
            // self-time in one function, 55% of main thread in onmessage).
            // And the work was invisible: chunks extend history LEFTWARD,
            // off-screen in follow mode, while the visible right edge is
            // maintained by live trades through applyTradeToState anyway.
            // Chunks still merge into the buffer per-arrival (cheap); the
            // one full rebuild at finalChunk folds everything.
            const now = Date.now()
            if (bfDirtyRef.current && (firstChunk || finalChunk)) {
              // since-train = forward extend only. force:true rebuilds the
              // live region from the raw buffer (~3h cap) and DROPS the
              // longer fine-folded display history (~24h) — the reconnect
              // "loaded data disappeared" bug. Full trains still force.
              rebuildLiveFromTrades({ force: !sinceMode })
              bfLastRebuildRef.current = now
              bfDirtyRef.current = false
              // Re-point the live-candle ref (rebuild may have replaced the
              // array object the ref held).
              const last = candlesRef.current[candlesRef.current.length - 1]
              if (last && !last.isHistorical) currentCandleRef.current = last
              // History store on top (no-op until /sol/profile has landed).
              _composeSol('backfill')
              scheduleChartUpdate()
            }
            // Fine series: re-seed from the (now deeper) buffer once a LEGACY
            // full train completes. Never on since: since-mode already
            // _fineIngest'd each added row above (the normal path now).
            if (finalChunk && !sinceMode) rebuildFineFromTrades()
            // Smart bucket sizing + snap-to-latest: FIRST chunk only. The
            // bucket derives from the visible recent window, which chunk 1
            // is; re-deriving per chunk would thrash the y-axis while older
            // history (which doesn't change the visible range in follow
            // mode) streams in.
            // Never on a since-train: the bucket was derived when this token
            // loaded and the visible range has not changed, so re-deriving
            // would thrash the y-axis on every wake — and scrollToLatest
            // would yank a user who had deliberately panned away before
            // backgrounding the tab. (`older` is empty in since mode anyway;
            // the explicit flag says WHY rather than relying on that.)
            if (!sinceMode && firstChunk && older.length) {
              const live = candlesRef.current.filter(c => !c.isHistorical)
              const viewCount = Math.min(live.length, viewWindowSizeRef.current || DEFAULT_VIEW_WINDOW)
              const recent = live.slice(-viewCount)
              let hiMcap = -Infinity, loMcap = Infinity
              for (const c of recent) {
                if (c.high_mcap != null && c.high_mcap > hiMcap) hiMcap = c.high_mcap
                if (c.low_mcap != null && c.low_mcap < loMcap) loMcap = c.low_mcap
              }
              if (Number.isFinite(hiMcap) && Number.isFinite(loMcap) && hiMcap > loMcap) {
                const cH = bodyScrollRef.current ? bodyScrollRef.current.clientHeight : 600
                const smartBucket = pickBucketForRange(hiMcap - loMcap, cH)
                if (smartBucket !== mcapBucketSizeRef.current) {
                  mcapBucketSizeRef.current = smartBucket
                  setMcapBucketSize(smartBucket)
                }
              }
              setTimeout(() => { scrollToLatest?.() }, 120)
            }
            if (finalChunk) {
              // (The backfillDoneTokenRef stamp used to be set HERE. It moved
              // to the outer finalChunk block below — see the note there.)
              dbg(`[Backfill] complete (${rawTradesRef.current.length} trades buffered)`)
              // Commit the replayed history to DISPLAY candles NOW. The
              // rebuild above reseeds the FINE series only; the sole other
              // fold trigger is the TF-change effect — so without this, a
              // perfectly replayed token still opened at candles=0 until
              // the user happened to nudge the timeframe. Mirrors that
              // effect's sol branch exactly: same guards, same
              // hist-preserving merge, same cap.
              {
                const tfSec = timeIntervalRef.current
                const canFold = USE_FINE_FOLD
                  && tfSec >= FINE_INTERVAL_SEC
                  && tfSec % FINE_INTERVAL_SEC === 0
                  && (fineCandlesRef.current.length > 0 || currentFineRef.current)
                if (canFold && !hlCoinRef.current) {
                  const hist = candlesRef.current.filter(c => c.isHistorical)
                  const live = foldFineCandles(tfSec)
                  candlesRef.current = deduplicateCandles([...hist, ...live]).slice(-maxCandlesFor(hlCoinRef.current, timeIntervalRef.current))
                  const last = candlesRef.current[candlesRef.current.length - 1]
                  currentCandleRef.current = (last && !last.isHistorical) ? last : null
                  dbg(`[Backfill fold] ${live.length} candles @ ${tfSec}s from ${fineCandlesRef.current.length} fine`)
                  _composeSol('backfill-fold')
                  scheduleChartUpdate()
                }
              }
              // Fit on the raw window only when history can't widen the view
              // any more (shallow already resolved, or the legacy server).
              if (tokenOk && (solShallowDoneRef.current === currentTokenRef.current || solProfileMissingRef.current)) {
                _solFit(currentTokenRef.current)
              }
              crumb('backfill-done', { buffered: rawTradesRef.current.length })
            }
          }
          // Gate release on the FIRST frame — the chart reveals with the
          // recent window while older chunks continue streaming in.
          if (firstChunk || finalChunk) {
            if (backfillGuardRef.current) {
              clearTimeout(backfillGuardRef.current)
              backfillGuardRef.current = null
            }
            if (tokenOk && solGateHoldRef.current === currentTokenRef.current) {
              // Shallow history still in flight: it releases (_solReleaseGate),
              // capped so a slow or dead route can never wedge the cover.
              if (!solGateTimerRef.current) {
                const _holdTok = currentTokenRef.current
                solGateTimerRef.current = setTimeout(() => {
                  solGateTimerRef.current = null
                  if (solGateHoldRef.current === _holdTok) solGateHoldRef.current = null
                  markHistoryPending(false)
                }, SOL_GATE_HOLD_MS)
              }
            } else {
              markHistoryPending(false)
            }
          }
          // Backfill train complete — the fine series was just re-seeded at full
          // captured depth (finalChunk above), so the Solana raw-buffer cap can
          // now safely engage on the next candle close.
          if (finalChunk) {
            // THE STAMP MEANS "THIS TOKEN'S TRAIN FINISHED" — not "…and had
            // rows in it". It used to live inside `if (tokenOk &&
            // incoming.length)`, so an EMPTY train (which the server sends as
            // the explicit history-phase-done signal for fresh/uncaptured
            // mints, and for a since-window in which nothing traded) released
            // the reveal gate below without ever stamping.
            //
            // Two things read this stamp, and both were wrong in that case.
            // The ack skips resetChart on a match — so an empty train meant
            // the NEXT resubscribe wiped a buffer that live trades had since
            // filled. And the ack now also skips re-arming the gate on a
            // match (see the subscribed handler); leaving the stamp unset
            // would re-arm a gate no later frame can release, which is the
            // 20s stuck this whole change exists to remove.
            //
            // tokenOk, not currentTokenRef, because a late train for a token
            // we have already left must not stamp the one we are on now —
            // tokenOk is the same identity check the merge uses.
            if (tokenOk) backfillDoneTokenRef.current = currentTokenRef.current
            backfillActiveRef.current = false
            if (backfillCapFailsafeRef.current) {
              clearTimeout(backfillCapFailsafeRef.current)
              backfillCapFailsafeRef.current = null
            }
            // The deferred pass the guard in reconcile() promised. Without
            // this the rising-edge pass is simply lost on any connection
            // whose train was still running at 1500ms, and holes older than
            // the train's window would wait for a timeframe change to be
            // noticed. Same 800ms settle the TF-change trigger uses.
            setTimeout(() => { try { reconcileRef.current?.() } catch {} }, 800)
          }
        }

        if (data.type === 'trade') {
          // Futures mode: sol trades never enter this chart. The current
          // server nulls cs.token on hl_subscribe so none should arrive,
          // but an older build (or a frame racing the subscribe) must not
          // slip through the token check against a stale/null ref.
          if (hlCoinRef.current) return
          if (data.token && data.token !== currentTokenRef.current) return
          pendingTradesRef.current.push(data)
          if (!frameScheduledRef.current) {
            frameScheduledRef.current = true
            requestAnimationFrame(() => drainTradeQueueRef.current())
          }
        }

        // ─── Hyperliquid trade stream ───
        if (data.type === 'hl_trade' && data.trades) {
          // Drop trades for any coin this chart isn't watching. The server
          // multiplexes every subscribed HL coin over one socket (stamping
          // each batch with .symbol), and a chart's socket carries ALL of
          // them — without this guard a two-pair futures layout cross-feeds
          // (ETH prints landing on the SOL chart and vice versa). hlCoinRef
          // is this instance's coin; ignore the stray symbols.
          // Hardened: drop the batch unless its symbol POSITIVELY matches
          // this chart's coin. The old form (symbol && coin && symbol!==coin)
          // had two escape hatches — a batch missing .symbol, or a transient
          // null hlCoinRef during a switch — both let foreign trades through.
          // Defense in depth behind the unsubscribe fix above. Symbols are
          // compared case-insensitively and base-normalized (server strips
          // USDC/USDT/USD; do the same here so a stray quoted symbol can't
          // slip past).
          const _norm = (s) => (s || '').toUpperCase().replace(/(USDC|USDT|USD)$/, '')
          // DEBUG (localStorage.cheddar_debug_hl='1'): prove what the guard
          // actually compares. If foreign trades still land, this shows
          // whether .symbol is missing, mismatched-format, or matching-but-
          // -passed. Remove once routing is confirmed.
          if (typeof localStorage !== 'undefined' && localStorage.getItem('cheddar_debug_hl') === '1') {
            const want = _norm(hlCoinRef.current)
            const got = _norm(data.symbol)
            dbg('[HLGuard]', { rawSymbol: data.symbol, want, got, pass: want && got === want })
          }
          if (!hlCoinRef.current || _norm(data.symbol) !== _norm(hlCoinRef.current)) {
            return
          }
          // Exchange time from the server (HL `time` → timestamp ms). Fallback
          // to app clock only if the venue omitted it — never Date.now().
          const _fallbackNow = serverNow()
          for (const t of data.trades) {
            // Convert HL trade → Solana trade shape
            // For futures: price IS the y-axis value, supply = 1 → mcap = price
            const price = t.price
            const solAmount = t.amount * price  // notional value as "sol_amount" equivalent
            const exTs = Number(t.timestamp)
            pendingTradesRef.current.push({
              type: 'trade',
              side: t.side,
              amount: t.amount,
              price_sol: String(price),
              trade_mcap_sol: price,  // supply=1 → mcap = price
              market_cap_sol: price,
              sol_amount: solAmount,
              wallet: cmeInfo(hlCoinRef.current) ? 'Tick-Stream' : 'Hyperliquid',
              signature: '',
              timestamp: (Number.isFinite(exTs) && exTs > 0) ? exTs : _fallbackNow,
            })
          }
          if (!frameScheduledRef.current) {
            frameScheduledRef.current = true
            requestAnimationFrame(() => drainTradeQueueRef.current())
          }
        }

        // ─── Bitunix trade stream (CEX orderflow) ───
        if (data.type === 'bitunix_trade' && data.trades) {
          // Symbol guard, mirroring hl_trade's. These CEX handlers ingested
          // unconditionally — with three streams subscribed per pair (HL +
          // Bitunix + Coinbase), any over-delivery put a second pair's
          // prints on the first chart (the dual-chart cross-pair bug).
          // Dash-tolerant strip: Bitunix tags "BTCUSDC", Coinbase "BTC-USDC".
          const _cexNorm = (s) => (s || '').toUpperCase().replace(/[-_]?(USDC|USDT|USD)$/, '')
          if (!hlCoinRef.current || _cexNorm(data.symbol) !== _cexNorm(hlCoinRef.current)) {
            return
          }
          const _fallbackNow = serverNow()
          for (const t of data.trades) {
            const price = t.price
            const solAmount = t.amount * price
            const exTs = Number(t.timestamp)
            pendingTradesRef.current.push({
              type: 'trade',
              side: t.side,
              amount: t.amount,
              price_sol: String(price),
              trade_mcap_sol: price,
              market_cap_sol: price,
              sol_amount: solAmount,
              wallet: 'Bitunix',
              signature: '',
              timestamp: (Number.isFinite(exTs) && exTs > 0) ? exTs : _fallbackNow,
            })
          }
          if (!frameScheduledRef.current) {
            frameScheduledRef.current = true
            requestAnimationFrame(() => drainTradeQueueRef.current())
          }
        }

        // ─── Coinbase trade stream (CEX orderflow) ───
        if (data.type === 'coinbase_trade' && data.trades) {
          // Same guard as bitunix_trade above — see that comment.
          const _cexNorm = (s) => (s || '').toUpperCase().replace(/[-_]?(USDC|USDT|USD)$/, '')
          if (!hlCoinRef.current || _cexNorm(data.symbol) !== _cexNorm(hlCoinRef.current)) {
            return
          }
          const _fallbackNow = serverNow()
          for (const t of data.trades) {
            const price = t.price
            const solAmount = t.amount * price
            // Coinbase may send ISO string or ms; parseTradeTime handles both.
            const rawTs = t.timestamp
            const exNum = Number(rawTs)
            const hasTs = (typeof rawTs === 'string' && rawTs.length > 0) || (Number.isFinite(exNum) && exNum > 0)
            pendingTradesRef.current.push({
              type: 'trade',
              side: t.side,
              amount: t.amount,
              price_sol: String(price),
              trade_mcap_sol: price,
              market_cap_sol: price,
              sol_amount: solAmount,
              wallet: 'Coinbase',
              signature: '',
              timestamp: hasTs ? rawTs : _fallbackNow,
            })
          }
          if (!frameScheduledRef.current) {
            frameScheduledRef.current = true
            requestAnimationFrame(() => drainTradeQueueRef.current())
          }
        }

        // ─── Gap fill response ───
        // Trades for a span we KNOW we missed. Folded with the same
        // primitives the buffer dump uses (addTickToLive + extremes), into
        // the placeholder candles already sitting in those slots. Safe by
        // construction: only slots inside the requested window are touched,
        // and those are placeholders by definition — nothing occupied can be
        // downgraded, no volume can be reduced, no cells can be shadowed.
        if (data.type === 'gap_fill') {
          // vol-hist-20261101: our own volume-bar history ask - not a gap.
          const _vh = volHistRef.current
          if (_vh.pending && data.since === _vh.pending.since && data.until === _vh.pending.until && data.token === _vh.tok) {
            const tr = (data.trades || []).filter(x => x && x.time < _vh.pending.until && x.amount > 0 && x.tradeMcap > 0)
            tr.sort((x, y) => x.time - y.time)
            _vh.t = tr.map(x => x.time).concat(_vh.t)
            _vh.s = tr.map(x => x.side === 'sell' ? 'sell' : 'buy').concat(_vh.s)
            _vh.a = tr.map(x => x.amount).concat(_vh.a)
            _vh.m = tr.map(x => x.tradeMcap).concat(_vh.m)
            _vh.from = _vh.pending.since
            _vh.pending = null
            dbg(`[VolHist] +${tr.length} trades, total ${_vh.t.length}`)
            scheduleChartUpdate()
            setTimeout(() => { try { pumpVolHist() } catch {} }, 500)
            return
          }
          const n = data.trades?.length || 0
          crumb('gap-fill', { n, mins: Math.round((data.until - data.since) / 60000) })
          dbg(`[GapFill] received ${n} trades for ${Math.round((data.until - data.since) / 60000)}min`)
          if (data.token && data.token !== currentTokenRef.current) return   // stale across a switch
          if (n === 0) {
            // Nothing there: the period was genuinely quiet. Mark those slots
            // so the reconciler never asks again — this is what stops a
            // permanent low-grade poll on a sleepy token.
            const iv = timeIntervalRef.current * 1000
            for (let t = Math.floor(data.since / iv) * iv; t <= data.until; t += iv) {
              verifiedEmptyRef.current.add(t)
            }
            if (verifiedEmptyRef.current.size > 5000) verifiedEmptyRef.current.clear()
          }
          if (n > 0) {
            const intervalMs = timeIntervalRef.current * 1000
            const bucketSize = mcapBucketSizeRef.current
            const candles = candlesRef.current
            const candleMap = new Map(candles.map(c => [c.startTime, c]))
            const supply = tokenSupplyRef.current || 1e9
            let folded = 0
            // doji-reconnect-20261102: fold in time order, and rebuild OHLC
            // for slots that held no trades before this fold (disconnect
            // placeholders carry a flat O=H=L=C, which drew as dojis).
            const _gfTrades = data.trades.slice().sort((x, y) => (Number(x.time ?? x.ts) || 0) - (Number(y.time ?? y.ts) || 0))
            const _gfFresh = new Set()
            const _gfSeen = new Set()
            for (const t of _gfTrades) {
              // Field shape is the SERVER's, not the live stream's:
              // build_backfill_trades emits {time, side, amount, tradeMcap}
              // with the market cap ALREADY computed (it applies decimal
              // reconciliation before the multiply). Reading t.price and
              // multiplying by supply — the live-stream idiom — yielded 0
              // for every row, so all 12k trades were silently skipped and
              // the reconciler re-asked forever. Take tradeMcap directly,
              // with the price path only as a fallback.
              const ts = Number(t.time ?? t.ts) || 0
              if (ts < data.since || ts > data.until) continue
              let tradeMcap = Number(t.tradeMcap ?? t.mcap) || 0
              if (!(tradeMcap > 0)) {
                const price = Number(t.price) || 0
                tradeMcap = price > 0 ? price * supply : 0
              }
              if (!(tradeMcap > 0)) continue
              const amount = Number(t.amount ?? t.sol) || 0
              if (!(amount > 0)) continue
              const side = t.side === 'sell' ? 'sell' : 'buy'
              foldWalletTrade(t.wallet, side, (Number(t.price) || 0) * amount, amount, ts)
              const candleStart = Math.floor(ts / intervalMs) * intervalMs
              let c = candleMap.get(candleStart)
              if (!c) {
                c = newLiveCandle(candleStart, nativeBucketRef.current ?? bucketSize)
                candleMap.set(candleStart, c)
                candles.push(c)
              }
              if (!_gfSeen.has(c)) {
                _gfSeen.add(c)
                let _pv = 0
                try { _pv = candleTotalVolume(c) || 0 } catch { _pv = 0 }
                if (!(_pv > 0)) _gfFresh.add(c)
              }
              addTickToLive(c, tradeMcap, side, amount)
              if (_gfFresh.has(c)) {
                _gfFresh.delete(c)   // first (earliest) trade seeds O=H=L=C
                c.open_mcap = c.high_mcap = c.low_mcap = c.close_mcap = tradeMcap
              }
              if (c.open_mcap == null) c.open_mcap = tradeMcap
              c.close_mcap = tradeMcap
              if (c.high_mcap == null || tradeMcap > c.high_mcap) c.high_mcap = tradeMcap
              if (c.low_mcap == null || tradeMcap < c.low_mcap) c.low_mcap = tradeMcap
              folded++
            }
            if (folded > 0) {
              candlesRef.current = deduplicateCandles(candles)
              scheduleChartUpdate()
              dbg(`[GapFill] folded ${folded} trades into ${Math.round((data.until - data.since) / 60000)}min of placeholders`)
            } else {
              // Rows came back but none landed. Something is off with the
              // shape, and re-asking every 8s would hammer the server with
              // the same query forever. Mark the span and say so loudly.
              console.warn(`[GapFill] ${n} trades returned but NONE folded — span marked to stop re-asking`, data.trades?.[0])
              const iv2 = timeIntervalRef.current * 1000
              for (let t2 = Math.floor(data.since / iv2) * iv2; t2 <= data.until; t2 += iv2) {
                verifiedEmptyRef.current.add(t2)
              }
            }
          }
        }

        if (data.type === 'buffer_dump') {
          crumb('dump', { n: data.count, dropped: data.dropped })
          dbg(`[Buffer] Received ${data.count} trades (${data.dropped} dropped)`)
          isSleepingRef.current = false
          pendingTradesRef.current = []
          frameScheduledRef.current = false

          if (data.trades && data.trades.length > 0) {
            const intervalMs = timeIntervalRef.current * 1000
            const bucketSize = mcapBucketSizeRef.current
            const candles = candlesRef.current

            let buyDelta = 0, sellDelta = 0, lastMcap = null
            const newRecentTrades = []
            const candleMap = new Map(candles.map(c => [c.startTime, c]))

            const _dumpSupply = tokenSupplyRef.current
            const _dumpRows = data.trades.map(t => ({
              m: t.trade_mcap_sol || (parseFloat(t.price_sol) * _dumpSupply),
              t: parseTradeTime(t.timestamp),
              s: t.side,
            }))
            // Trail x = now - arrivalTs. Stamping Date.now() on EVERY dump row
            // (phone wake / awake buffer_dump) piled the whole missed window on
            // the live edge, then the clump drifted together = flash + wrong
            // start. Anchor age-0 to the newest event in the dump and age the
            // rest by event-time delta so spacing matches the missed tape.
            let _dumpNewestTs = 0
            for (let _dni = 0; _dni < _dumpRows.length; _dni++) {
              const _tt = _dumpRows[_dni].t
              if (_tt > _dumpNewestTs) _dumpNewestTs = _tt
            }
            const _dumpClientNow = Date.now()

            // The wallet registry cannot use the _dumpFloor gate below —
            // the floor protects the RAW BUFFER, which resetChart empties,
            // while the registry survives resets on purpose. A floor of
            // -Infinity on a fresh buffer refolds the whole dump into a
            // map that already holds it. Authoritative replay instead:
            // clear, then fold everything. Keyed on currentTokenRef —
            // the same ref foldWalletTrade writes through — so the map
            // cleared is by construction the map about to be rebuilt.
            // (The dump frame carries no token of its own; when the
            // server stamps one, both this and the fold should gate on
            // it.)
            if (ownsWalletStats(currentTokenRef.current, walletStatsInstRef.current)) resetWalletStats(currentTokenRef.current)

            // Dedup boundary for the dump: everything at/older than the
            // buffer's newest trade is already applied (live or backfill).
            // Data-derived and server-clock-consistent — the old gate
            // compared server-stamped trade times against the LOCAL-clock
            // liveStartTimeRef: a local clock running ahead silently ate
            // the first skew-seconds of the dump (a permanent hole at the
            // history↔live seam), while a local clock behind let the
            // backfill-overlap trades double-count. Captured ONCE before
            // the loop so the pushes below can't gate their own batch.
            const _dumpFloor = rawTradesRef.current.newestTime()

            // Same live SpikeGate over the new part of the dump, in order. A
            // print still held at the end stays in the gate and is applied by
            // applyTradeToState when a later live print confirms it.
            let _dumpOk = null
            if (SPIKE_FILTER_ON && !hlCoinRef.current) {
              _dumpOk = new Set()
              const _g = spikeGate()
              for (let _gi = 0; _gi < data.trades.length; _gi++) {
                const _r = _dumpRows[_gi], _t = data.trades[_gi]
                if (!(_r.t > _dumpFloor) || !(_r.m > 0)) continue
                for (const _a of _g.feed(_r.t, _r.m, _t.wallet, _t.pool, _t)) _dumpOk.add(_a)
              }
            }

            // doji-reconnect-20261102: slots with no prior trades (disconnect
            // placeholders, flat O=H=L=C) get OHLC rebuilt from the dump by
            // trade TIME, applied after the loop.
            const _dmSeen = new Set()
            const _dmFresh = new Map()   // candle -> {ft, fm, lt, lm, hi, lo}
            for (let _di = 0; _di < data.trades.length; _di++) {
              const trade = data.trades[_di]
              const price = parseFloat(trade.price_sol)
              const amount = trade.amount || 0
              const side = trade.side
              const tradeMcap = trade.trade_mcap_sol || (price * tokenSupplyRef.current)
              if (price <= 0 || amount <= 0) continue
              if (_dumpOk && _dumpRows[_di].t > _dumpFloor && !_dumpOk.has(trade)) continue   // spike — held/dropped
              // Parse ONCE. This ran twice per trade (wallet fold + candle
              // fold) — regex + Date per call, ×21k trades in a dump, pure
              // duplicate work on an identical input.
              const tradeTime = parseTradeTime(trade.timestamp)
              foldWalletTrade(trade.wallet, side, price * amount, amount, tradeTime)
              if (tradeTime <= _dumpFloor) continue

              const candleStart = Math.floor(tradeTime / intervalMs) * intervalMs
              let targetCandle = candleMap.get(candleStart)
              if (!targetCandle) {
                targetCandle = newLiveCandle(candleStart, nativeBucketRef.current ?? bucketSize)
                candleMap.set(candleStart, targetCandle)
                candles.push(targetCandle)
              }

              if (!_dmSeen.has(targetCandle)) {
                _dmSeen.add(targetCandle)
                let _pv = 0
                try { _pv = candleTotalVolume(targetCandle) || 0 } catch { _pv = 0 }
                if (!(_pv > 0)) _dmFresh.set(targetCandle, { ft: tradeTime, fm: tradeMcap, lt: tradeTime, lm: tradeMcap, hi: tradeMcap, lo: tradeMcap })
              }
              {
                const _f = _dmFresh.get(targetCandle)
                if (_f) {
                  if (tradeTime < _f.ft) { _f.ft = tradeTime; _f.fm = tradeMcap }
                  if (tradeTime >= _f.lt) { _f.lt = tradeTime; _f.lm = tradeMcap }
                  if (tradeMcap > _f.hi) _f.hi = tradeMcap
                  if (tradeMcap < _f.lo) _f.lo = tradeMcap
                }
              }
              addTickToLive(targetCandle, tradeMcap, side, amount)
              if (side === 'buy') buyDelta  += amount
              else                sellDelta += amount

              // Track OHLC
              if (targetCandle.open_mcap == null) targetCandle.open_mcap = tradeMcap
              targetCandle.close_mcap = tradeMcap
              if (targetCandle.high_mcap == null || tradeMcap > targetCandle.high_mcap) {
                targetCandle.high_mcap = tradeMcap
              }
              if (targetCandle.low_mcap == null || tradeMcap < targetCandle.low_mcap) {
                targetCandle.low_mcap = tradeMcap
              }

              rawTradesRef.current.push(tradeTime, side, amount, tradeMcap)
              _fineIngest(tradeTime, tradeMcap, side, amount)
              lastBucketMcapRef.current = tradeMcap
              lastMcap = tradeMcap

              const isOwnTrade = !!(publicKeyRef.current && trade.wallet && trade.wallet !== 'unknown' &&
                trade.wallet === publicKeyRef.current)

              newRecentTrades.push({
                id: `${tradeTime}-${Math.random().toString(36).slice(2)}`,
                side, amount, mcap: tradeMcap,
                wallet: trade.wallet || 'unknown',
                isOwn: isOwnTrade,
                tradeType: trade.trade_type || 'swap',
                // ts was MISSING here — without it every HL trade hit the
                // `if (t.ts == null) continue` guard in OrderflowTrail and the
                // futures trail drew only its flat deadline (no jagged points).
                // arrivalTs drives the trail x; synthesize from event-time
                // delta vs newest dump row (see _dumpNewestTs above). Live
                // ingest uses Date.now() — dumps use the same client epoch.
                ts: tradeTime,
                arrivalTs: _dumpNewestTs > 0
                  ? _dumpClientNow - Math.max(0, _dumpNewestTs - tradeTime)
                  : _dumpClientNow,
                time: tzFormat({ hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(new Date(tradeTime)),
              })

              if (side === 'buy' && isOwnTrade) {
                // Use exact trade mcap + correctly computed tokenAmount, route through the live ref
                const supply = tokenSupplyRef.current
                const tokenAmount = price > 0 ? amount / price : 0
                onOwnBuyDetectedRef.current?.(currentTokenRef.current, tradeMcap, tokenAmount, supply)
              }
            }
            for (const [_c, _f] of _dmFresh) {
              _c.open_mcap = _f.fm; _c.close_mcap = _f.lm; _c.high_mcap = _f.hi; _c.low_mcap = _f.lo
            }

            // Trim to max candles
            const liveOnly = candles.filter(c => !c.isHistorical)
            if (liveOnly.length > MAX_CANDLES) {
              const hist = candles.filter(c => c.isHistorical)
              candlesRef.current = [...hist, ...liveOnly.slice(-maxCandlesFor(hlCoinRef.current, timeIntervalRef.current))]
            }

            // Fresh "now" candle — same app clock as startNewCandle /
            // scheduleCandleTimer (serverNow). Futures exchange stamps bucket
            // into these slots; slot boundary itself is never Date.now().
            const _slotNow = serverNowRef.current()
            const nowAligned = Math.floor(_slotNow / intervalMs) * intervalMs
            candles.push(newLiveCandle(nowAligned, nativeBucketRef.current ?? bucketSize))
            candlesRef.current = deduplicateCandles(candles)
            currentCandleRef.current = candlesRef.current[candlesRef.current.length - 1]

            scheduleCandleTimer()

            if (buyDelta > 0 || sellDelta > 0) {
              setStats(prev => {
                const buyVolume = prev.buyVolume + buyDelta
                const sellVolume = prev.sellVolume + sellDelta
                return {
                  buyVolume, sellVolume,
                  delta: buyVolume - sellVolume,
                  tradeCount: prev.tradeCount + data.trades.length,
                  marketCap: lastMcap ?? prev.marketCap,
                }
              })
            }

            if (newRecentTrades.length > 0) {
              const minFilter = tradeFilterMinRef.current
              const filtered = minFilter > 0 ? newRecentTrades.filter(t => t.amount >= minFilter) : newRecentTrades
              if (filtered.length) {
                // Dump buffer is chronological (oldest->newest). Tape + trail
                // are newest-first - reverse so list[0] is the live-edge print
                // (OrderflowTrail lead-in / liveSide read index 0).
                const newestFirst = filtered.slice().reverse()
                recentTradesRef.current = [...newestFirst, ...recentTradesRef.current].slice(0, 200)
                setRecentTrades([...recentTradesRef.current])
              }
            }

            // Fill empty candle gaps
            if (candles.length >= 2) {
              const firstStart = candles[0].startTime
              const lastStart = candles[candles.length - 1].startTime
              const existingStarts = new Set(candles.map(c => c.startTime))
              const gapCandles = []
              for (let t = firstStart + intervalMs; t < lastStart; t += intervalMs) {
                const aligned = firstStart + Math.round((t - firstStart) / intervalMs) * intervalMs
                if (!existingStarts.has(aligned)) {
                  gapCandles.push(newLiveCandle(aligned, nativeBucketRef.current ?? bucketSize))
                }
              }
              if (gapCandles.length > 0) {
                candles.push(...gapCandles)
                candlesRef.current = deduplicateCandles(candles)
                dbg(`[Buffer] Filled ${gapCandles.length} empty candle gaps`)
                requestGapFill(gapCandles.map(c => c.startTime), intervalMs, 'buffer-dump')
              }
            }

            scheduleChartUpdate()
            dbg(`[Buffer] Backfilled ${data.trades.length} trades into candles`)
          }
        }

        if (data.type === 'wallet_activity') {
          // Forward to useTrading via a window event so positions stay fresh
          // for tokens not currently charted. Listener lives in useTrading.js.
          window.dispatchEvent(new CustomEvent('solcheddar-wallet-activity', { detail: data }))
        }
        if (data.type === 'meteora_pool') {
          // Forward Meteora DLMM pool pubkey to the canvas heatmap hook.
          // Listener lives in FootprintChartCanvas.jsx.
          window.dispatchEvent(new CustomEvent('solcheddar-meteora-pool', { detail: { mint: data.mint, pool: data.pool } }))
        }
        if (data.type === 'meteora_tank') {
          // Bid-side SOL from the server's DLMM poll (browser can't call
          // that API — CORS). Straight into the tank registry the Tracker
          // panel reads; mcap comes from the chart's own state, not the
          // message, so the ratio uses the same number the chart shows.
          // Full payload as the 6th arg: bidUsd/askUsd/totalUsd (server
          // 2026-08-19+) feed the Tracker's tug bar. Older servers omit
          // them and the registry degrades to bid-only.
          publishTankSample(data.mint, data.sol, lastBucketMcapRef.current || 0, data.sym, data.usd, data)
        }
        if (data.type === 'pong') applyClockSync(data, false)
      }
    }

    connectWS()

    // ── Network awareness ──────────────────────────────────────────
    // The backoff is blind to the NETWORK: on a drop the socket zombies
    // until its own timeout, and when connectivity returns the user sits
    // out the remaining backoff — it feels dead, so they refresh the whole
    // page. The browser already announces both transitions:
    //   offline → close the socket NOW (no zombie; status honest at once)
    //   online  → cancel any pending backoff, reset attempts, reconnect
    //             IMMEDIATELY. The existing onopen restore re-subscribes
    //             (sol token or HL coin) and the server backfill / kline
    //             merge heals the gap — connection and chart recover, the
    //             page never reloads.
    const onOffline = () => {
      setStatus('offline')
      const w = wsRef.current
      if (w && (w.readyState === WebSocket.OPEN || w.readyState === WebSocket.CONNECTING)) {
        try { w.close() } catch {}
      }
    }
    const onOnline = () => {
      if (manualCloseRef.current) return
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null }
      reconnectAttemptsRef.current = 0
      const w = wsRef.current
      if (!w || w.readyState === WebSocket.CLOSED || w.readyState === WebSocket.CLOSING) {
        setStatus('reconnecting')
        connectWS()
      }
    }
    window.addEventListener('offline', onOffline)
    // ─── Wake-bounce (published for the sleep/wake effect) ───
    // Mobile suspend produces sockets that LIE: readyState can claim OPEN
    // after the server dropped us for missed pings (no FIN processed while
    // frozen — a half-open zombie), and an honest onclose that DID fire left
    // its reconnect backoff timer frozen with the page. Either way, after a
    // long background the only trustworthy move is a clean bounce through
    // the normal reconnect path — whose onopen resubscribes and pulls fresh
    // backfill, healing the data gap in the same stroke.
    const bounceWS = (why) => {
      const dead = wsRef.current
      if (dead) {
        dead.onopen = dead.onclose = dead.onerror = dead.onmessage = null
        try { dead.close() } catch {}
      }
      wsRef.current = null
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null }
      reconnectAttemptsRef.current = 0
      dbg('[Wake] ' + why + ' — bouncing socket')
      crumb('ws-bounce', { why })
      connectWS()
    }
    wsControlRef.current = { bounce: bounceWS }

    window.addEventListener('online', onOnline)

    return () => {
      manualCloseRef.current = true
      clearHeartbeat()
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null }
      if (wsRef.current) wsRef.current.close()
      if (candleTimerRef.current) clearInterval(candleTimerRef.current)
      if (candleTimeoutRef.current) clearTimeout(candleTimeoutRef.current)
    }
  }, [resetChart, startNewCandle, scheduleCandleTimer, showToast])

  // --- Sleep/wake detection ---
  useEffect(() => {
    const THROTTLE_THRESHOLD = 3000

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now()
        lastTickRef.current = performance.now()
        sleepMonitorRef.current = setInterval(() => {
          const now = performance.now()
          const delta = now - lastTickRef.current
          if (delta > THROTTLE_THRESHOLD) {
            const ws = wsRef.current
            if (ws && ws.readyState === WebSocket.OPEN && !isSleepingRef.current) {
              isSleepingRef.current = true
              ws.send(JSON.stringify({ action: 'sleeping' }))
              crumb('sleep-throttle', {})
              if (candleTimerRef.current) { clearInterval(candleTimerRef.current); candleTimerRef.current = null }
              dbg(`[Sleep] Tab throttled (${Math.round(delta)}ms gap) - server buffering`)
            }
            clearInterval(sleepMonitorRef.current)
            sleepMonitorRef.current = null
          }
          lastTickRef.current = now
        }, 1000)
      } else {
        if (sleepMonitorRef.current) { clearInterval(sleepMonitorRef.current); sleepMonitorRef.current = null }

        // Backfill empty candle slots
        // ── Wake refill: two venues, two truths ──
        // SOLANA: the away-span's trades live in the server's swap archive,
        // so manufacturing empty candles and gap-filling them with trades is
        // correct — that IS loading it from the server, trade-grained.
        // FUTURES: there is no trade archive to gap-fill from; the server's
        // truth is klines + capture profiles. Minting empty candles here just
        // planted data-less slots for the heal to fight ("came back and the
        // 5m is bare candlesticks while the 1m shows everything"), and the
        // gap-fill request could only ever answer 0. So: HL skips the filler
        // entirely and asks the server for the real thing.
        // WHO OWNS THE WAKE SPAN — decided BEFORE anything fills it.
        // Two mechanisms can cover the gap and they must not both run. On a
        // trusted-open socket we ask for a buffer dump, and the gap_fill
        // answer is the only source, so it is required. On a bounce the
        // resubscribe carries `since`, and the since-train ships every trade
        // in that same window — a gap_fill answer would then land on slots the
        // train already folded in and ADD to them. gap_fill's fold has no
        // placeholder check, so the result is silently doubled volume across
        // the wake span: no error, no crash, just candles that lie.
        //
        // The reconciler's `if (backfillActiveRef.current) return` does not
        // cover this. That guards the RECONCILER; this is a direct call that
        // fires before the bounce has even been decided. So decide first, and
        // let exactly one of them fill.
        //
        // Hoisted verbatim from below — same reads, same values, just earlier.
        // The matrix it encodes is documented at the branch that consumes it.
        const WAKE_TRUST_MS = 15000
        const hiddenFor = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0
        const ws = wsRef.current
        const rs = ws?.readyState
        const willBounce = !(rs === WebSocket.OPEN && hiddenFor < WAKE_TRUST_MS)

        if (hlCoinRef.current) {
          healHistoryRef.current?.('wake')
        } else {
          const candles = candlesRef.current
          if (candles.length >= 1) {
            const intervalMs = timeIntervalRef.current * 1000
            const lastCandle = candles[candles.length - 1]
            const now = tapeNow()
            const existingStarts = new Set(candles.map(c => c.startTime))
            const gapCandles = []
            for (let t = lastCandle.startTime + intervalMs; t < now; t += intervalMs) {
              if (!existingStarts.has(t)) gapCandles.push(newLiveCandle(t, nativeBucketRef.current ?? mcapBucketSizeRef.current))
            }
            if (gapCandles.length > 0) {
              candles.push(...gapCandles)
              candlesRef.current = deduplicateCandles(candlesRef.current)
              dbg(`[Wake] Filled ${gapCandles.length} empty candle gaps`)
              // Placeholders are minted either way — they hold the grid's
              // shape. Only the REQUEST is conditional: on a bounce the
              // since-train fills these same slots, and asking twice is what
              // double-counts.
              if (!willBounce) {
                requestGapFill(gapCandles.map(c => c.startTime), timeIntervalRef.current * 1000, 'wake')
              } else {
                crumb('wake-gapfill-skip', { n: gapCandles.length })
              }
            }
          }
        }

        // ─── Wake-time connection recovery ───
        // The old code trusted readyState and, when the socket WASN'T open,
        // did nothing but restart the candle timer — leaving reconnection to
        // a backoff setTimeout that was FROZEN while hidden. Two failure
        // modes, both "leave the app and come back and it's broken":
        //   dead   — OS killed the socket in the background; onclose fired
        //            but its retry timer thawed on iOS's schedule, not ours.
        //   zombie — server dropped us for missed pings, no FIN processed
        //            while frozen, readyState still says OPEN; 'awake' goes
        //            into a dead pipe and the chart freezes for minutes
        //            (the heartbeat only SENDS pings, it measures nothing).
        // Matrix: short hide + OPEN → trust it, cheap awake-dump as before.
        // Anything else — not OPEN, or hidden long enough that OPEN is a
        // claim we can't verify — bounce through the normal reconnect path.
        crumb('wake', { hiddenSec: Math.round(hiddenFor / 1000), fut: !!hlCoinRef.current })
        if (!willBounce) {
          if (isSleepingRef.current) {
            try { ws.send(JSON.stringify({ action: 'awake' })) } catch {}
            dbg('[Wake] Tab visible - requesting buffer dump')
          }
          isSleepingRef.current = false
          scheduleCandleTimer()
        } else {
          isSleepingRef.current = false
          scheduleCandleTimer()
          wsControlRef.current.bounce?.(
            rs === WebSocket.OPEN
              ? `hidden ${Math.round(hiddenFor / 1000)}s — OPEN untrusted after suspend`
              : `socket ${rs === undefined ? 'missing' : 'not open (' + rs + ')'} on wake`
          )
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    // bfcache restores (mobile especially) fire 'pageshow' and can SKIP
    // visibilitychange entirely — same wake logic, second doorbell.
    const handlePageShow = () => { if (document.visibilityState === 'visible') handleVisibilityChange() }
    window.addEventListener('pageshow', handlePageShow)

    // ─── Futures dead-feed watchdog (event-independent) ───
    // Every heal path above depends on an EVENT arriving intact: the
    // wake-heal has a max(60s, interval) floor (a 10-min sleep on a 30m
    // chart heals nothing), Samsung's freezer can skip visibilitychange,
    // and the candle timer mints empty filler candles that make a zombie
    // socket look alive. This watches the DATA instead: on a futures
    // chart, three consecutive zero-volume candles means the feed is dead
    // no matter what readyState claims -> re-merge klines AND bounce the
    // socket. ETH/BTC/SOL never print three genuinely empty candles.
    let lastWatchdogFire = 0
    const watchdog = setInterval(() => {
      if (!hlCoinRef.current || document.hidden) return
      const cs = candlesRef.current
      if (!cs || cs.length < 4) return
      const now = Date.now()
      if (now - lastWatchdogFire < 90_000) return
      // Candle emptiness was the WRONG deadness signal, twice over: the
      // first cut counted the zero-vol forming candle (cried wolf every 2
      // minutes on live ETH), and the closed-candles fix still fired on
      // quiet pairs at big TFs — three closed empty 30m candles is 90 quiet
      // minutes on an illiquid perp, a normal Tuesday, not a dead feed.
      // Measure the TRANSPORT instead: the client pings every 30s and the
      // server pongs, so a live pipe delivers a message at least twice a
      // minute no matter how dead the market is. 90s of TOTAL silence =
      // three missed pongs = the socket is a zombie regardless of what
      // readyState claims. Quiet markets can't trip this; dead pipes can't
      // dodge it.
      if (now - lastWsMsgAtRef.current > 90_000) {
        lastWatchdogFire = now
        crumb('fut-watchdog', { silentSec: Math.round((now - lastWsMsgAtRef.current) / 1000) })
        dbg('[FutWatchdog] 90s of ws silence on a futures chart — healing + bouncing')
        // (heal on watchdog removed — the reconciler owns this now)
        wsControlRef.current.bounce?.('watchdog: silent futures feed')
      }
    }, 20_000)
    return () => {
      clearInterval(watchdog)
      window.removeEventListener('pageshow', handlePageShow)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (sleepMonitorRef.current) clearInterval(sleepMonitorRef.current)
    }
  }, [drainTradeQueue, startNewCandle, scheduleCandleTimer])

  // --- Display bucket change ---
  // INVARIANT: display bucket changes never mutate candle data. Candles
  // live at the per-coin fine storage bucket; buildDisplay does ALL
  // grouping at render time. (The repair pass for legacy coarse-stamped
  // candles was deleted with the candle cache — every candle now comes
  // from the single trade rebuilder at the fine bucket.)
  useEffect(() => {
    if (rawTradesRef.current.length > 0) {
      lastBucketMcapRef.current = rawTradesRef.current.lastMcap()
    }
    updateChartData()
  }, [mcapBucketSize, updateChartData])

  // --- THE rebuilder: live region from the raw trade buffer ---
  // Trades on disk are the only persistence and this is the only writer
  // that turns them into candles. Called after trade restore (token
  // subscribe / return) and on timeframe changes (sync from buffer,
  // async from the full store). Slots at the current interval;
  // authoritative replace when the rebuild covers existing volume,
  // per-slot richer-wins merge as the no-coverage fallback.
  const rebuildLiveFromTrades = useCallback((opts = {}) => {
    const force = !!opts.force  // TF changes: old-interval candles must
                                // NEVER survive, regardless of volume
    const trades = rawTradesRef.current
    if (!trades || trades.length === 0) return
    const intervalMs = timeIntervalRef.current * 1000
    const fineBucket = nativeBucketRef.current ?? mcapBucketSizeRef.current
    if (!fineBucket) return
    const slots = new Map()
    trades.forEach((time, tradeMcap, side, amount) => {
      const slotStart = Math.floor(time / intervalMs) * intervalMs
      let candle = slots.get(slotStart)
      if (!candle) { candle = newLiveCandle(slotStart, fineBucket); slots.set(slotStart, candle) }
      addTickToLive(candle, tradeMcap, side, amount)
      if (candle.open_mcap == null) candle.open_mcap = tradeMcap
      candle.close_mcap = tradeMcap
      if (candle.high_mcap == null || tradeMcap > candle.high_mcap) candle.high_mcap = tradeMcap
      if (candle.low_mcap  == null || tradeMcap < candle.low_mcap)  candle.low_mcap  = tradeMcap
    })
    if (slots.size === 0) return

    // SEAM SUBSTITUTION — before the volume comparison below, so the tip's
    // volume counts toward slotsVol and the rebuild is judged on what it
    // actually carries. Overwriting the whole slot (rather than folding the
    // tip into the slot this loop just built) is what keeps repeated rebuilds
    // idempotent: every pass mints the seam candle fresh from the same two
    // exact inputs.
    {
      const _seamC = _buildSeamCandle()
      if (_seamC) slots.set(_seamC.startTime, _seamC)
    }

    // Volume comparison: rebuild vs the existing live region.
    let slotsVol = 0
    for (const c of slots.values()) slotsVol += candleTotalVolume(c)
    let existingLiveVol = 0
    for (const c of candlesRef.current) {
      if (!c.isHistorical) existingLiveVol += candleTotalVolume(c)
    }

    const byStart = new Map()
    for (const c of candlesRef.current) {
      if (c.isHistorical) byStart.set(c.startTime, c)
    }

    if (force || slotsVol >= existingLiveVol * 0.999) {
      // AUTHORITATIVE REPLACE. The rebuild carries at least everything
      // the chart holds (append-only store ⊇ all farmed data), so the
      // slots become the entire live region. Merging-by-startTime here
      // was the double chart: after a TF flip the array holds
      // old-interval candles, and new-interval slots interleave BETWEEN
      // their startTimes instead of replacing them — the same volume
      // drawn twice at offset positions.
      for (const [t, c] of slots) {
        if (candleLevelCount(c) === 0) continue
        byStart.set(t, c)
      }
    } else {
      // Fallback: rebuild is missing volume (no trade coverage for some
      // candles). Keep the existing live region; merge slots in only
      // where they're richer.
      for (const c of candlesRef.current) {
        if (!c.isHistorical) byStart.set(c.startTime, c)
      }
      for (const [t, c] of slots) {
        if (candleLevelCount(c) === 0) continue
        const prev = byStart.get(t)
        if (!prev || prev.isHistorical || candleTotalVolume(c) >= candleTotalVolume(prev)) {
          byStart.set(t, c)
        }
      }
    }

    const merged = Array.from(byStart.values()).sort((a, b) => a.startTime - b.startTime)
    candlesRef.current = merged.slice(-maxCandlesFor(hlCoinRef.current, timeIntervalRef.current))
    // The live pointer must ALWAYS be the candle whose window contains
    // NOW. Adopting "last slot in the rebuild" pointed ticks at stale
    // data: restore resolving before any live trade ends on the old
    // session's final slot, the just-created (empty, volume-0) live
    // candle loses the replace, and every tick builds into the stale
    // slot — the live edge stuck on the dashed line.
    {
      // Same app clock startNewCandle uses (serverNow) for both venues.
      const _slotNow = serverNowRef.current()
      const nowSlot = Math.floor(_slotNow / intervalMs) * intervalMs
      const last = candlesRef.current[candlesRef.current.length - 1]
      if (last && !last.isHistorical && last.startTime === nowSlot) {
        currentCandleRef.current = last
      } else {
        const fresh = newLiveCandle(nowSlot, fineBucket)
        candlesRef.current.push(fresh)
        currentCandleRef.current = fresh
      }
    }
    scheduleChartUpdate()
  }, [scheduleChartUpdate])

  // --- Once-per-token trade restore ---
  // Callable from BOTH the 'subscribed' ack and the no-ack fallback in
  // handleSubscribe (returning to a token the server already considers
  // subscribed may produce no ack at all). The guard makes it idempotent
  // so whichever path fires first wins and the other no-ops.
  const restoredTokenRef = useRef(null)
  const restoreTradesOnce = useCallback((tokenKey, stillCurrent) => {
    // Solana local restore is disabled — the server backfill is the source of
    // truth (see PERSIST_SOL_TRADES_LOCALLY). restoreTradesOnce is only ever
    // called with sol: keys, so this short-circuits the whole local stitch.
    if (!PERSIST_SOL_TRADES_LOCALLY) return
    if (restoredTokenRef.current === tokenKey) return
    restoredTokenRef.current = tokenKey
    loadTrades(tokenKey).then(trades => {
      if (!trades || trades.length === 0) return
      if (!stillCurrent()) return
      // Extend backward only — keep just trades older than the buffer's
      // current oldest, so this restore and the server 'backfill' (which may
      // have already populated the recent window) can't double-count their
      // overlap regardless of which lands first.
      const oldest = rawTradesRef.current.oldestTime()
      const fresh = trades.filter(t => t && t.time < oldest)
      if (fresh.length === 0) return
      rawTradesRef.current.prepend(fresh)
      if (nativeBucketRef.current == null && fresh.length > 0) {
        setNativeBucket(fresh[fresh.length - 1].tradeMcap, 'restore')
      }
      rebuildLiveFromTrades()
      rebuildFineFromTrades()
      dbg(`[Restore] ${fresh.length} trades for ${tokenKey}`)
    }).catch(err => console.warn('[Restore] trade restore failed:', err))
  }, [rebuildLiveFromTrades])

  // The forming column is the clock slot of this timeframe, not the last
  // bar that happened to have prints. A fold/rebuild stops on the last
  // loaded bar, so a quiet current bucket (fresh coin, short history, a
  // gap since the last trade) leaves that older bar as the tail, left of
  // where the live column should be. The live slot is always the tail.
  const ensureLiveColumn = () => {
    const tfSec = timeIntervalRef.current || 300
    const intervalMs = tfSec * 1000
    const nowFn = serverNowRef.current
    const nowMs = typeof nowFn === 'function' ? nowFn() : Date.now()
    const nowSlot = Math.floor(nowMs / intervalMs) * intervalMs
    const bucket = nativeBucketRef.current ?? mcapBucketSizeRef.current
    const src = candlesRef.current || []
    const kept = []
    let live = null
    for (let i = 0; i < src.length; i++) {
      const c = src[i]
      if (!c) continue
      if (c.startTime === nowSlot && !c.isHistorical) { live = c; continue }
      if (c.startTime < nowSlot) kept.push(c)
    }
    if (!live) {
      const cur = currentCandleRef.current
      live = (cur && !cur.isHistorical && cur.startTime === nowSlot)
        ? cur
        : newLiveCandle(nowSlot, bucket)
    }
    kept.push(live)
    candlesRef.current = kept
    currentCandleRef.current = live
  }

  // --- Time interval change: instant local refold, then reconcile ---
  // ONE paint, immediately, from what the browser already holds:
  //   • history — the outgoing TF's store cells, summed whole-slot into the
  //     new TF when the old TF divides it (up-switch). A coarse cell can't be
  //     split, so a down / non-divisible switch folds no history;
  //   • recent — the fine series (5s → any TF that is a multiple of 5s;
  //     6.5h Solana / the raw cap on futures), which already contains the raw
  //     window and every live trade; below 5s (1s) the raw buffer rebuilds.
  // Nothing is re-downloaded or re-subscribed: no since-train, no gap_fill.
  // The only fetch is the new TF's profile history, which RECONCILES behind
  // the paint — server cells replace folded ones slot for slot (_folded), the
  // live slot and the cutoff seam are re-minted once the seam is armed, and
  // compose picks exactly one source per slot (no seam double count).
  useEffect(() => {
    const hlCoin = hlCoinRef.current
    const token = currentTokenRef.current
    const canFold = USE_FINE_FOLD
      && timeInterval >= FINE_INTERVAL_SEC
      && timeInterval % FINE_INTERVAL_SEC === 0
      && (fineCandlesRef.current.length > 0 || currentFineRef.current)
    // Live region at the new TF, local data only.
    const buildLive = () => {
      if (canFold) {
        candlesRef.current = foldFineCandles(timeInterval)
        const last = candlesRef.current[candlesRef.current.length - 1]
        currentCandleRef.current = (last && !last.isHistorical) ? last : null
      } else {
        candlesRef.current = candlesRef.current.filter(c => !c.isHistorical)
        rebuildLiveFromTrades({ force: true })
      }
    }

    if (!hlCoin) {
      // ─── Solana ───
      const prevSol = solHistRef.current
      // Kept 1m store: fold from it on any 1m-multiple switch; if it spans the
      // full depth, only top up the newest window (from where it ends).
      const fine = solFineRef.current?.key === `${token}|60` ? solFineRef.current : (prevSol.key === `${token}|60` ? prevSol : null)
      const useFine = !!(token && fine && timeInterval % 60 === 0)
      let fineSince = 0
      if (useFine) {
        const nowMs = serverNowRef.current()
        const iv = (fine.cov || []).find(([a]) => a <= nowMs - SOL_HIST_MAX_SEC * 1000 + 3600_000)
        if (iv && nowMs - iv[1] <= SOL_HIST_SLAB_SEC * 1000) fineSince = iv[1]
      }
      if (token) {
        _solStoreFor(token, timeInterval)
        const n = _foldSolStoreInto(useFine ? fine : prevSol, token, timeInterval)
        if (n) dbg(`[SolHist] refold: ${n} slots ${prevSol.key} → ${timeInterval}s`)
      }
      candlesTfRef.current = timeInterval
      buildLive()
      ensureLiveColumn()
      _composeSol('tf-refold')            // store (refolded) + live; no-op before subscribe
      if (token) markHistoryPending(false)
      scheduleCandleTimer()
      updateChartData()
      dbg(`[TF refold] sol @ ${timeInterval}s (${canFold ? 'fine' : 'raw'})`)
      // Reconcile: the new TF's /sol/profile (shallow, then slabs).
      if (token) {
        if (fineSince) _loadSolHistory('tf-topup', { topUp: true, sinceMs: fineSince })
        else _loadSolHistory('tf')
      }
    } else {
      // ─── Futures ───
      const coin = hlCoin
      const tfAtStart = timeInterval
      const tokenKey = resolveTokenKey(hlCoinRef.current, currentTokenRef.current)
      const intervalMs = tfAtStart * 1000
      const prevStore = futStoreRef.current
      candlesTfRef.current = tfAtStart
      _futStoreFor(coin, tfAtStart)            // reset to this pair|tf
      const folded = _foldPrevStoreInto(prevStore, coin, tfAtStart)
      if (folded) dbg(`[FutHist] refold: ${folded} slots ${prevStore.key} → ${tfAtStart}s`)
      buildLive()
      _composeFut('tf-refold')
      ensureLiveColumn()
      scheduleCandleTimer()
      updateChartData()
      crumb('tf-refold', { tf: tfAtStart, fold: canFold, hist: folded })

      // Reconcile. Klines + shallow profile (and, below 5s only, the local
      // IDB trade union — never the server's raw trades).
      Promise.all([
        fetchHyperliquidCandles(coin, tfAtStart).catch(err => {
          console.warn('[Hyperliquid History] TF kline fetch failed:', err)
          return []
        }),
        (canFold || !tokenKey) ? Promise.resolve([]) : loadTrades(tokenKey).catch(() => []),
        _fetchHlProfilesSafe(coin, tfAtStart),
      ]).then(([historical, stored, om]) => {
        if (hlCoinRef.current !== coin) return
        if (timeIntervalRef.current !== tfAtStart) return
        // Arm the seam, then re-mint the live region so the seam slot is the
        // exact build (tip + post-cutoff trades) — rebuild, never add.
        _captureFutSeam(coin, tfAtStart, om)
        if (!canFold && stored && stored.length > 0) {
          const keyOf = (t) => t.time + '|' + t.tradeMcap + '|' + t.amount + '|' + t.side
          const seen = new Set()
          const union = []
          for (const t of stored) {
            const k = keyOf(t)
            if (!seen.has(k)) { seen.add(k); union.push(t) }
          }
          rawTradesRef.current.forEach((time, tradeMcap, side, amount) => {
            const t = { time, tradeMcap, amount, side }
            const k = keyOf(t)
            if (!seen.has(k)) { seen.add(k); union.push(t) }
          })
          union.sort((a, b) => a.time - b.time)
          rawTradesRef.current.replaceAll(union)
        }
        buildLive()
        const currentCandleStart = Math.floor(serverNowRef.current() / intervalMs) * intervalMs
        const klines = (historical || []).filter(c => c.startTime < currentCandleStart)
        if (klines.length) _futStorePut(coin, tfAtStart, klines)
        const cells = _mergeOmCells(klines, om, tfAtStart)
          .filter(c => c._omFootprint && c.startTime < currentCandleStart)
        if (cells.length) _futStorePut(coin, tfAtStart, cells, { cells: true })
        _composeFut('tf-reconcile')
        ensureLiveColumn()
        updateChartData()
        dbg(`[Hyperliquid History] TF reconcile: ${klines.length} klines, ${cells.length} shallow cells @ ${tfAtStart}s`)
        // Depth arrives behind — slabs put + compose as they land.
        _deepFutHistoryFetch(coin, tfAtStart)
      })
    }
  }, [timeInterval, scheduleCandleTimer, updateChartData, scheduleChartUpdate, rebuildLiveFromTrades, markHistoryPending])

  // ─── Futures recency top-up ───
  // The server aggregates capture profiles in batches, so the newest few
  // minutes are often NOT yet queryable at the exact moment a commit's
  // seed and slabs fire — those slots seed as klines and, before this,
  // nothing ever asked again ("candles, no footprint data right after
  // refresh": the span just before the reload, cells on both sides of it).
  // Every 60s while a futures chart is live, re-fetch a small newest
  // window and land it through the same slab path. Store precedence makes
  // this idempotent: slots that already hold equal-or-richer cells are
  // untouched, klines upgrade the moment aggregation catches up, and any
  // transient miss — not just this one — heals within a minute instead of
  // persisting until the next TF switch.
  const futTopUpBusyRef = useRef(false)
  const futTopUpKickRef = useRef(null)
  // The top-up runner: re-fetch the last ~8 slots (min 10 minutes) and land
  // them through the slab path. Idempotent by store precedence — slots
  // holding equal-or-richer cells are untouched, klines upgrade the moment
  // the server's batch aggregation catches up.
  const runFutTopUp = async () => {
    const coin = hlCoinRef.current
    if (!coin || futTopUpBusyRef.current) return
    const tfSec = timeIntervalRef.current
    const hours = Math.max(10 / 60, (tfSec * 8) / 3600)
    futTopUpBusyRef.current = true
    try {
      const profiles = await fetchFutProfiles({
        coin, proxyBase: HTTP_PROXY_BASE, intervalSec: tfSec,
        hours, timeoutMs: 6000,
      })
      if (hlCoinRef.current !== coin || timeIntervalRef.current !== tfSec) return
      if (profiles && profiles.length) _applyDeepProfiles(coin, tfSec, profiles)
    } catch { /* next pass retries */ } finally {
      futTopUpBusyRef.current = false
    }
  }
  useEffect(() => {
    // Mount-once. There is no hook-scope hlCoin state to key on (the TF
    // effect's `hlCoin` is a local read off the ref), so the interval
    // simply runs for the life of the hook and the runner guards on the
    // refs: on Solana or with no coin it's a 60s no-op — cheaper than the
    // bug a phantom dependency would have been (this effect's first cut
    // referenced `hlCoin` and would have ReferenceError'd on mount).
    const id = setInterval(() => { runFutTopUp(); _loadSolHistory('topup', { topUp: true }) }, 60_000)
    return () => { clearInterval(id); clearTimeout(futTopUpKickRef.current) }
  }, [])

  // --- Subscribe to token ---
  const handleSubscribe = useCallback((token) => {
    if (!token) return
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      showToast('WebSocket not connected', 'sell')
      return
    }
    // Reset immediately so no old-token state bleeds into the new chart
    // while we wait for the server's 'subscribed' ack
    resetChart()
    // Arm the canvas reveal gate NOW — it keys on currentToken state.
    // Setting it only at the ack left a window where the IDB restore
    // painted uncovered (the pre-gate flash).
    setCurrentToken(token)
    restoredTokenRef.current = null  // new switch → restore may run once
    // Atomic token switch: hold the reveal until the new token's history
    // fetch resolves — the Tier-A cached paint stays behind the cover
    // instead of flashing stale data that reshapes when fresh data lands.
    // The gate's 1500ms fallback bounds the wait if the fetch stalls.
    markHistoryPending(true)
    hlCoinRef.current = null  // leaving futures mode
    feedDelayRef.current = 0
    pendingTradesRef.current = []
    frameScheduledRef.current = false
    currentTokenRef.current = token  // guard trade handler against stale messages
    crumb('subscribe-send', { tok: token.slice(0, 8) })
    {
      const _msg = { action: 'subscribe', token, wallet: publicKeyRef.current || null, bin: 1, cl: performance.now() }
      // Short raw window only; deep history loads from /sol/profile at the
      // ack. An old server (route missing, detected once per session) gets
      // no `since` and sends its full train for the legacy merge.
      if (!solProfileMissingRef.current) {
        _msg.since = Math.round(serverNow() - SOL_RAW_WINDOW_MS)
        solRawFromRef.current = _msg.since
      } else {
        solRawFromRef.current = 0
      }
      solFitPendingRef.current = token
      wsRef.current.send(JSON.stringify(_msg))
    }

    // DO NOT depend on the 'subscribed' ack for liveness. resetChart()
    // above killed the candle timer; if the server considers this token
    // already subscribed (e.g. returning from futures) it may never
    // re-ack — previously that meant a frozen chart with a live tape
    // until a TF change happened to reschedule the timer. Start the
    // clock locally; the ack (if it comes) resets and restarts it,
    // which is harmless.
    startNewCandle()
    scheduleCandleTimer()
    // No-ack fallback restore: if the ack hasn't run it within 1.2s,
    // run it here. Once-per-token guard makes the duplicate impossible.
    setTimeout(() => {
      if (currentTokenRef.current === token) {
        restoreTradesOnce(`sol:${token}`, () => currentTokenRef.current === token)
        // No ack (server already had us on this token) → the ack-side
        // history load never ran. A load in flight has keyed the store.
        if (solHistRef.current.key == null) _loadSolHistory('no-ack')
      }
    }, 1200)

  }, [showToast, resetChart, scheduleChartUpdate, startNewCandle, scheduleCandleTimer, restoreTradesOnce])

  // --- Set trade filter min (called from main component) ---
  const setTradeFilterMin = useCallback((val) => {
    tradeFilterMinRef.current = val
  }, [])

  // --- Push wallet pubkey to server for wallet_activity broadcasts ---
  // Called from the parent whenever the local wallet's publicKey changes
  // (unlock, switch wallet, lock). The server uses this to filter NLN swaps
  // by trader and push wallet_activity messages back to this client.
  const setServerWallet = useCallback((walletB58) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    wsRef.current.send(JSON.stringify({ action: 'set_wallet', wallet: walletB58 || null }))
  }, [])

  // --- Subscribe to Hyperliquid futures stream ---
  const subscribeHyperliquid = useCallback((coin) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      showToast('WebSocket not connected', 'sell')
      return
    }
    // Drop the PREVIOUS coin before subscribing the new one. The server's
    // hl_subscribe adds this client to coins[new] but does NOT remove it
    // from coins[old] — so without an explicit unsubscribe, switching
    // SOL→ETH leaves this socket subscribed to BOTH, and the server keeps
    // streaming the stale coin forever. The client-side .symbol guard
    // masked it for the current coin only; stale frames still arrived and
    // (in a single-chart timeline of switches) accumulated. One coin per
    // socket at a time. No-op on the very first subscribe (hl_coin null).
    if (hlCoinRef.current && hlCoinRef.current !== coin) {
      wsRef.current.send(JSON.stringify({ action: 'hl_unsubscribe' }))
    }
    // Clear the Solana identity the moment we enter futures. The server
    // nulls cs.token on hl_subscribe (live sol trades stop), but a sol
    // BACKFILL already in flight (24h window — seconds of build+transfer)
    // lands late, and its token check compared against the STALE mint still
    // held here — 24h of Solana trades rebuilt into the futures chart, then
    // the periodic persist wrote that contaminated buffer under the hl:<coin>
    // IDB key, poisoning Tier-A restores of this pair forever after. Nulling
    // the ref makes every late sol frame fail its identity check.
    currentTokenRef.current = null
    setCurrentToken(null)
    hlCoinRef.current = coin
    feedDelayRef.current = cmeInfo(coin) ? CME_FEED_DELAY_MS : 0
    wsRef.current.send(JSON.stringify({ action: 'hl_subscribe', coin }))

    const tokenKey = `hl:${coin}`

    // Pair-switch commits (loadFuturesHistory) load Tier-A themselves and
    // merge it in the single paint — running this too would repaint mid-
    // commit (the exact flash this removes). This path remains for any
    // standalone subscribe (no commit in flight).
    if (pairCommitRef.current === coin) return

    loadTrades(tokenKey).then(trades => {
      if (!trades || trades.length === 0) return
      if (hlCoinRef.current !== coin) return
      rawTradesRef.current.prepend(trades)
      if (nativeBucketRef.current == null && trades.length > 0) {
        setNativeBucket(trades[trades.length - 1].tradeMcap, 'tierA-restore')
      }
      rebuildLiveFromTrades()
      rebuildFineFromTrades()
      dbg(`[Tier-A] Restored ${trades.length} trades for ${coin}`)
    }).catch(err => console.warn('[Tier-A] trade restore failed:', err))
  }, [showToast, scheduleChartUpdate, rebuildLiveFromTrades])

  // ── Futures history heal (wake / reconnect) ────────────────────────
  // The slot-aware kline rescue only ran inside pair/TF commits, so a chart
  // left open across hidden or offline spans accumulated empty filler
  // candles (wake filler + candle timer) that nothing ever replaced — the
  // 30m-chart blank columns. This re-fetches klines and re-merges IN PLACE:
  // no bucket resize, no scroll reset, no reveal gate — data-only commit.
  // Empty live candles are dropped (a kline is strictly richer than a slot
  // that never saw a trade); live candles WITH data always win their slots.
  // Plain closure over refs so mount-time handlers (ws onopen, wake) can
  // call it through healHistoryRef without stale-closure or dep churn.
  const _healFuturesHistory = async (why) => {
    const coin = hlCoinRef.current
    if (!coin) return
    if (pairCommitRef.current) return          // a pair commit owns the chart
    const tf = timeIntervalRef.current
    try {
      const historical = await fetchHyperliquidCandles(coin, tf)
      if (hlCoinRef.current !== coin || timeIntervalRef.current !== tf) return
      if (pairCommitRef.current) return
      if (historical && historical.length > 0) {
        // Klines refresh the skeleton; the store's precedence rule means
        // they can't downgrade a cell — the entire cellByTs/lattice-guard
        // apparatus the old heal carried is now a property of the store,
        // not a ritual this function must perform correctly.
        _futStorePut(coin, tf, historical)
      }
      _composeFut(`heal:${why}`)
      dbg(`[HealHistory] ${why}: klines merged into store, composed`)
      // Arm depth only if nothing is already climbing this coin|tf.
      // force:true used to restart the ladder from rung 1 on every
      // reconcile — stacking 49→97→145 climbs one second apart.
      // Inflight gate on _deepFutHistoryFetch is what stops that.
      _deepFutHistoryFetch(coin, tf, { force: true })
    } catch (e) {
      console.warn('[HealHistory] failed:', e?.message || e)
    }
  }
  healHistoryRef.current = _healFuturesHistory

  // Re-derive the history<->live seam from the RETAINED source. One O(n)
  // filter pass over a few hundred candles; idempotent — bails unless the
  // derived historical set actually changed, so the once-per-slot cadence
  // can't cause paint or scroll churn. Fired from startNewCandle: the slot
  // roll and the retention prune (the two hole-minting moments) both live
  // there, so coverage is always recomputed against what live data exists
  // NOW rather than what existed at fetch time.
  const _reseamFutHistory = (why) => {
    // The seam IS the composer now. The old version re-derived historical
    // from a snapshot array with its own keep-rules — the snapshot went
    // stale (deep cells landed elsewhere) and the reseam faithfully
    // restored the stale view every slot roll. Composing from the store
    // cannot go stale: the store is where landings write.
    if (pairCommitRef.current) return
    _composeFut(why)
  }
  reseamFutHistoryRef.current = _reseamFutHistory

  // ── OpenMarket volume-profile fetch (futures footprint history) ──
  // Plain closure over refs (same pattern as the fine engine) so both the
  // pair-switch and TF-change paths share it. Resolves to
  // { profiles, nativeBucket } or null — NEVER throws; on any failure the
  // callers fall back to plain kline candlesticks exactly as before.
  const _fetchHlProfilesSafe = async (coin, tiSec) => {
    if (!FUT_HISTORY_ENABLED) return null   // off — resolve instantly, klines only
    try {
      // Profile interval rides the FINEST lattice the OM API offers at or
      // below the chart TF. It no longer needs to match the kline interval:
      // klines fetch at the chart's own interval (chartUtils intervalMap),
      // and profiles finer than the TF — 15m on the 30m chart, the API has
      // no thirty-minute period — are folded onto the chart grid by
      // foldProfilesToSlot before conversion. Both feeds land on chart
      // slots; the map-merge lines up 1:1 by construction.
      const periodSec = futProfilePeriodSec(coin, tiSec)
      // SHALLOW window only — the commit must paint fast. Full depth
      // (MAX_CANDLES × period, ≤72h) arrives via _deepFutHistoryFetch,
      // which both commit paths kick off AFTER their one paint.
      const hours = Math.min(FUT_SHALLOW_HOURS, 72, (maxCandlesFor(coin, tiSec) * periodSec) / 3600)
      const profiles = await fetchFutProfiles({
        coin, proxyBase: HTTP_PROXY_BASE, intervalSec: periodSec, hours,
        log: (m) => dbg('[FutHist]', m),
      })
      if (!profiles || profiles.length === 0) return null
      const lastProf = profiles[profiles.length - 1].profile
      const refPrice = lastProf && lastProf.length ? lastProf[0] : 0
      if (!(refPrice > 0)) return null
      // ── ONE GRAIN PER COIN. NEVER PICK LOCALLY. ─────────────────────────
      // This called pickStorageBucket directly, outside the setNativeBucket
      // gate that is supposed to be the single owner. Two inputs, neither of
      // them stable:
      //
      //   lastProf[0]              whichever level sits at index 0 of the last
      //                            profile in THIS batch
      //   mcapBucketSizeRef        the display bucket AT THE INSTANT THIS SLAB
      //                            LANDS — which moves while you zoom
      //
      // Slabs arrive in sequence (145 -> 289 -> 433 -> 501), each calling this
      // fresh. Zoom while they land and each one commits a different grain, so
      // candles end up on different grids by nothing more than when their slab
      // happened to arrive. That is why some columns plot evenly and some
      // bunch: nearestLatticeRow rounds off-grid mcaps to whichever row is
      // closer, collapsing neighbours onto one row and leaving the next empty.
      //
      // setNativeBucket is first-writer-wins, so this commits the grain when
      // nothing has yet and is a no-op when something has. Either way the
      // value read back is the coin's one grain.
      setNativeBucket(refPrice, 'fut-shallow')
      // The CELLS, though, are keyed at the step the server aggregated at
      // (profiles.bucket, its pinned per-coin grain) — stamping the live
      // grain on server-lattice prices left the in-between rows empty.
      // Old server without `bucket`: fall back to the live grain.
      const nativeBucket = profiles.bucket > 0 ? profiles.bucket : nativeBucketRef.current
      if (!(nativeBucket > 0)) return null
      return { profiles, nativeBucket }
    } catch (e) {
      console.warn('[OM] footprint history fetch failed (kline fallback):', e?.message || e)
      return null
    }
  }

  // ── Background deep futures footprint history ──
  // Fired AFTER a pair/TF commit paints. Fetches the FULL depth window with
  // a long timeout (it's off the commit path — a slow droplet aggregation
  // costs nothing visible), then upgrades still-kline historical candles in
  // place: same startTime → cell version wins, live region never touched,
  // one repaint. Superseded silently by any newer call / pair / TF change.
  const futDeepSeqRef = useRef(0)
  // coin|tf currently climbing the slab ladder. A second arm for the SAME
  // key returns early (below) — it does NOT bump seq or restart from rung 1.
  // (Earlier builds abandoned-and-restarted; that made the interleaved
  // 49→97→145 ladders in the log. The gate is what stops that.)
  const futDeepInflightRef = useRef(null)
  const _deepFutHistoryFetch = (coin, tfSec, { force = false } = {}) => {
    if (!FUT_HISTORY_ENABLED || !coin) return
    const periodSec = futProfilePeriodSec(coin, tfSec)
    // CME: the server keeps traded data only (no empty candles for closed
    // sessions), so a wall-clock window from "now" comes back short over
    // nights/weekends. Ask for the candle budget PLUS a closed-market
    // allowance (weekend 49h + daily halts), bounded by the server's 7-day
    // store. The compose cap then keeps the newest traded candles.
    const fullHours = cmeInfo(coin)
      ? Math.min(CME_FETCH_MAX_HOURS, (maxCandlesFor(coin, tfSec) * periodSec) / 3600 + 64)
      : Math.min(72, (maxCandlesFor(coin, tfSec) * periodSec) / 3600)
    // Skip when the commit's shallow fetch already covered the full depth —
    // UNLESS forced (the wake/reconnect heal never fetches profiles itself,
    // so it needs this pass even on short-depth TFs).
    if (!force && fullHours <= FUT_SHALLOW_HOURS) return
    const inflightKey = `${coin}|${tfSec}`
    if (futDeepInflightRef.current === inflightKey) return
    futDeepInflightRef.current = inflightKey
    const seq = ++futDeepSeqRef.current
    // First top-up ~10s out instead of waiting for the 60s tick: commits
    // and heals fire their slabs immediately, often BEFORE the capture
    // service has flushed the newest minutes into queryable form — this
    // one-shot re-asks right after the typical flush lag, so a bare
    // newest-span heals in seconds, not up to a minute.
    clearTimeout(futTopUpKickRef.current)
    futTopUpKickRef.current = setTimeout(runFutTopUp, 10_000)
    ;(async () => {
      // ── Slabbed, not monolithic ──
      // One request for the whole depth window (up to 72h of server-side
      // aggregation on the 30m chart) rode a single timeout — and when it
      // blew, the WHOLE refill silently died and klines stood ("all the
      // data is on the 1m [8h window] but not the 5m [41h window]").
      // Sequential ≤12h slabs instead, newest first, each applied as it
      // lands: the recent history the eye is on upgrades in seconds, one
      // slow slab costs only its own span, and the server's profile cache
      // makes repeat slabs cheap. Same landing logic per slab, same seq /
      // coin / TF supersede guards — a pair or TF switch mid-sequence
      // abandons the rest.
      const SLAB_HOURS = 12
      const slabs = []
      for (let end = 0; end < fullHours; end += SLAB_HOURS) {
        slabs.push({ hoursEnd: end, hours: Math.min(SLAB_HOURS, fullHours - end) })
      }
      for (const slab of slabs) {
        let profiles
        try {
          profiles = await fetchFutProfiles({
            coin, proxyBase: HTTP_PROXY_BASE, intervalSec: periodSec,
            hours: slab.hours, hoursOffset: slab.hoursEnd,
            timeoutMs: FUT_DEEP_TIMEOUT_MS,
            log: (m) => dbg('[FutHist deep]', m),
          })
        } catch (e) {
          console.warn(`[FutHist] slab ${slab.hoursEnd}-${slab.hoursEnd + slab.hours}h failed (continuing):`, e?.message || e)
          continue
        }
        // Superseded / stale? Drop the remaining slabs — the chart moved on.
        if (seq !== futDeepSeqRef.current) return
        if (hlCoinRef.current !== coin || timeIntervalRef.current !== tfSec) return
        if (!profiles || profiles.length === 0) continue
        _applyDeepProfiles(coin, tfSec, profiles)
      }
    })().finally(() => {
      // Only the live seq clears the gate; a superseded run must not unlock
      // a newer climb for the same key.
      if (seq === futDeepSeqRef.current && futDeepInflightRef.current === inflightKey) {
        futDeepInflightRef.current = null
      }
    })
  }

  // One slab's landing: build cells at this TF's lattice, put them into the
  // store (cell precedence: beats klines always, beats weaker cells by
  // volume), recompose. Sliver demotion and the ticking-candle guarantee
  // live in _composeFut — this function knows no merge rules at all.
  const _applyDeepProfiles = (coin, tfSec, profiles) => {
    try {
      const lastProf = profiles[profiles.length - 1].profile
      const refPrice = lastProf && lastProf.length ? lastProf[0] : 0
      if (!(refPrice > 0)) return
      // Same rule as the shallow path above: read the coin's committed grain,
      // never pick one here. A deep slab landing at a different zoom than the
      // shallow one used to get a different bucket for the same coin.
      setNativeBucket(refPrice, 'fut-deep')
      // Cells keyed at the server's own step (see _fetchHlProfilesSafe).
      const nativeBucket = profiles.bucket > 0 ? profiles.bucket : nativeBucketRef.current
      if (!(nativeBucket > 0)) return
      // OHLC enrichment from whatever the store already holds for the slot.
      const map = _futStoreFor(coin, tfSec)
      const klineByTs = new Map()
      for (const h of map.values()) {
        klineByTs.set(Math.floor(h.startTime / 1000), {
          open: h.open_mcap, high: h.high_mcap, low: h.low_mcap, close: h.close_mcap,
        })
      }
      // Fold to the chart's slot before converting, so cells can only ever
      // land on timestamps the klines actually occupy.
      const cells = profilesToCellCandles(foldProfilesToSlot(profiles, tfSec), { nativeBucket, ohlcByTs: klineByTs })
      if (!cells.length) return
      _futStorePut(coin, tfSec, cells, { cells: true })
      _composeFut('deep-slab')
      dbg(`[FutHist] slab landed: ${cells.length} cell candles @ ${tfSec}s`)
    } catch (e) {
      console.warn('[FutHist] slab apply failed (other slabs stand):', e?.message || e)
    }
  }

  // Merge OM cell-candles over the kline skeleton: same startTime → the
  // cell version wins (real footprint), missing periods keep the kline
  // candlestick. Kline OHLC enriches the cells' open/close.
  // slotSec threaded in so the profiles can be folded to the chart's grid
  // before conversion (see foldProfilesToSlot). Optional: callers without a
  // timeframe in scope pass nothing and get the previous behaviour.
  const _mergeOmCells = (filtered, om, slotSec = 0) => {
    if (!om) return filtered
    try {
      const klineByTs = new Map(filtered.map(h => [Math.floor(h.startTime / 1000), {
        open: h.open_mcap, high: h.high_mcap, low: h.low_mcap, close: h.close_mcap,
      }]))
      const cells = profilesToCellCandles(foldProfilesToSlot(om.profiles, slotSec), { nativeBucket: om.nativeBucket, ohlcByTs: klineByTs })
      if (!cells.length) return filtered
      const cellByTs = new Map(cells.map(c => [c.startTime, c]))
      let hit = 0
      const merged = filtered.map(h => {
        const cell = cellByTs.get(h.startTime)
        if (cell) { hit++; return cell }
        return h
      })
      dbg(`[OM] footprint history: ${hit}/${filtered.length} kline periods upgraded to cells (bucket ${om.nativeBucket})`)
      // Kline fetch empty but profiles present (HL API hiccup): show cells alone.
      if (!merged.length && cells.length) return cells
      return merged
    } catch (e) {
      console.warn('[OM] cell merge failed (kline fallback):', e?.message || e)
      return filtered
    }
  }

  // --- Load Hyperliquid historical candles on pair switch ---
  const loadFuturesHistory = useCallback(async (coin, bucketSize, timeInterval) => {
    // The floor is stamped at SUBSCRIBE, not at TF changes — the stream has
    // been continuous since this moment, so candles built after it are
    // complete regardless of what TF they're later folded to.
    feedDelayRef.current = cmeInfo(coin) ? CME_FEED_DELAY_MS : 0   // before any clock read
    futLiveFloorRef.current = serverNowRef.current()
    hlCoinRef.current = coin
    // SINGLE-COMMIT pair switch. The old sequence painted three times across
    // two awaits: Tier-A restore rebuilt first, the kline replace ERASED it,
    // a compensating rebuild restored it — candles landing then repositioning
    // (the pair-switch flash). Now both sources resolve first, the state is
    // built in one synchronous block, and the chart paints once. This commit
    // also owns the Tier-A restore for the switch — subscribeHyperliquid's
    // own restore stands down via pairCommitRef.
    pairCommitRef.current = coin
    try {
      const [historical, stored, om] = await Promise.all([
        fetchHyperliquidCandles(coin, timeInterval).catch(err => {
          console.warn('[Hyperliquid History] Error:', err)
          return []
        }),
        loadTrades(`hl:${coin}`).catch(() => []),
        _fetchHlProfilesSafe(coin, timeInterval),   // OM footprint history (null on any failure)
      ])
      if (hlCoinRef.current !== coin) return   // superseded by another switch

      const liveStartTime = serverNowRef.current()
      liveStartTimeRef.current = liveStartTime

      // Seed buffer + fine series from the restore BEFORE building candles.
      // resetChart wiped the buffer at switch; anything in it now arrived
      // live during the fetch and is newer than the restore — keep both.
      if (stored && stored.length > 0) {
        rawTradesRef.current.prepend(stored)
        if (nativeBucketRef.current == null) {
          setNativeBucket(stored[stored.length - 1].tradeMcap, 'tierA-stored')
        }
        rebuildFineFromTrades()
        dbg(`[Tier-A] Restored ${stored.length} trades for ${coin}`)
      }

      const intervalMs = timeInterval * 1000
      const currentCandleStart = Math.floor(liveStartTime / intervalMs) * intervalMs
      // Arm the seam before either live-region branch runs. Both mint the
      // seam slot; _buildSeamCandle substitutes the exact version into each.
      _captureFutSeam(coin, timeInterval, om)
      let filtered = (historical || []).filter(c => c.startTime < currentCandleStart)
      // Upgrade kline candlesticks to OM footprint cell-candles where profiles exist.
      filtered = _mergeOmCells(filtered, om, timeInterval).filter(c => c.startTime < currentCandleStart)

      // Live region from the restored data: fold when the fine series can
      // serve it, legacy rebuild otherwise.
      let liveNow = []
      const canFold = USE_FINE_FOLD
        && timeInterval >= FINE_INTERVAL_SEC
        && timeInterval % FINE_INTERVAL_SEC === 0
        && (fineCandlesRef.current.length > 0 || currentFineRef.current)
      if (canFold) {
        liveNow = foldFineCandles(timeInterval)
      } else if (rawTradesRef.current.length > 0) {
        rebuildLiveFromTrades({ force: true })
        liveNow = candlesRef.current.filter(c => !c.isHistorical)
      }

      // Slot-aware keep: klines survive wherever the live region has no
      // coverage (restore holes, outage spans) — see filterHistoricalBySlots.
      // SEED THE STORE from the same merged set this commit paints. The
      // commit keeps its own synchronous assembly below (pair switch is the
      // one moment the whole chart legitimately rebuilds in one breath),
      // but every LATER writer — reseam, heal, deep slabs — composes from
      // the store, so it must hold this truth from frame one. TF derived
      // from intervalMs — the one TF value PROVABLY in scope here (the old
      // `tf` bug: heal-scope name, ReferenceError, empty futures chart).
      {
        const _tfHere = intervalMs / 1000
        _futStoreFor(coin, _tfHere)              // reset to this pair|tf
        const _kl = filtered.filter(c => !c._omFootprint)
        if (_kl.length) _futStorePut(coin, _tfHere, _kl)
        const _cells = filtered.filter(c => c._omFootprint)
        if (_cells.length) _futStorePut(coin, _tfHere, _cells, { cells: true })
      }
      const validHistorical = filterHistoricalBySlots(filtered, liveNow, intervalMs)

      let arr = deduplicateCandles([...validHistorical, ...liveNow]).slice(-maxCandlesFor(hlCoinRef.current, timeIntervalRef.current))
      let last = arr[arr.length - 1]
      if (!last || last.isHistorical || last.startTime < currentCandleStart) {
        const liveCandle = newLiveCandle(currentCandleStart,
          nativeBucketRef.current ?? mcapBucketSizeRef.current ?? bucketSize)
        arr = deduplicateCandles([...arr, liveCandle]).slice(-maxCandlesFor(hlCoinRef.current, timeIntervalRef.current))
        last = liveCandle
      }
      candlesRef.current = arr
      currentCandleRef.current = (last && !last.isHistorical) ? last : null

      // lastBucketMcap + smart bucket sizing from the kline skeleton
      // (unchanged logic, now inside the single commit).
      const lastHist = filtered.length > 0 ? filtered[filtered.length - 1] : null
      if (lastHist && lastHist.close_mcap > 0) {
        lastBucketMcapRef.current = lastHist.close_mcap
      }
      if (filtered.length > 0) {
        const viewCount = Math.min(filtered.length, viewWindowSizeRef.current || DEFAULT_VIEW_WINDOW)
        const recentCandles = filtered.slice(-viewCount)
        let hiPrice = -Infinity, loPrice = Infinity
        for (const c of recentCandles) {
          if (c.high_mcap != null && c.high_mcap > hiPrice) hiPrice = c.high_mcap
          if (c.low_mcap != null && c.low_mcap < loPrice) loPrice = c.low_mcap
        }
        if (Number.isFinite(hiPrice) && Number.isFinite(loPrice) && hiPrice > loPrice) {
          const cH = bodyScrollRef.current ? bodyScrollRef.current.clientHeight : 600
          const smartBucket = pickBucketForRange(hiPrice - loPrice, cH)
          if (smartBucket !== mcapBucketSizeRef.current) {
            mcapBucketSizeRef.current = smartBucket
            setMcapBucketSize(smartBucket)
          }
        }
      }

      // THE one paint.
      scheduleCandleTimer()
      scheduleChartUpdate()
      setTimeout(() => { scrollToLatest?.() }, 150)
      crumb('fut-commit', { coin, hist: validHistorical.length, live: liveNow.length })
      dbg(`[Hyperliquid History] pair-commit: ${validHistorical.length} historical + ${liveNow.length} live for ${coin}`)
      // Full-depth footprint history in the background — upgrades the
      // klines beyond the shallow window to cells when it lands.
      _deepFutHistoryFetch(coin, timeInterval)
    } catch (err) {
      console.warn('[Hyperliquid History] Error:', err)
    } finally {
      if (pairCommitRef.current === coin) pairCommitRef.current = null
    }
  }, [scheduleChartUpdate, scheduleCandleTimer, scrollToLatest, setMcapBucketSize, rebuildLiveFromTrades])

  // Memoized so this object's IDENTITY only changes when something in it
  // actually changed. It used to be a bare literal, so every App render
  // produced a new `data` object — and since FootprintChart takes it as a
  // prop, the chart re-rendered (~25ms of React work) on every App render
  // regardless of whether any market data moved. Measured: ~30 renders/s,
  // ~75% of the main thread, the whole page pinned at 18-27fps.
  //
  // Deps are exactly the mutable members: the 7 state values plus the 6
  // useCallback'd actions. Everything else in the object is a useRef, whose
  // identity is stable for the hook's lifetime and must NOT be listed
  // (a ref's .current changing is invisible to React by design — callers
  // read it at use time, which is the point of passing refs).
  return useMemo(() => ({
    // State
    chartData,
    stats,
    recentTrades,
    status,
    historyPending,
    historyPendingRef,
    currentToken,
    tokenHistory,

    // Refs exposed for drawing / interactions
    lastBucketMcapRef,
    tokenSupplyRef,
    serverNowRef,      // EMA-smoothed server clock (fn ref) — trail x runs on this
    solPriceRef,
    rawTradesRef,
    wsRef,
    lastWsMsgAtRef,    // health-20261104: transport message age
    appClockRef,       // health-20261104: ws round-trip (delay)
    onOwnBuyDetectedRef,

    // Actions
    handleSubscribe,
    resetChart,
    setTradeFilterMin,
    loadFuturesHistory,
    subscribeHyperliquid,
    setServerWallet,

    // Token subscribed callback trigger — main component wires this
    // to trading hook's loadPositions / loadSRLines / detectExistingPosition
    // via the onSubscribed pattern (the WS onmessage fires setCurrentToken which
    // is returned above — main component watches it via useEffect)
  }), [
    chartData, stats, recentTrades, status, historyPending, currentToken, tokenHistory,
    handleSubscribe, resetChart, setTradeFilterMin, loadFuturesHistory,
    subscribeHyperliquid, setServerWallet,
  ])
}
